import { MessageUtils } from './../../classes/messageutils';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  ComponentFactoryResolver,
  Injector,
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
  HostListener,
  Injectable,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { getItemsFromCollection } from '@mitel/cloudlink-sdk/utils';
import { User } from '@mitel/cloudlink-sdk/admin';
import {
  Conversation,
  Message,
  Participant,
  Command,
  CommandResponse,
  UserConversation
} from '@mitel/cloudlink-sdk/chat';

import { Menu } from 'primeng/menu';
import { Popover } from 'primeng/popover';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SliderChangeEvent } from 'primeng/slider';
import { TranslateService, MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import findLast from 'lodash-es/findLast';
import takeRight from 'lodash-es/takeRight';
import remove from 'lodash-es/remove';
import chunk from 'lodash-es/chunk';
import lodashMap from 'lodash-es/map';
import unescape from 'lodash-es/unescape';
import moment from 'moment';
import { Subject, Subscription, ReplaySubject, interval, defer, timer, fromEvent, firstValueFrom } from 'rxjs';
import {
  map,
  filter,
  debounceTime,
  tap,
  auditTime,
  takeUntil,
  take,
  retryWhen,
  switchMap,
  bufferTime
} from 'rxjs/operators';

import { UnreadMessageDatePipe, ChatDatePipe } from '../../classes/chat-pipes';
import {
  ContentTypes,
  ChatStorageKeys,
  ChatAnalyticAttributes,
  ChatMaxValues,
  ChatMessageLimits
} from '../../classes/constants';
import { MessageEx } from '../../classes/messageex';
import { normalizeAiQuickReplies, parseAiReply, visibleAiReplyPortion } from '../../classes/ai-quick-replies';
import { SharedHelpers } from '../../classes/sharedhelpers';
import { ChatAiConfig } from '../../interfaces/chat-ai-config';
import { ChatCloudFileLink } from '../../interfaces/chat-cloud-file-link';
import { ChatMaisService, MaisSource, MaisStreamChunk, MaisToolCall } from '../../services/chat-mais.service';
import { ChatAdminService } from '../../services/chat-admin.service';
import { ChatAuthService } from '../../services/chat-auth.service';
import { ChatCloudLinkService, DeletedParticipant, ParticipantEx } from '../../services/chat-cloudlink.service';
import {
  ChatConversationService,
  ConversationParticipants,
  ChatMessageErrorEvent,
  DatabaseConversation
} from '../../services/chat-conversation.service';
import { LocationService } from '../../services/location.service';
import { RecordService } from '../../services/record.service';
import { ScrollingService } from '../../services/scrolling.service';
import { ChatItemComponent } from '../chat-item/chat-item.component';
import { forwardRef } from '@angular/core';
import { ResizeSensor } from 'css-element-queries';
import { AudioHelpers } from '../../classes/audiohelpers';
import { FileTypes } from './../../enums/file-types';
import {
  ActionCardBehaviorEvent,
  ActionCardHelpers,
  PostWebhookPayloadEvent,
  SendWebhookPayloadEvent,
  OpenLinkEvent
} from './../../classes/actioncardhelpers';

//Needed for the CL SDK
import { PrimeThemeService } from '../../themes/prime-theme.service';
import { CdkDrag } from '@angular/cdk/drag-drop';
import uniqBy from 'lodash-es/uniqBy';
import escape from 'lodash-es/escape';
import { ChatLoggingService, ChatLogEvent, ChatLogLevel } from '../../services/chat-logging.service';
import { trigger, transition, style, animate } from '@angular/animations';
import 'hammerjs';
import Hammer from 'hammerjs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { v4 as uuidv4 } from 'uuid';
import { ChatDatabaseService } from '../../services/chat-database.service';
import {
  ChatOneDriveError,
  ChatOneDriveErrorCode,
  ChatOneDriveService,
  ChatOneDriveSharedFile
} from '../../services/chat-onedrive.service';
import { AttachMenuComponent, AttachTarget } from '../attach-menu/attach-menu.component';
import { IChatDbMessage, IChatDbConversation, IChatUser } from '../../classes/appdb';
import { KeyboardKeys } from '../../classes/keyboard-keys';
//need to return depricated keycodes for MiCollab windows PC client (other buttons seems to work fine)
import { DOWN_ARROW, ENTER, UP_ARROW } from '@angular/cdk/keycodes';
import { MentionHelpers } from '../../classes/mentionhelpers';
import { MenuHelpers } from '../../classes/menuhelpers';
import { EmojiOutput, EmojiPickerComponent } from '../emoji-picker/emoji-picker.component';
import { ChatConfirmationDialogComponent } from '../chat-confirmation-dialog/chat-confirmation-dialog.component';
import { AnalyticObject } from '../../interfaces/analytics-interfaces';
import { EmojiPipe } from '../../pipes/emoji.pipe';
import { MessageInfoComponent } from '../message-info/message-info.component';
import { default as AnalyticEvents } from '@mitel-internal/cloudlink-analytic-events/analytic-events.json';
import { HeaderButton } from '../header/header.component';

const uuid = uuidv4;
export class QuoteMessageCite {
  messageId: string;
  createdBy: string;
  createdOn?: Date;
  body: string;
}

export class QuoteMessageContent {
  body: string;
  contentType: string;
}

export class QuoteMessage {
  cite: QuoteMessageCite;
  content: QuoteMessageContent;
}

//Explanations for all config options available in the chat component repo README
export interface ChatConfig {
  hideAddFiles?: boolean;
  //Legacy support
  hideActionsAddFiles?: boolean;
  //Legacy support
  hideActionsShareLocation?: boolean;
  //Legacy support
  hideActionsShareAudio?: boolean;
  hideShareLocation?: boolean;
  hideShareAudio?: boolean;
  hideEmojis?: boolean;
  showCharCounter?: boolean;
  hideCommands?: boolean;
  hiddenCommands?: string[];
  autoJoinStream?: boolean;
  autoAcceptInvite?: boolean;
  hideMentions?: boolean;
  suggestionsEnabled?: boolean;
  hideMessageOptions?: boolean;
  hideMessageOptionsPreviewPdf?: boolean;
  hideMessageOptionsDownload?: boolean;
  hideMessageOptionsReaction?: boolean;
  hideMessageOptionsReply?: boolean;
  hideMessageOptionsInfo?: boolean;
  hideTranslatedBody?: boolean;
  useDocumentFocusForUnread?: boolean;
  useDocumentVisibleForUnread?: boolean;
  disableSpellcheck?: boolean;
  showHostErrors?: boolean;
  showDisconnectedHostError?: boolean;
  showOfflineHostError?: boolean;
  showMessageOptionsEdit?: boolean;
  showMessageOptionsDelete?: boolean;
  showMessageOptionsSelect?: boolean;
  maxInputCharacters?: number;
  logLevel?: ChatLogLevel; // Minimum level logged and emitted (default 'info'); overrides enableDebugLogs/disableLogs
  consoleOutput?: boolean; // Set false to only emit logs via onInfoLog/onErrorLog, never to the console (default true)
  /** @deprecated Use logLevel: 'debug' */
  enableDebugLogs?: boolean;
  /** @deprecated Use logLevel: 'error' */
  disableLogs?: boolean;
  disableDragDrop?: boolean;
  disablePasteFile?: boolean;
  allowedFileTypes?: string[]; //List of regex for contentType, e.g. ['image/.*', 'application/pdf']
  blockedFileTypes?: string[]; //List of regex for contentType, e.g. ['image/.*', 'application/pdf']
  useNicknames?: boolean;
  focusOnLoad?: boolean;
  maxRecordingTime?: number;
  dialogVisibleTimeout?: number;
  showHeader?: boolean;
  headerFontSize?: number;
  headerHeight?: number;
  headerShowBackButton?: boolean | number;
  headerShowSearch?: boolean;
  headerTitle?: string;
  headerButtons?: HeaderButton[];
  translationDelay?: number;
  getMessagePageSize?: number;
  disableTextArea?: boolean;
  enableIndexDb?: boolean;
  indexDbAddNewConversations?: boolean;
  indexDbConversationLimit?: number;
  enableOfflineSending?: boolean;
  indexDbConversationListMax?: number;
  reverseNames?: boolean;
  reverseMentionSearchNames?: boolean;
  disableImageAttachmentRender?: boolean;
  hideNoConversationIdError?: boolean;
  showGoToAiSuggestions?: boolean;
  fullScreenInChat?: boolean;
  showExitSearchButton?: boolean;
  userReplacements?: {
    [userId: string]: { displayName: string; email: string; avatar: string };
  };
  maxImageSize?: number;
  maxParticipantLoad?: number;
  saveInput?: boolean;
  hideReadIndicators?: boolean;
  showShareVideo?: boolean;
  showNamePointer?: boolean;
  allowedPlatformSystemMessages?: string[]; //List of platform system messages to allow (outside of attachments), default is allow all
  usePlatformAttachmentSystemMessages?: boolean;
  logMessageEvents?: boolean;
  smsMode?: boolean;
  snapshotMessageOnLoad?: string;
  onlyLoadFromDb?: boolean;
  enableTwemoji?: boolean;
  maxFileSizeMb?: number; // in mb
  // Files over maxFileSizeMb go to the user's OneDrive and a share link is posted instead, while the host supplies a
  // connected msgraph integration through ChatUserDataService. Off by default: those files are refused as before.
  enableOneDriveLargeFiles?: boolean;
  showDayOfWeek?: boolean;
  usePrimaryGreen?: boolean;
  isOneToOneChat?: boolean;
  useCustomSystemAvatar?: boolean;
  customAvatarFile?: string;
  jsonMessagesAsSource?: boolean;
  jsonMessagesPayload?: any; // TBD - Type to be defined later
  retryFetchOnEmpty?: boolean;
}

export class MessageReaction {
  userId: string;
  reactionId: string;
}

export class AvatarEvent {
  userId: string;
  mouseEvent: Event;
}

export class ChatMessageEvent {
  userId: string;
  body: string;
  mouseEvent: Event;
}

export class ConversationLoadEvent {
  conversationId: string;
  loadType?: string;
}

export class FirstFetchEvent {
  conversationId: string;
  recentMessage: Message;
}

export class PendingAttachment {
  imageUrl?: string;
  imageSafeUrl?: SafeUrl;
  fileName: string;
  fileSize: string;
  file: any;
  // Over maxFileSizeMb: shared as a OneDrive link instead of uploaded to the chat
  oneDrive?: boolean;
}

export class OneDriveUpload {
  id: string;
  fileName: string;
  // 0 to 1
  progress: number;
  abortController: AbortController;
}

export class UnreadUpdate {
  conversationId: string;
  lastReadMessageDate: string;
}

export class TextAreaProps {
  lastNode: any;
  lastCaretPos: number;
  message: string;
  maxInputCharacters: number;
  mentionInput: string;
  mentionCaretEnd: number;
  mentionNode: any;
  mentionPending: boolean;
  commandPending: boolean;
  mentions: User[];
  commands: Command[];
  selectedDropdownIndex: number;
  mentionFetchAttempt: number;
  lastInputValue: string;
  lastInputString: string;
  mentionDropdownMargins: { bottom: number; left: number };
}

export class AudioObj {
  audioPlaying: boolean;
  audioStart: string;
  audioProgress: string;
  audioEnd: string;
}

enum ConversationLoadType {
  Initial = 1,
  Reconnect = 2,
  Search = 3,
  Snapshot = 4,
  FromOffline = 5
}

enum FetchType {
  Normal = 1,
  Snapshot = 2,
  SnapshotUp = 3,
  SnapshotDown = 4
}

enum CommandResultType {
  Success = 1,
  Failure = 2,
  NoMatch = 3
}

//Need these translations hardcoded in case the network is down and the translation files aren't pulled down.
//Can show the properly translated error strings in the UI when the chat fails to load.
@Injectable()
export class ChatMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    if (params.key === 'cl_chat.problemCommunicatingWithChat') {
      switch (params.translateService.currentLang) {
        case 'cy-GB':
          return "Efallai y bydd problem cyfathrebu â'ch gwasanaeth sgwrsio, aildrydarwch. Os bydd y mater yn parhau, cysylltwch â'ch gweinyddwr.";
        case 'da':
          return 'Der kan være et problem med at kommunikere med din chat-service, prøv igen. Hvis problemet fortsætter, skal du kontakte din administrator.';
        case 'de-DE':
          return 'Es kann ein Problem bei der Kommunikation mit deinem Chatdienst geben, bitte versuche es erneut. Wenn das Problem weiterhin besteht, wenden Sie sich an Ihren Administrator.';
        case 'es-CL':
        case 'es-ES':
        case 'es-MX':
          return 'Es posible que haya un problema para comunicarse con su servicio de chat, inténtelo nuevamente. Si el problema persiste, comuníquese con administración.';
        case 'fi':
          return 'Chat-palvelusi osalta voi olla olemassa viestinnän ongelma, yritä uudelleen. Mikäli ongelma ei poistu, ota yhteyttä pääkäyttäjääsi.';
        case 'fr-CA':
          return 'Des problèmes de communication peuvent survenir avec votre service de clavardage, veuillez réessayer. Si le problème persiste, contactez votre administrateur.';
        case 'fr-FR':
          return 'Des problèmes de communication peuvent survenir avec votre service de discussion, veuillez réessayer. Si le problème persiste, contactez votre administrateur.';
        case 'it-IT':
          return "Potrebbe esserci un problema di comunicazione con il servizio chat. Riprovare. Se il problema\r\npersiste, contattare l'amministratore.";
        case 'nb-NO':
          return 'Det kan hende det er et problem med å kommunisere med chattetjenesten din, prøv på nytt. Ta kontakt med administrator dersom problemet vedvarer.';
        case 'nl':
          return 'Er is mogelijk een probleem met de communicatie met uw chatservice. Probeer het nog eens. Neem contact op met uw beheerder als het probleem zich blijft voordoen.';
        case 'no':
          return 'Det kan hende det er eit problem med å kommunisere med chattetenesta di, prøv igjen. Ta kontakt med administrator dersom problemet varar.';
        case 'pl':
          return 'Mógł wystąpić problem podczas komunikowania się z usługą czata — spróbuj ponownie. \r\nJeśli problem będzie nadal występować, skontaktuj się z administratorem.';
        case 'pt-BR':
          return 'Poderá existir um problema na comunicação com seu serviço de chat. Se o problema persistir, contate seu administrador.';
        case 'pt-PT':
          return 'Poderá existir um problema na comunicação com o seu serviço de chat. Se o problema persistir, contacte o seu administrador.';
        case 'ru-RU':
          return 'Возможно, возникла проблема со связью с вашим чатом, повторите попытку. Если проблема не устранена, обратитесь к администратору.';
        case 'sv-SE':
          return 'Det kan finnas ett problem med att kommunicera med chattjänsten. Försök igen och om problemet kvarstår kontaktar du administratören.';
        case 'zh-CN':
          return '连接聊天服务时出问题，请重试。如果问题一直存在，请联系管理员。';
        case 'zh-TW':
          return '連接聊天服務時出問題，請重試。如果問題一直存在，請聯繫管理員。';
        case 'zh':
          return '连接聊天服务时出问题，请重试。如果问题一直存在，请联系管理员。';
        case 'en':
        default:
          return 'There may be a problem communicating with your chat service, please retry. If the issue persists, contact your admin.';
      }
    } else if (params.key === 'cl_chat.failedToInitialize') {
      switch (params.translateService.currentLang) {
        case 'cy-GB':
          return 'Wedi methu ymgychwyn.';
        case 'da':
          return 'Opstart mislykkedes.';
        case 'de-DE':
          return 'Starten fehlgeschlagen.';
        case 'es-CL':
        case 'es-ES':
        case 'es-MX':
          return 'No se pudo inicializar.';
        case 'fi':
          return 'Alustus epäonnistui.';
        case 'fr-CA':
          return 'Échec d’initialisation.';
        case 'fr-FR':
          return 'Échec d’initialisation.';
        case 'it-IT':
          return 'Impossibile inizializzare.';
        case 'nb-NO':
          return 'Kunne ikke initialisere.';
        case 'nl':
          return 'Initialiseren mislukt.';
        case 'no':
          return 'Kunne ikke initialisere.';
        case 'pl':
          return 'Inicjowanie nie powiodło się.';
        case 'pt-BR':
          return 'Falha no arranque.';
        case 'pt-PT':
          return 'Falha ao arrancar.';
        case 'ru-RU':
          return 'Не удалось инициализироваться.';
        case 'sv-SE':
          return 'Det gick inte att initiera.';
        case 'zh-CN':
          return '发起失败。';
        case 'zh-TW':
          return '發起失敗。';
        case 'zh':
          return '發起失敗。';
        case 'en':
        default:
          return 'Failed to initialize.';
      }
    }
    return params.key;
  }
}

@Component({
  selector: 'mi-chat',
  templateUrl: 'chat.component.html',
  styleUrls: ['chat.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TranslateService,
    {
      provide: MissingTranslationHandler,
      useClass: ChatMissingTranslationHandler
    },
    EmojiPipe
  ],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(500, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))])
    ])
  ],
  standalone: false
})
export class ChatComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  // ============================================================================
  // CONSTANTS & CONFIGURATION DATA
  // ============================================================================
  private readonly oldRequestError = 'Old Request';

  public readonly emojiDictionary = [
    { key: ':)', regex: ':\\)', value: '1F642' },
    { key: ':D', regex: ':D', value: '1F603' },
    { key: ':(', regex: ':\\(', value: '1F641' },
    { key: ';)', regex: ';\\)', value: '1F609' },
    { key: ":'(", regex: ":\\'\\(", value: '1F622' },
    { key: ':/', regex: ':\\/', value: '1F615' },
    { key: ':o', regex: ':o', value: '1F626' },
    { key: ':O', regex: ':O', value: '1F626' },
    { key: ':p', regex: ':p', value: '1F61B' },
    { key: ':P', regex: ':P', value: '1F61B' },
    { key: ';p', regex: ';p', value: '1F61C' },
    { key: ';P', regex: ';P', value: '1F61C' },
    { key: '-_-', regex: '-_-', value: '1F611' }
  ];

  private readonly streamOnlyCommands: string[] = ['invite', 'join', 'kick', 'leave', 'rename'];
  private readonly hiddenCommands: string[] = ['join', 'kick', 'leave', 'rename', 'who'];

  private defaultNext = {
    $skip: 0,
    $top: this.chatConversationService.pageSize
  };

  // ============================================================================
  // @INPUT PROPERTIES
  // ============================================================================
  @Input() public conversationId: string;
  @Input() public previewMode: boolean;
  @Input() public reloadUser: boolean;

  @Input() public locale: string = 'en';
  @Input() public config: ChatConfig;
  @Input() public aiMode: boolean;
  @Input() public aiConfig: ChatAiConfig;

  @Input() public postKeepAlive: boolean;
  @Input() public theme: string;
  @Input() public customTheme: any;
  @Input() public downloadAttachmentOverride: Function;
  @Input() public recordStartOverride: Function;
  @Input() public recordEndOverride: Function;
  @Input() public fullScreenPdfOverride: Function;
  @Input() public sendOverride: Function;
  @Input() public hostOffline: boolean;

  //Have to call a function in a setter to expose it in the custom element...
  //Used by MiCollab
  //TBD - see if there's a way to expose the function directly
  @Input()
  set goToMessageInput(messageId) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.goToMessageInput()'
    });
    this.goToMessage({ messageId, forceSearch: true });
  }
  @Input()
  set findMessageInput(messageId) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.findMessageInput()'
    });
    this.findMessage(messageId);
  }
  @Input()
  set sendTextInput(message) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.sendTextInput()'
    });
    this.sendText(message);
  }
  @Input()
  set setTextInput(message) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.setTextInput()'
    });
    this.setText(message);
  }
  @Input()
  set getTextInput(getText) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.getTextInput()'
    });
    this.getText();
  }
  @Input()
  set setCaretInput(atStart) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.setCaretInput()'
    });
    this.setCaret(atStart);
  }
  @Input()
  set clearUnreadMessagesInput(clear) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.clearUnreadMessagesInput()'
    });
    this.clearUnreadMessages();
  }
  @Input()
  set focusTextAreaInput(focus) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.focusTextAreaInput()'
    });
    this.focusTextArea();
  }
  @Input()
  set resubscribeChatSubscriptionsInput(focus) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.resubscribeChatSubscriptionsInput()'
    });
    this.resubscribeChatSubscriptions(false);
  }
  @Input()
  set scrollToBottomInput(scroll) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.triggerScrollToBottom()'
    });
    this.scrollingService.scrollToBottom();
  }
  //MiCollab passes a Conversation object they want added to the indexedDB
  @Input()
  set handleDbConversationInput(conversation: Conversation) {
    if (conversation) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.handleDbConversationInput()',
        logParameters: [conversation?.conversationId]
      });
      this.handleDbConversation(conversation);
    }
  }
  //MiCollab passes a UserConversation object they want added to the indexedDB
  @Input()
  set handleDbUserConversationInput(userConversation: UserConversation) {
    if (userConversation) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.handleDbUserConversationInput()',
        logParameters: [userConversation?.conversation.conversationId]
      });
      this.handleDbConversation(userConversation?.conversation as Conversation);
    }
  }
  //MiCollab passes a Conversation object array they want added to the indexedDB
  @Input()
  set handleDbConversationListInput(conversations: Conversation[]) {
    if (conversations) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.handleDbConversationList()',
        logParameters: [conversations?.map(c => c?.conversationId)]
      });
      this.handleDbConversationList(conversations);
    }
  }
  //MiCollab passes a UserConversation object array they want added to the indexedDB
  @Input()
  set handleDbUserConversationListInput(userConversations: UserConversation[]) {
    if (userConversations) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.handleDbUserConversationListInput()',
        logParameters: [userConversations?.map(c => c?.conversation?.conversationId)]
      });
      this.handleDbUserConversationList(userConversations);
    }
  }
  @Input()
  set reloadConversationInput(reload) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.reloadConversationInput()'
    });
    this.reloadConversation();
  }

  //MiCollab passes Message object they want added to the indexedDB
  //to mitigate/inject message in case user opens chat from pop-up notification
  //and new message is not there yet due to Elastic Search being eventually consistent
  @Input()
  set handleMessageNotificationPayload(message: Message) {
    if (message) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.handleMessageNotificationPayload()',
        logParameters: [message?.conversationId, message?.messageId]
      });
      this.handleMessageNotificationInput(message);
    }
  }

  @Input()
  set exitFullScreenInput(input) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.exitFullScreenInput()'
    });
    this.exitFullScreen();
  }

  // ============================================================================
  // @OUTPUT PROPERTIES
  // ============================================================================
  @Output() onGoHome: EventEmitter<void> = new EventEmitter<void>();
  @Output() onAvatarMouseEnter: EventEmitter<AvatarEvent> = new EventEmitter<AvatarEvent>();
  @Output() onAvatarMouseLeave: EventEmitter<AvatarEvent> = new EventEmitter<AvatarEvent>();
  @Output() onAvatarContextMenu: EventEmitter<AvatarEvent> = new EventEmitter<AvatarEvent>();
  @Output() onMessageContextMenu: EventEmitter<ChatMessageEvent> = new EventEmitter<ChatMessageEvent>();
  @Output() onMessageSendAttempt: EventEmitter<MessageEx> = new EventEmitter<MessageEx>();
  @Output() onMessageSendSuccess: EventEmitter<MessageEx> = new EventEmitter<MessageEx>();
  @Output() onMessageSendFail: EventEmitter<MessageEx> = new EventEmitter<MessageEx>();
  @Output() onConversationLoadStart: EventEmitter<ConversationLoadEvent> = new EventEmitter<ConversationLoadEvent>();
  @Output() onConversationLoadEnd: EventEmitter<ConversationLoadEvent> = new EventEmitter<ConversationLoadEvent>();
  @Output() onConversationLoadError: EventEmitter<ConversationLoadEvent> = new EventEmitter<ConversationLoadEvent>();
  @Output() onConversationFirstFetch: EventEmitter<FirstFetchEvent> = new EventEmitter<FirstFetchEvent>();
  @Output() onInitalizationError: EventEmitter<any> = new EventEmitter<any>();
  @Output() onShareLocation: EventEmitter<CustomEvent> = new EventEmitter<CustomEvent>();
  @Output() onInfoLog: EventEmitter<ChatLogEvent> = new EventEmitter<ChatLogEvent>();
  @Output() onErrorLog: EventEmitter<ChatLogEvent> = new EventEmitter<ChatLogEvent>();
  @Output() onGetText: EventEmitter<string> = new EventEmitter<string>();
  @Output() onWindowOpen: EventEmitter<string> = new EventEmitter<string>();
  @Output() onRecordStart: EventEmitter<void> = new EventEmitter<void>();
  @Output() onRecordEnd: EventEmitter<void> = new EventEmitter<void>();
  @Output() onHeaderButtonClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() onActionCardOpenLink: EventEmitter<CustomEvent> = new EventEmitter<CustomEvent>();
  @Output() onActionCardEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() onUploadStart: EventEmitter<string> = new EventEmitter<string>();
  @Output() onUploadEnd: EventEmitter<string> = new EventEmitter<string>();
  @Output() onUploadError: EventEmitter<string> = new EventEmitter<string>();
  @Output() onParticipantsChanged: EventEmitter<ConversationParticipants> =
    new EventEmitter<ConversationParticipants>();
  @Output() onGoToAiSuggestionsMessage: EventEmitter<string> = new EventEmitter<string>();
  @Output() onGoToLatest: EventEmitter<void> = new EventEmitter<void>();
  @Output() onUnreadUpdated: EventEmitter<UnreadUpdate> = new EventEmitter<UnreadUpdate>();
  @Output() onInputSaved: EventEmitter<any> = new EventEmitter<any>();
  @Output() onNameClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() onMentionClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() onDbConversationListResynced: EventEmitter<DatabaseConversation[]> = new EventEmitter<
    DatabaseConversation[]
  >();
  @Output() onAnalyticEvent: EventEmitter<AnalyticObject> = new EventEmitter<AnalyticObject>();
  @Output() onSelectMessageMode: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onIgniteHotkeyPressed: EventEmitter<string> = new EventEmitter<string>();
  @Output() onFullScreenMode: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onAiThreadReady: EventEmitter<string> = new EventEmitter<string>();
  @Output() onAiSourcesReceived: EventEmitter<{ messageId: string; sources: { title: string; chunk: string }[] }> =
    new EventEmitter<{ messageId: string; sources: { title: string; chunk: string }[] }>();
  @Output() onAiStreamError: EventEmitter<{ messageId: string; error: string }> = new EventEmitter<{
    messageId: string;
    error: string;
  }>();
  // The aiConfig.handoff chip was clicked. Nothing is sent to MAIS - the host starts its own
  // live-agent, callback or transfer flow, using sessionId to look up the conversation if needed.
  @Output() onAiHandoffRequested: EventEmitter<{ conversationId: string; sessionId: string }> = new EventEmitter<{
    conversationId: string;
    sessionId: string;
  }>();

  // ============================================================================
  // @VIEWCHILD & @VIEWCHILDREN PROPERTIES
  // ============================================================================
  @ViewChild('chatMain', { static: true }) chatMain: ElementRef;
  @ViewChild('header') header: ElementRef;
  @ViewChild('maincontent', { static: true }) maincontent: ElementRef;
  @ViewChild('dialogBoxes', { static: true }) dialogBoxes: ElementRef;
  @ViewChild('chatList') chatList: ElementRef;
  @ViewChild('textArea') textArea: ElementRef;
  @ViewChild('commandsDropdown') commandsDropdown: Menu;
  @ViewChild('mentionsDropdown') mentionsDropdown: Menu;
  @ViewChild('attachMenu') attachMenu: AttachMenuComponent;

  // Backs the text-area's aria-haspopup/aria-controls: id of whichever suggestion dropdown
  // (command or mention) is currently open, or null if neither is - WCAG 1.3.1.
  get suggestionsDropdownId(): string | null {
    if (this.commandsDropdown?.overlayVisible) {
      return 'commands-dropdown';
    }
    if (this.mentionsDropdown?.overlayVisible) {
      return 'mentions-dropdown';
    }
    return null;
  }
  @ViewChild('mentionsAnchor') mentionsAnchor: ElementRef;
  @ViewChild('emojisDropdown') emojisDropdown: Popover;
  @ViewChild('recordAudio') recordAudio: ElementRef;
  @ViewChild('attachmentInput', { static: true }) attachmentInput: ElementRef;
  @ViewChild('fullscreendownload', { static: true })
  fullScreenDownload: ElementRef;
  @ViewChild('fullscreenimagecontainer') fullScreenImageContainer: ElementRef;
  @ViewChild('fullscreenimg') fullScreenImg: ElementRef;
  @ViewChild('quotedisplay') quoteDisplay: ElementRef;
  @ViewChild(CdkDrag) cdkDrag: CdkDrag;
  @ViewChild('mobileRecordingBar') mobileRecordingBar: ElementRef;
  @ViewChildren(forwardRef(() => ChatItemComponent))
  chatItems: QueryList<ChatItemComponent>;
  @ViewChild('iphoneInput') iphoneInput: ElementRef;
  @ViewChild('recordVideo') videoPreview: ElementRef<HTMLVideoElement>;
  @ViewChild(EmojiPickerComponent) emojiPicker: EmojiPickerComponent;
  @ViewChild('imageFullScreenDownload') imageFullScreenDownload: any;
  @ViewChild('imageFullScreenZoomIn') imageFullScreenZoomIn: any;
  @ViewChild('pdfFullScreenDownload') pdfFullScreenDownload: any;
  @ViewChild('pdfFullScreenExit') pdfFullScreenExit: any;
  @ViewChildren('dropdownMentions') dropdownMentions: QueryList<ElementRef>;
  @ViewChildren('dropdownCommands') dropdownCommands: QueryList<ElementRef>;
  @ViewChild('audio') audio: ElementRef;
  @ViewChild('waveformsCanvas') waveformsCanvas: ElementRef;
  @ViewChild('progressCanvas') progressCanvas: ElementRef;
  @ViewChild('playRecordingBtn') playRecordingBtn: ElementRef;
  @ViewChild('audioRecordBtn') audioRecordBtn: ElementRef;
  @ViewChild('locationBtn') locationBtn: ElementRef;
  @ViewChild('dragDropOkBtn') dragDropOkBtn: ElementRef;
  @ViewChild('fullScreenClose') fullScreenClose: ElementRef;

  @HostListener('window:keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
    if (!this.isMobile && (this.mentionsDropdown?.overlayVisible || this.commandsDropdown?.overlayVisible)) {
      if (event.keyCode === ENTER || event.code === KeyboardKeys.ENTER || event.code === KeyboardKeys.NUMPADENTER) {
        event.preventDefault();
        this.preventSend = true;
        this.selectDropdown();
        this.textAreaProps.commandPending = false;
        this.textAreaProps.mentionPending = false;
        this.closeTextInputDropdowns();
        this.cdRef.markForCheck();
      } else if (event.keyCode === UP_ARROW || event.code === KeyboardKeys.ARROWUP) {
        event.preventDefault();
        this.setDropdownIndex(this.textAreaProps.selectedDropdownIndex - 1);
      } else if (event.keyCode === DOWN_ARROW || event.code === KeyboardKeys.ARROWDOWN) {
        event.preventDefault();
        this.setDropdownIndex(this.textAreaProps.selectedDropdownIndex + 1);
      } else if (event.key === KeyboardKeys.ESCAPE || event.key === KeyboardKeys.ESC) {
        event.preventDefault();
        this.textAreaProps.commandPending = false;
        this.textAreaProps.mentionPending = false;
        this.closeTextInputDropdowns();
        this.cdRef.markForCheck();
      } else if (event.key === KeyboardKeys.TAB) {
        // Close without preventDefault so focus still moves on
        this.textAreaProps.commandPending = false;
        this.textAreaProps.mentionPending = false;
        this.closeTextInputDropdowns();
        this.cdRef.markForCheck();
      }
    } else if (
      (event.keyCode === UP_ARROW || event.code === KeyboardKeys.ARROWUP) &&
      !this.editModeOpen &&
      !this.textArea.nativeElement.innerHTML &&
      this.config?.showMessageOptionsEdit &&
      this.chatAuthService.allowEditDelete &&
      (this.chatFocused || this.chatInputFocused) &&
      !this.config?.smsMode
    ) {
      let lastMineChatItem = this.chatItems
        .toArray()
        .reverse()
        .find(chatItem => chatItem.isMine && chatItem.isTextContentType);
      lastMineChatItem?.editMessage(event);
    }
  }

  @HostListener('document:keydown.control.q', ['$event']) onIgniteHotKeydownQHandler(event: KeyboardEvent) {
    if (!this.isMobile && this.isCustomElement) {
      event.preventDefault();
      this.onIgniteHotkeyPressed.emit('cl-chat-hotkey-actionbtns');
    }
  }

  @HostListener('document:keydown.control.m', ['$event']) onIgniteHotKeydownIHandler(event: KeyboardEvent) {
    if (!this.isMobile && this.isCustomElement) {
      event.preventDefault();
      this.onIgniteHotkeyPressed.emit('cl-chat-main-nav-hotkey-pressed');
    }
  }

  // ============================================================================
  // UI STATE (Signal-ready)
  // These properties represent UI state that could be converted to signals
  // ============================================================================

  // Loading & Status State (Converted to Signals)
  public loading: WritableSignal<boolean> = signal(true);
  public contentLoading: WritableSignal<boolean> = signal(false);
  public syncing: WritableSignal<boolean> = signal(true);
  public sdkError: WritableSignal<boolean> = signal(false);
  public subscriptionError: WritableSignal<boolean> = signal(false);
  public error: WritableSignal<string | undefined> = signal(undefined);
  public isDisconnected: WritableSignal<boolean> = signal(false);
  public noDbConversation: WritableSignal<boolean> = signal(false);
  public newMessageAnnouncement: WritableSignal<string> = signal('');
  // WCAG 2.4.11: real-time height of the status-banner stack, so message controls
  // scrolled to the top of the list aren't focused underneath an obscuring banner.
  public bannerStackHeight: WritableSignal<number> = signal(0);

  // Recording State
  public isRecording: boolean = false;
  public isVideoRecording: boolean = false;
  public currentRecording = null;
  public currentVideoRecording: Blob;
  public recordingTime: string = '00:00';

  // Upload & Drag State
  public uploading: boolean = false;
  public dragging: boolean = false;
  public draggingError: boolean = false;
  public pendingAttachments: PendingAttachment[] = [];
  public oneDriveUploads = signal<OneDriveUpload[]>([]);
  public attachMenuOpen = false;
  // Where the next files from the picker go - set by the paperclip menu
  private attachTarget: AttachTarget = 'auto';

  // Search & Navigation State
  public search: string;
  public isSearch = false;
  public isSnapshot = false;
  public goingToQuoted = false;

  // Full Screen State
  public isFullScreen = false;
  public isFullScreenPdf = false;
  public isFullScreenImage = false;
  public fullScreenPercentage: number = 100;
  public fullScreenZoom: number = 12.5;
  public fullScreenUseWidth = false;
  public fullScreenSource = undefined;
  public fullScreenName: string = '';
  public fullScreenId: string = '';

  // Text Input & Mentions State
  public message: string = '';
  public textAreaProps: TextAreaProps = {
    lastNode: null,
    lastCaretPos: null,
    message: null,
    maxInputCharacters: 40000,
    mentionInput: null,
    mentionCaretEnd: null,
    mentionNode: null,
    mentionPending: null,
    commandPending: null,
    mentions: [],
    commands: [],
    selectedDropdownIndex: 0,
    mentionFetchAttempt: 0,
    lastInputValue: null,
    lastInputString: null,
    mentionDropdownMargins: { bottom: 0, left: 0 }
  };
  public readOnly: boolean = false;
  public textAreaDisabled = false;
  public textAreaHasTwemoji: boolean = false;
  public stackedTextArea = false;
  public textAreaPaddingRight = 10;
  public inputSaveRestored = false;

  // Dropdown & Menu State
  public dropdownVisible = {
    video_recording: false,
    emoji: false
  };
  private emojiContainerEl: HTMLDivElement | null = null;
  public dropdownMaxHeight = 243 / 14 + 'rem';
  public optionsOpen = false;

  // Message & Conversation State
  public messages: MessageEx[] = [];
  public currentConversation: Conversation = <any>{
    conversationId: '',
    name: 'No Conversation Selected',
    participants: [],
    stream: false
  };
  public participants: Participant[] = [];
  public reactions: {
    [messageId: string]: { [reactionString: string]: MessageReaction[] };
  } = {};

  // Unread Message State
  public _lastReadMessageDate: Date | undefined;
  public lastReadMessageDateString: string | undefined;
  get lastReadMessageDate(): Date | undefined {
    return this._lastReadMessageDate;
  }
  set lastReadMessageDate(date: Date | undefined) {
    this._lastReadMessageDate = date;
    this.lastReadMessageDateString = this.unreadMessageDatePipe.transform(date, this.dateTimeLocale);
  }
  public hasUnreadMessage: boolean = false;
  public unreadMessageCount: string;
  public unreadMessageString: string;
  public firstUnreadMessage: MessageEx;
  public firstUnreadMessageDateString: string;

  // Quote & Reply State
  public quotedMessage: QuoteMessageCite | undefined = undefined;
  public quotedMessageDisplay: string;

  // Location State
  public currentLocation;
  public isLocationProcessing: boolean = false;

  // Dialog State
  public infoDialogText: string;
  public warningDialogText: string;

  // Tooltip & Item Info State
  public itemName: string;
  public itemCreateUserName: string;
  public itemCreateUserId: string;
  public itemCreateUserAvatarSrc: string;
  public itemCreateTime: Date;
  public showTooltip: boolean;

  // Audio State
  public audioObj: AudioObj = {
    audioPlaying: false,
    audioStart: '',
    audioProgress: '0%',
    audioEnd: ''
  };
  public audioLoaded: boolean = false;
  public audioContext: AudioContext;
  public minWidthForWaveforms = 300;

  // Message Edit & Selection State
  public editModeOpen = false;
  public currentEditMessageId: string;
  public selectMessagesMode = false;
  public messageDeleteList: MessageEx[] = [];
  public messageDeleteListLength: number = 0;

  // Message Info Modal State
  public currentMessageInfoId: string;
  public currentMessageInfoModalRef: DynamicDialogRef;
  public isMessageInfoOpen: boolean;

  // Typing Indicator State
  public remoteTyping: string[] = [];

  // Device & Platform State
  public isMobile = false;
  public isCustomElement: boolean = false;

  // Locale & Configuration State
  public dateTimeLocale: string = 'en';
  public maxFileSizeMb: number = 10;
  public maxRecordingTime: number = 60000;
  public maxImageSize = ChatMaxValues.MaxImageSize;
  public maxParticipantLoad: number = 500;
  public isLargeParticipantLoad: boolean = false;
  public offlineSendingEnabled = false;

  // ============================================================================
  // PRIVATE STATE & IMPLEMENTATION DETAILS
  // These are internal implementation properties
  // ============================================================================

  // Pagination & Fetch State
  private next: any = { $skip: 0, $top: this.chatConversationService.pageSize };
  private snapshotUp: any = { $skip: 0, $top: 25 };
  private snapshotDown: any = { $skip: 0, $top: 25 };
  private moreMessages: boolean = false;
  private moreSnapshotMessagesUp: boolean = false;
  private moreSnapshotMessagesDown: boolean = false;
  private fetchingMessages = 0;
  private fetchNormalCount = 0;

  // Load State Tracking
  private firstLoad: boolean = true;
  private firstLoadFetch: boolean = false;
  private isReload: boolean = false;
  private conversationLoadAttempt = 0;
  private focusedOnLoad = false;

  // Focus & Input State
  private chatFocused: boolean = false;
  private chatInputFocused: boolean = false;
  private typing: boolean = false;
  private ignoreFirstFocus = false;
  private preventSend = false;
  private enterKeyDown = false;
  private lastMessageInput = '';

  // Participant State
  private participantTotal: number = 0;

  // Attachment Tracking
  private lastAttachmentIds: string[] = [];
  private pendingAttachmentIds: string[] = [];
  private uploadCount = 0;

  // Message Tracking
  private _lastMessageSend: { sendId: string; createdOn: Date };
  private dbLastReadMessageDate: string = null;

  // Date Tracking
  private dateTracker: any = {};

  // Timeout Tracking
  private remoteTypingTimeouts: { [userId: string]: any } = {};
  private recordingInterval: any;
  private goingToQuotedTimeout;
  private imageFullScreenTimeout;
  private resizeScrollTimeout;
  private infoDialogTimeout;
  private warningDialogTimeout;
  private justSentButtonTimeout: any;
  private ignoreFirstFocusTimeout: any;
  private resyncTimeout: any;
  private reloadConversationTimeout: any;
  private resizeTimer;

  // Misc Flags & State
  private justSentButton = false;
  private inputFunction;
  private suggestionCount: number = 0;
  private scrollContainerHeightSnapshot: number | undefined;
  private lastTouchedElementWasChatItem: boolean = false;
  private isActive = true;
  private mentionRefs = [];
  private blobUrl: string;
  private previousConversationId: string;

  // Drag & Drop State
  public hammer: Hammer = null;
  public disableCdkDrag: boolean = false;

  // ============================================================================
  // REACTIVE STREAMS & SUBSCRIPTIONS
  // These are RxJS subjects and subscriptions
  // ============================================================================

  // Subjects for User Input
  public keyUpSubject = new Subject<any>();
  public keyDownSubject = new Subject<any>();
  public keyPressSubject = new Subject<any>();
  public chatFocusSubject$ = new Subject<any>();
  private locationClick$ = new Subject<Event>();

  // Lifecycle Subjects
  private ngUnsubscribe: Subject<void> = new Subject();
  private fetchUnsubscribe: Subject<void> = new Subject();
  private loadUnsubscribe: Subject<void> = new Subject();
  private ngOnChangesUnsubscribe = new Subject();

  // Replay Subjects
  private joinFailed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private myParticipantLastReadLoaded$: ReplaySubject<boolean> = new ReplaySubject(1);

  // Subscriptions
  private subs: Subscription = new Subscription();
  private scrollingSubUp: Subscription;
  private scrollingSubDown: Subscription;
  private scrollingSub: Subscription;
  private messageSubscriptionSub: Subscription = new Subscription();
  private errorSub: Subscription = new Subscription();
  private joinFailedSub: Subscription = new Subscription();
  private updateLastReadMessageDateSub: Subscription = new Subscription();
  private updateHasUnreadMessageSub: Subscription = new Subscription();
  private userLoadedSub: Subscription = new Subscription();

  constructor(
    private chatConversationService: ChatConversationService,
    public chatAuthService: ChatAuthService,
    public scrollingService: ScrollingService,
    private recordService: RecordService,
    public locationService: LocationService,
    public chatCloudLinkService: ChatCloudLinkService,
    public chatAdminService: ChatAdminService,
    public translate: TranslateService,
    private unreadMessageDatePipe: UnreadMessageDatePipe,
    private chatDatePipe: ChatDatePipe,
    private zone: NgZone,
    private httpClient: HttpClient,
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
    public cdRef: ChangeDetectorRef,
    public primeThemeService: PrimeThemeService,
    private chatLoggingService: ChatLoggingService,
    private sanitizer: DomSanitizer,
    private chatDatabaseService: ChatDatabaseService,
    private chatMaisService: ChatMaisService,
    private chatOneDriveService: ChatOneDriveService,
    private emojiPipe: EmojiPipe,
    private dialogService: DialogService,
    private destroyRef: DestroyRef
  ) {
    this.isMobile = SharedHelpers.isMobile();

    this.locationClick$.pipe(debounceTime(300), takeUntil(this.ngUnsubscribe)).subscribe(event => {
      this.getLocationPreview(event);
    });

    this.chatLoggingService.infoSubject$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(info => {
      if (info) {
        this.onInfoLog.emit(info);
      }
    });
    this.chatLoggingService.errorSubject$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(error => {
      if (error) {
        this.onErrorLog.emit(error);
      }
    });
    this.chatLoggingService.chatMessageErrorSubject$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(event => {
      if (event.errorMessage) {
        this.error.set(event.errorMessage);
        this.cdRef.markForCheck();
      }
    });
    this.chatLoggingService.chatLocalErrorSubject$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(errorMessage => {
      if (errorMessage) {
        this.sendLocalError(errorMessage);
      }
    });
    this.chatLoggingService.analyticSubject$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(analyticObj => {
      if (analyticObj) {
        this.onAnalyticEvent.emit(analyticObj);
      }
    });
  }

  ngOnInit() {
    performance.mark('chat-init-start');
    //allows for focus outlines to show up
    document.addEventListener('keydown', this.keydownEventListener);

    this.setIndexDb().then(() => {
      performance.mark('chat-indexeddb-done');
      performance.measure('chat:indexeddb-block', 'chat-init-start', 'chat-indexeddb-done');
    });

    if (window.customElements) {
      this.isCustomElement = !!customElements.get('cloudlink-chat');
    }

    this.errorSub.add(
      this.chatCloudLinkService.subscriptionError$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
        if (value) {
          this.chatLoggingService.error({
            logMessage: 'chat.component.ngOnInit(): cloudlink service subscription error',
            logParameters: [value]
          });
          this.subscriptionError.set(true);
          //To allow loading of messages from GETs despite not being able to get message events
          this.chatCloudLinkService.messageSubscriptionReady$.next(true);
          this.cdRef.markForCheck();
          this.onInitalizationError.emit(value);
        }
      })
    );

    this.errorSub.add(
      fromEvent(window, 'online')
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          if (this.subscriptionError) {
            this.chatLoggingService.info({
              logMessage: 'chat.component.ngOnInit(): online event - retrying subscriptions'
            });
            this.retrySubscriptions();
          }
        })
    );

    //Need to reset services just in case user has changed
    if (this.chatAuthService.userId && this.reloadUser) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.ngOnInit() - reloadUser'
      });
      this.chatCloudLinkService.initialize();
      this.chatAuthService.getClaims();
      this.chatAuthService.ready$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.chatConversationService.initialize();
        },
        error: () => {
          this.chatLoggingService.error({
            logMessage: 'chat.component.ngOnInit(): Error waiting for chatAuthService'
          });
          this.sdkError.set(true);
          this.cdRef.markForCheck();
        }
      });
    }

    this.chatConversationService.mentionClicked$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(userId => {
      this.onMentionClick.emit(userId);
    });

    this.chatConversationService.errorDialog$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(error => {
      this.chatLoggingService.messageError(error);
    });

    this.chatConversationService.databaseConversationsResynced$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(databaseConversations => {
        this.onDbConversationListResynced.emit(databaseConversations);
      });

    this.chatFocusSubject$
      .pipe(
        //focusout/in can cause multiple events, debounceTime(1) solves this
        debounceTime(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(focus => {
        if (focus) {
          this.onFocusChat();
        } else {
          this.onBlurChat();
        }
      });

    this.recordService.currentlyPlaying$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(messageId => {
      //pause all other audio files except currently playing one
      if (messageId != 'recording' && this.audioObj?.audioPlaying && this.currentRecording) {
        this.playRecording();
      }
    });

    if (this.config?.jsonMessagesAsSource || this.aiMode) {
      this.setupScrolling();
      // aiMode never reaches initialize() from here, which is the only other place the text-area
      // key handlers get wired up - without them Enter falls through to contenteditable's default
      // (a new line) instead of sending. Safe to add to this.subs: waitForServices() resets it
      // before initialize() re-adds them, and sendTypingIndicator() already no-ops in aiMode.
      if (this.aiMode) {
        this.setupLocalTypingIndicators();
      }
    } else {
      this.waitForServices();
    }

    //Needs to be in ngOnInit to have the locale property initialized
    //Only set if the host app hasn't already configured its own default/fallback language
    if (!this.translate.getFallbackLang()) {
      this.translate.setFallbackLang('en');
    }

    this.cdRef.markForCheck();
    performance.mark('chat-init-end');
    performance.measure('chat:total-init', 'chat-init-start', 'chat-init-end');
  }

  waitForServices(fromOfflineLogin = false) {
    this.chatCloudLinkService.ready$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {},
      error: () => {
        if (!this.chatCloudLinkService.isOffline()) {
          this.chatLoggingService.error({
            logMessage: 'chat.component.waitForServices(): Error waiting for chatCloudlinkService'
          });
          this.sdkError.set(true);
        }
        this.cdRef.markForCheck();
      }
    });
    //Wait for the auth service (which waits for the cloudlink service) to initialize before starting anything
    this.chatAuthService.ready$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.subs.unsubscribe();
        this.subs = new Subscription();
        this.initialize();
        if (fromOfflineLogin) {
          this.onConversationLoadStart.emit({
            conversationId: this.conversationId,
            loadType: ConversationLoadType[ConversationLoadType.FromOffline].toString()
          });
          this.loadConversation();
        }
      },
      error: () => {
        this.chatLoggingService.error({
          logMessage: 'chat.component.waitForServices(): Error waiting for chatAuthService'
        });
        this.sdkError.set(true);
        this.cdRef.markForCheck();
      }
    });
  }

  // Guards against a double bootstrap when conversationId and aiConfig become ready in
  // different ngOnChanges calls (see maybeInitializeAiConversation) or the same one.
  private aiBootstrappedConversationId: string | null = null;
  private aiBootstrapInFlight = false;
  // Bumped by startNewAiSession(). Each send captures it up front and drops its reply if it no
  // longer matches - conversationId stays the same across a new session, so onMessageReceived's
  // own conversationId filter can't tell an old session's late reply from a new one.
  private aiSessionGeneration = 0;

  // aiMode/aiConfig can each go through ngOnChanges independently of conversationId (e.g. a
  // host app resolves aiConfig.authToken asynchronously after conversationId is already set) -
  // call this from every place that might be the one where all three finally line up, instead
  // of only the conversationId-change branch, so a late aiConfig doesn't leave aiMode stuck with
  // no bootstrap ever having run.
  private async maybeInitializeAiConversation(): Promise<void> {
    if (!this.aiMode || !this.aiConfig || !this.conversationId) {
      return;
    }
    // Skip rather than fire a guaranteed-to-fail request - a host app may set aiMode before its
    // own workspaceSlug/authToken inputs are filled in (e.g. two-way-bound form fields that
    // still start empty). Also means this never marks aiBootstrappedConversationId, so it's
    // retried automatically once the config actually arrives, rather than permanently skipped.
    if (!this.aiConfig.workspaceSlug || !this.aiConfig.authToken) {
      return;
    }
    if (this.aiBootstrapInFlight || this.aiBootstrappedConversationId === this.conversationId) {
      return;
    }
    this.aiBootstrapInFlight = true;
    try {
      const succeeded = await this.initializeAiConversation();
      // Only on success - a failed attempt (bad config, transient network error) must stay
      // retryable for this conversationId rather than being silently locked out forever.
      if (succeeded) {
        this.aiBootstrappedConversationId = this.conversationId;
      }
    } finally {
      this.aiBootstrapInFlight = false;
    }
  }

  // aiMode's counterpart to loadConversation(): resumes (or starts) a MAIS session and loads
  // its history into this.messages. Deliberately workspace-level, not thread-based for now -
  // known accepted tradeoff: GET .../chats has no query params to scope it, so this currently
  // loads the entire workspace's history, not just this session's. Real thread support (the only
  // v1 mechanism that actually scopes history per-conversation) is planned but not yet built.
  // Returns whether history actually loaded, so maybeInitializeAiConversation() knows whether
  // this is retryable.
  private async initializeAiConversation(): Promise<boolean> {
    await this.resetState(false, false);
    // Pre-populate rather than letting chat-item.component.ts's getDisplayName() discover these
    // are unresolved and fall through to chatAdminService.getPrincipal() - that does a real
    // CloudLink admin lookup, which 400s for aiMode's synthetic ids (neither the assistant's nor
    // the human's own aiConfig.userId exist as real CloudLink users) and then cascades into a
    // Dexie error trying to cache an "Unknown User" placeholder. Same technique jsonMessagesAsSource
    // already relies on (via its own pre-supplied participants payload).
    this.seedAiAssistantPrincipal();
    if (this.aiConfig.userId) {
      this.chatAdminService.displayNames[this.aiConfig.userId] = this.aiConfig.userId;
    }
    this.onConversationLoadStart.emit({
      conversationId: this.conversationId,
      loadType: ConversationLoadType[ConversationLoadType.Initial].toString()
    });
    this.loading.set(true);
    // syncing defaults to true and is otherwise only ever cleared by CloudLink's own DB<->server
    // sync flow (loadConversation() et al), which aiMode never runs - without this, the "Syncing…"
    // caption stays stuck on screen forever whenever aiMode is the first thing to load (e.g. a
    // reload straight into an aiMode conversation, where no CloudLink code path ever clears it).
    this.syncing.set(false);
    this.cdRef.markForCheck();

    const restored = this.restoreAiSessionState();
    this.aiConfig.sessionId = this.aiConfig.sessionId ?? restored?.sessionId ?? uuid();
    this.saveAiSessionState();

    let succeeded = true;
    try {
      const history: any = await firstValueFrom(this.chatMaisService.getHistory(this.aiConfig));
      this.messages = this.mapAiHistoryToMessages(history);
      // Only once history has actually loaded empty - a failed load stays retryable (see
      // maybeInitializeAiConversation()) and may yet turn out to have history in it.
      this.applyAiGreeting();
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.initializeAiConversation(): Error loading AI history',
        logParameters: [this.conversationId, ex]
      });
      this.messages = [];
      succeeded = false;
    }

    this.updateStartOfDayForMessages();
    this.loading.set(false);
    this.onConversationLoadEnd.emit({ conversationId: this.conversationId });
    this.onAiThreadReady.emit(this.aiConfig.sessionId);
    this.cdRef.markForCheck();
    return succeeded;
  }

  // Also re-run from ngOnChanges on every aiConfig change, so a host app can swap the assistant's
  // name/avatar after bootstrap (e.g. an avatar posted in later by an iframe parent) - the
  // bootstrap guard means initializeAiConversation() itself never runs twice per conversation.
  private seedAiAssistantPrincipal(): void {
    const assistantUserId = this.aiConfig.assistantUserId ?? 'ai-assistant';
    this.chatAdminService.displayNames[assistantUserId] = this.aiConfig.assistantDisplayName ?? assistantUserId;
    if (this.aiConfig.assistantAvatarUrl) {
      this.chatAdminService.avatars[assistantUserId] = {
        50: this.aiConfig.assistantAvatarUrl,
        150: this.aiConfig.assistantAvatarUrl
      };
    }
  }

  // Shows aiConfig.greeting while the conversation has nothing else in it. Also re-run from
  // ngOnChanges on every aiConfig change after bootstrap, so a greeting the host supplies or
  // changes later (e.g. posted in by an iframe parent) replaces the one on screen - but never once
  // a real turn exists, and removing the greeting from aiConfig removes it from an empty chat.
  private applyAiGreeting(): void {
    if (this.messages.some(m => !m.clCompChat_aiLocal)) {
      return;
    }
    const greeting = this.buildAiGreetingMessage();
    this.messages = greeting ? [greeting] : [];
    this.updateStartOfDayForMessages();
  }

  // Reuses buildAiAssistantMessage() so the greeting renders exactly like a finished assistant
  // reply (avatar, name, markdown). A non-closing chunk plus streamingOverride false gives a
  // finished message with no stats row, since there was no real turn to measure.
  private buildAiGreetingMessage(): MessageEx | null {
    const greeting = this.aiConfig?.greeting;
    if (!greeting?.text?.trim()) {
      return null;
    }
    // Fresh id per build, not a stable one: the messages @for tracks by messageId, and chat-item
    // only works out its displayed text once, in ngOnInit (parseMessage()). A reused id makes
    // Angular hand the new greeting to the previous greeting's chat-item - after
    // startNewAiSession() or a greeting config change - which then renders its chips but no text.
    const id = `ai-greeting-${uuid()}`;
    const message = this.buildAiAssistantMessage(
      id,
      greeting.text,
      new Date(),
      this.conversationId,
      this.aiConfig.assistantUserId ?? 'ai-assistant',
      { id, type: 'greeting' },
      false
    );
    const options = normalizeAiQuickReplies(greeting.options ?? []);
    message.clCompChat_aiLocal = true;
    message.clCompChat_quickReplies = options.length ? options : undefined;
    return message;
  }

  private mapAiHistoryToMessages(history: any): MessageEx[] {
    const items = Array.isArray(history) ? history : Array.isArray(history?.history) ? history.history : [];
    return items.map((item: any) => this.aiHistoryItemToMessage(item));
  }

  private aiHistoryItemToMessage(item: any): MessageEx {
    const createdOn = item.sentAt ? new Date(item.sentAt * 1000) : new Date();
    const isUser = (item.role ?? 'user') === 'user';
    const createdBy = isUser ? this.chatAuthService.userId : (this.aiConfig.assistantUserId ?? 'ai-assistant');
    // item.content keeps the raw ```ui block as the model sent it, so a reload strips it again
    // here. Only the user's own text is left untouched.
    const reply: { body: string; quickReplies?: string[] } = isUser
      ? { body: item.content ?? '' }
      : this.splitAiReply(item.content ?? '', true);
    return {
      conversationId: this.conversationId,
      accountId: this.chatAuthService.accountId,
      // chatId is shared between a user turn and its assistant reply, not a per-message id -
      // combine with role so the two don't collide under onMessageReceived's dedupe-by-messageId.
      messageId: item.chatId != null ? `${item.chatId}-${item.role}` : this.getLocalMessageId(),
      body: reply.body,
      contentType: isUser ? ContentTypes.TEXT : ContentTypes.MARKDOWN,
      createdOn,
      createdBy,
      modifiedOn: createdOn,
      modifiedBy: createdBy,
      clCompChat_state: 'sent',
      // GET .../chats returns a completely different shape from the SSE chunks (role/content,
      // not id/textResponse) but still carries sources/toolCalls straight on the history item
      // itself - a live capture confirmed both are present here, so a reloaded/restored
      // conversation must map them the same way buildAiAssistantMessage does for a live turn, or
      // ai-sources-card/ai-thinking-card silently never render for anything loaded from history.
      clCompChat_sources: item.sources?.length ? item.sources : undefined,
      clCompChat_toolCalls: item.toolCalls?.length ? item.toolCalls : undefined,
      clCompChat_quickReplies: reply.quickReplies,
      // No latencyMs here - there's no send-time reference to time from for a turn reloaded from
      // history, unlike a live send (see buildAiAssistantMessage). item.type carries the mode
      // ('chat'/'query') on the history item itself (a live capture confirmed this); falling back
      // to the current aiConfig.mode covers a server response that omits it.
      clCompChat_stats: !isUser
        ? {
            totalTokens: item.metrics?.total_tokens,
            retrievedChunks: item.sources?.length ?? 0,
            mode: item.type ?? this.aiConfig?.mode
          }
        : undefined,
      _links: {}
    };
  }

  private aiSessionStorageKey(workspaceSlug: string): string {
    return `clComponentChatAiSession_${workspaceSlug}`;
  }

  private saveAiSessionState(): void {
    if (!this.aiConfig?.workspaceSlug) {
      return;
    }
    sessionStorage.setItem(
      this.aiSessionStorageKey(this.aiConfig.workspaceSlug),
      JSON.stringify({ sessionId: this.aiConfig.sessionId })
    );
  }

  private restoreAiSessionState(): { sessionId?: string } | null {
    if (!this.aiConfig?.workspaceSlug) {
      return null;
    }
    const saved = sessionStorage.getItem(this.aiSessionStorageKey(this.aiConfig.workspaceSlug));
    return saved ? JSON.parse(saved) : null;
  }

  // Running { body, createdOn, sources, toolCalls } per in-flight assistant turn, keyed by the
  // SSE chunk's stable `id` - MAIS's textResponse per chunk is a DELTA, not the full accumulated
  // text, and createdOn is pinned to the first chunk so re-sorts in onMessageReceived don't
  // reorder a message mid-stream. `sources` retains the last non-empty sources array seen for
  // the turn: a live capture showed the real server sends sources on intermediate chunks and
  // OMITS the field entirely on the closing chunk, so reading sources off the close chunk
  // directly (as this used to) never actually surfaces them - the accumulator is what makes them
  // durable. `toolCalls` is kept the same way even though live captures show the server resends
  // the full array on every chunk rather than delta-ing it (so "latest" is always "complete") -
  // matching sources' pattern means one code path covers both, and it's still needed to survive
  // a close chunk the same way if that ever changes server-side. Deliberately NOT wired to
  // ChatMaisService.messagesChanged$: that subject is a page-wide providedIn:'root' singleton, so
  // a shared subscription there would deliver every chunk to every ChatComponent instance with no
  // way to tell whose turn it belongs to. Each sendMessage() call instead gets its own private
  // routing via streamChat()'s onChunk callback, and captures conversationId up front so a
  // mid-stream conversation switch can't relabel a still-in-flight reply onto the wrong
  // conversation.
  private aiAccumulatingMessages = new Map<
    string,
    { body: string; createdOn: Date; sources?: MaisSource[]; toolCalls?: MaisToolCall[] }
  >();

  // Coalesces one send's raw per-token chunks (via a private, per-call Subject fed by streamChat's
  // onChunk callback) so onMessageReceived - which re-sorts/re-filters the whole message list -
  // isn't called once per token. pendingIds tracks ids this call has started accumulating, so the
  // caller can finalize anything left stuck 'pending' if the stream promise ultimately rejects
  // (a non-2xx response, a transport error, or MAIS's own type:"abort" - streamChat() throws on
  // all three rather than emitting them, so there's no separate "abort" event to handle here).
  private subscribeToAiChunks(
    conversationId: string,
    assistantUserId: string,
    pendingIds: Set<string>,
    sendStartedAt: number,
    sessionGeneration: number = this.aiSessionGeneration
  ): { chunks$: Subject<MaisStreamChunk>; subscription: Subscription } {
    const chunks$ = new Subject<MaisStreamChunk>();
    const subscription = chunks$
      .pipe(
        bufferTime(40),
        filter(chunks => chunks.length > 0)
      )
      .subscribe(chunks => {
        // startNewAiSession() ran since this turn was sent - its reply belongs to the old session.
        if (sessionGeneration !== this.aiSessionGeneration) {
          return;
        }
        const latestById = new Map<string, MaisStreamChunk>();
        for (const chunk of chunks) {
          const existing = this.aiAccumulatingMessages.get(chunk.id);
          if (!existing) {
            this.aiAccumulatingMessages.set(chunk.id, {
              body: chunk.textResponse ?? '',
              createdOn: new Date(),
              sources: chunk.sources?.length ? chunk.sources : undefined,
              toolCalls: chunk.toolCalls?.length ? chunk.toolCalls : undefined
            });
            pendingIds.add(chunk.id);
            this.clearRemoteTyping(assistantUserId);
          } else {
            existing.body += chunk.textResponse ?? '';
            if (chunk.sources?.length) {
              existing.sources = chunk.sources;
            }
            if (chunk.toolCalls?.length) {
              existing.toolCalls = chunk.toolCalls;
            }
          }
          latestById.set(chunk.id, chunk);
        }
        latestById.forEach((chunk, id) => {
          const accumulated = this.aiAccumulatingMessages.get(id);
          // accumulated.body stays the raw text - only the built message is split, so the ```ui
          // block can still be parsed whole from the accumulator once the turn closes.
          const reply = this.splitAiReply(accumulated?.body ?? '', !!chunk.close);
          const message = this.buildAiAssistantMessage(
            id,
            reply.body,
            accumulated?.createdOn ?? new Date(),
            conversationId,
            assistantUserId,
            chunk,
            undefined,
            accumulated?.sources,
            accumulated?.toolCalls,
            sendStartedAt
          );
          message.clCompChat_quickReplies = reply.quickReplies;
          this.onMessageReceived(message);
          if (chunk.close) {
            if (accumulated?.sources?.length) {
              this.onAiSourcesReceived.emit({ messageId: id, sources: accumulated.sources });
            }
            this.aiAccumulatingMessages.delete(id);
            pendingIds.delete(id);
          }
        });
        this.cdRef.markForCheck();
      });
    return { chunks$, subscription };
  }

  // Finalizes any of this call's messages still stuck mid-stream (the stream promise rejected
  // before a close:true chunk arrived) - stops the streaming cursor instead of leaving it stuck
  // forever. clCompChat_state stays 'sent': onAiStreamError (emitted separately, in the catch
  // block that calls this) is the signal a host app reacts to, not a bubble-level decoration.
  private failPendingAiMessages(pendingIds: Set<string>, conversationId: string, assistantUserId: string): void {
    pendingIds.forEach(id => {
      const accumulated = this.aiAccumulatingMessages.get(id);
      this.onMessageReceived(
        this.buildAiAssistantMessage(
          id,
          // Aborted turns keep their partial prose but never offer chips.
          this.splitAiReply(accumulated?.body ?? '', true).body,
          accumulated?.createdOn ?? new Date(),
          conversationId,
          assistantUserId,
          { id, type: 'abort' },
          false,
          accumulated?.sources,
          accumulated?.toolCalls
        )
      );
      this.aiAccumulatingMessages.delete(id);
    });
    pendingIds.clear();
    this.cdRef.markForCheck();
  }

  // aiConfig.quickReplies' single entry point: every assistant body (live stream, non-streaming
  // reply, abort, history reload) passes through here, so the ```ui block is handled the same way
  // however the turn arrived. `final` is false only mid-stream, where the block may still be
  // incomplete - the body is cut at the fence and no chips are offered until the turn closes.
  private splitAiReply(raw: string, final: boolean): { body: string; quickReplies?: string[] } {
    if (!this.aiConfig?.quickReplies) {
      return { body: raw };
    }
    if (!final) {
      return { body: visibleAiReplyPortion(raw) };
    }
    const { text, quickReplies } = parseAiReply(raw);
    return { body: text, quickReplies: quickReplies.length ? quickReplies : undefined };
  }

  // clCompChat_state is deliberately always 'sent' here, never 'pending'/'failed' - that field
  // means "outbound send lifecycle" everywhere else (mine-only by construction), and reusing it
  // for "still generating"/"generation failed" would collide with every pre-existing check that
  // assumes those states only ever apply to the current user's own message. clCompChat_aiStreaming
  // carries the "still generating" signal instead, so no isMine() gating is needed anywhere else.
  private buildAiAssistantMessage(
    id: string,
    body: string,
    createdOn: Date,
    conversationId: string,
    assistantUserId: string,
    chunk: MaisStreamChunk,
    streamingOverride?: boolean,
    // Accumulated turn-level sources/toolCalls (see aiAccumulatingMessages) take precedence;
    // falling back to chunk.sources/chunk.toolCalls covers the non-streaming chat() call site,
    // which has no accumulator and passes its single response object as `chunk`.
    sources?: MaisSource[],
    toolCalls?: MaisToolCall[],
    // Wall-clock ms timestamp (Date.now()) captured right before the send call - only meaningful
    // together with chunk.close, and only ever passed by sendMessage()'s two live call sites, not
    // failPendingAiMessages() (an abort never carries close:true, so stats never compute for it).
    sendStartedAt?: number
  ): MessageEx {
    const resolvedSources = sources ?? chunk.sources;
    const resolvedToolCalls = toolCalls ?? chunk.toolCalls;
    return {
      conversationId,
      accountId: this.chatAuthService.accountId,
      messageId: id,
      body,
      contentType: ContentTypes.MARKDOWN,
      createdOn,
      createdBy: assistantUserId,
      modifiedOn: new Date(),
      modifiedBy: assistantUserId,
      clCompChat_state: 'sent',
      clCompChat_aiStreaming: streamingOverride ?? !chunk.close,
      clCompChat_sources: resolvedSources?.length ? resolvedSources : undefined,
      clCompChat_toolCalls: resolvedToolCalls?.length ? resolvedToolCalls : undefined,
      clCompChat_stats: chunk.close
        ? {
            latencyMs: sendStartedAt !== undefined ? Date.now() - sendStartedAt : undefined,
            totalTokens: chunk.metrics?.total_tokens,
            retrievedChunks: resolvedSources?.length ?? 0,
            mode: this.aiConfig?.mode
          }
        : undefined,
      _links: {}
    } as MessageEx;
  }

  ngAfterViewInit() {
    //ios will require two clicks if you add hover css, so we won't have hover css for it
    if (
      this.chatCloudLinkService.browser.os.toLowerCase() !== 'ios' &&
      this.chatMain.nativeElement.className.indexOf('chat-has-hover') === -1
    ) {
      if (this.chatMain.nativeElement.className === '') {
        this.chatMain.nativeElement.className = 'chat-has-hover';
      } else {
        this.chatMain.nativeElement.className += ' chat-has-hover';
      }
    }

    setTimeout(() => {
      if (SharedHelpers.isMobile()) {
        this.inputFunction = this.textInput.bind(this);
        this.textArea.nativeElement.addEventListener('input', this.inputFunction);
      }
    });
    //ResizeSensor works on individual elements for size changes
    new ResizeSensor(this.maincontent.nativeElement, this.resizeSensorFunc);
    new ResizeSensor(this.dialogBoxes.nativeElement, this.dialogBoxesResizeFunc);
  }

  // WCAG 2.4.11: keep --banner-stack-height in sync with however many status banners
  // are actually showing, so .main-content's scroll-padding-top never over/under-shoots.
  dialogBoxesResizeFunc = () => {
    this.bannerStackHeight.set(this.dialogBoxes.nativeElement.offsetHeight);
  };

  resizeSensorFunc = () => {
    if (this.maincontent.nativeElement.offsetWidth <= 350) {
      this.stackedTextArea = true;
    } else {
      this.stackedTextArea = false;
    }
    this.updatePaddingRight();

    let pixelMaxHeight = this.maincontent.nativeElement.offsetHeight - 28;

    this.dropdownMaxHeight = pixelMaxHeight < 243 ? pixelMaxHeight / 14 + 'rem' : 243 / 14 + 'rem';
    //To handle iOS zoom on safari
    if (
      this.chatCloudLinkService.browser.os.toLowerCase() === 'ios' &&
      this.chatCloudLinkService.browser.name.toLowerCase() === 'ios'
    ) {
      this.dropdownMaxHeight = '8.75rem'; //140px
    }
    this.chatLoggingService.debug({
      logMessage: 'chat.component.ResizeSensor(): RESIZE',
      logParameters: [this.maincontent.nativeElement.offsetHeight, this.maincontent.nativeElement.offsetWidth]
    });
    this.chatLoggingService.debug({
      logMessage: 'chat.component.ResizeSensor(): spaceFromBottom',
      logParameters: [this.scrollingService.spaceFromBottom]
    });
    //Sometimes the scroll event triggers before the resize
    if (
      this.scrollingService.previousSpaceFromBottom != null &&
      this.scrollingService.previousSpaceFromBottom !== this.scrollingService.spaceFromBottom
    ) {
      this.chatLoggingService.debug({
        logMessage: 'chat.component.ResizeSensor(): previousSpaceFromBottom',
        logParameters: [this.scrollingService.previousSpaceFromBottom]
      });
      this.scrollingService.spaceFromBottom = this.scrollingService.previousSpaceFromBottom;
    }
    this.chatLoggingService.debug({
      logMessage: 'chat.component.ResizeSensor(): clientHeight',
      logParameters: [this.maincontent.nativeElement.clientHeight]
    });
    this.chatLoggingService.debug({
      logMessage: 'chat.component.ResizeSensor(): scrollTop',
      logParameters: [this.maincontent.nativeElement.scrollTop]
    });
    this.scrollingService.checkForSpaceFromBottom = false;
    //Need to use a timeout because of the scroll height potentially changing because of image resizing
    if (this.resizeScrollTimeout) {
      clearTimeout(this.resizeScrollTimeout);
      this.resizeScrollTimeout = null;
    }
    this.resizeScrollTimeout = setTimeout(() => {
      this.chatLoggingService.debug({
        logMessage: 'chat.component.ResizeSensor(): scrollHeight spaceFromBottom',
        logParameters: [this.maincontent.nativeElement.scrollHeight, this.scrollingService.spaceFromBottom]
      });
      var newScrollTop =
        this.maincontent.nativeElement.scrollHeight -
        this.maincontent.nativeElement.clientHeight -
        this.scrollingService.spaceFromBottom;
      this.chatLoggingService.debug({
        logMessage: 'chat.component.ResizeSensor(): new scrollTop',
        logParameters: [newScrollTop]
      });
      if (newScrollTop < 0) {
        newScrollTop = 0;
      }
      this.maincontent.nativeElement.scrollTop = newScrollTop;
      this.chatLoggingService.debug({
        logMessage: 'chat.component.ResizeSensor(): new spaceFromBottom',
        logParameters: [this.scrollingService.spaceFromBottom]
      });
      this.detectChanges();
      this.scrollingService.checkForSpaceFromBottom = true;
      this.chatLoggingService.debug({
        logMessage: 'chat.component.ResizeSensor(): Reenable scroll'
      });
    }, 0);
  };

  async ngOnChanges(changes: SimpleChanges) {
    // Handled before the conversationId block below (not after, where this used to live) because
    // that block's chatAuthService.ready$ subscription fires SYNCHRONOUSLY if ready$ has already
    // resolved (a ReplaySubject) - restoring identity has to happen first in this same tick, or
    // the conversationId handler reads the still-stale userId/accountId before this ever runs.
    if (changes?.aiMode || changes?.aiConfig) {
      // Captured before being overwritten below: chatCloudLinkService.aiMode is a root-singleton
      // flag that survives this component being destroyed and recreated (e.g. a host app
      // toggling a "destroyed" flag to force a full reload of the chat UI). A brand-new
      // ChatComponent instance's first ngOnChanges has no changes.aiMode.previousValue at all
      // (Angular has no history for it), so without this, the recovery block below never runs
      // for a fresh instance that boots straight into non-aiMode after a prior instance left the
      // singletons in the aiMode-bypassed state.
      const wasAiMode = this.chatCloudLinkService.aiMode;
      this.chatCloudLinkService.aiMode = !!this.aiMode;
      if (this.aiMode && this.aiConfig) {
        // aiMode has no separate concept of an "account" - reuse userId so isMine()/avatar/
        // read-indicator checks elsewhere, which only key off these two fields, keep working.
        this.chatAuthService.userId = this.aiConfig.userId;
        this.chatAuthService.accountId = this.aiConfig.userId;
        if (this.aiBootstrappedConversationId) {
          // Already-rendered items are OnPush and read chatAdminService.avatars/displayNames
          // straight from their templates - nothing else tells them those maps just changed.
          this.seedAiAssistantPrincipal();
          this.applyAiGreeting();
          this.chatItems?.forEach(c => c.markForCheck());
          this.cdRef.markForCheck();
        }
      } else if ((changes?.aiMode?.previousValue || wasAiMode) && !this.aiMode) {
        // Leaving aiMode. Five things left it in a state the rest of the app can't recover
        // from without a full reload:
        // 1. chatAuthService.userId/accountId are stuck on aiConfig.userId (often undefined).
        // 2. ChatCloudLinkService.initialize() - the thing that actually sets up the real SDK
        //    microservice instances (notify/chat/admin/presence/media) and subscribeToSubs() -
        //    is only ever called from checkForNotify()'s non-aiMode branch. A session that starts
        //    in aiMode never runs it at all, so chatCloudLinkService.notify/.chat etc. stay
        //    undefined for the rest of the app's life unless something calls it explicitly.
        // 3. ChatConversationService/ChatAdminService each subscribed to chatAuthService.ready$
        //    once in their constructor and unsubscribed right after that first (aiMode-bypassed,
        //    correctly no-op'd) firing - nothing else ever re-triggers their real initialize()
        //    (which itself depends on #2 above having already run).
        // 4. chatAuthService.ready$ is a ReplaySubject that already resolved via the aiMode
        //    bypass, so a *new* subscriber (like the conversationId block right below) fires
        //    immediately with the stale state instead of waiting for real identity to resolve.
        // 5. ChatComponent's OWN initialize() (which subscribes to chatCloudLinkService's
        //    messagesChanged$/reloadConversation$/online$ - real-time incoming messages included)
        //    is only ever called from waitForServices(), which ngOnInit() only calls once, and
        //    only if aiMode was already false at that exact moment. A session that starts in
        //    aiMode (e.g. a reload straight into it) never calls it at all, for this component
        //    instance's entire lifetime, unless triggered again here.
        // reset() replaces ready$ with a fresh, unresolved one, so that later subscription
        // genuinely waits; the three service initialize() calls (in dependency order) bypass the
        // dead constructor-time subscriptions directly; waitForServices() re-establishes (or, in
        // the reload-into-aiMode case, establishes for the first time) ChatComponent's own
        // subscriptions - safe to call again since it resets this.subs before re-adding to it;
        // getClaims() re-fetches the real identity via whoAmI() and calls .next(true) on the (now
        // fresh) ready$ once it resolves, which both this and the conversationId block are waiting on.
        try {
          this.chatAuthService.reset();
          this.chatCloudLinkService.initialize();
          this.chatConversationService.initialize();
          this.chatAdminService.initialize();
          this.waitForServices();
          this.chatAuthService.getClaims();
        } catch (ex) {
          this.chatLoggingService.error({
            logMessage: 'chat.component.ngOnChanges(): Error restoring identity after leaving aiMode',
            logParameters: [ex]
          });
        }
        // aiMode's conversationId is a fixed, reused string (e.g. 'ai-mode-demo'), not a fresh
        // id per session - without this, maybeInitializeAiConversation()'s dedup guard sees that
        // exact id already marked bootstrapped from the *previous* time aiMode was on, and skips
        // re-entry entirely, leaving whatever conversation was open in between still on screen.
        this.aiBootstrappedConversationId = null;
      }
      // conversationId may already have been set on an earlier ngOnChanges call (e.g. aiConfig's
      // authToken resolves asynchronously after conversationId is bound) - retry bootstrap here
      // too rather than relying solely on the conversationId-change branch below.
      await this.maybeInitializeAiConversation();
    }
    if (changes?.conversationId && this.conversationId) {
      this.chatLoggingService.debug({
        logMessage: 'chat.component.ngOnChanges(): changes, current, selectMessagesMode',
        logParameters: [changes?.conversationId, this.conversationId, this.selectMessagesMode]
      });

      if (this.selectMessagesMode) {
        this.activateSelectMessagesMode(false);
      } else {
        this.editModeOpen = false;
      }

      if (this.dropdownVisible['emoji']) {
        this.emojisDropdown?.hide();
      }

      //ngOnChanges will occur before ngOnInit
      if (this.previousConversationId) {
        this.saveTextInput(this.previousConversationId);
        this.cancelOneDriveUploads();
      }
      this.previousConversationId = this.conversationId;
      await this.setIndexDb();
      this.dbLastReadMessageDate = null;
      if (this.ngOnChangesUnsubscribe) {
        this.ngOnChangesUnsubscribe.next('');
        this.ngOnChangesUnsubscribe.complete();
        this.ngOnChangesUnsubscribe = new Subject();
      }
      this.chatLoggingService.debug({
        logMessage: 'chat.component.ngOnChanges(): Conversation id changed, waiting for auth',
        logParameters: [
          this.conversationId,
          this.chatCloudLinkService.isOffline(),
          this.config ? this.config.enableIndexDb : false,
          'indexedDB' in window
        ]
      });
      var authSub = new Subscription();
      authSub.add(
        this.chatAuthService.ready$
          .pipe(takeUntil(this.ngOnChangesUnsubscribe), takeUntil(this.ngUnsubscribe))
          .subscribe(
            async () => {
              authSub.unsubscribe();
              this.chatLoggingService.info({
                logMessage: 'chat.component.ngOnChanges(): Conversation id changed',
                logParameters: [
                  this.conversationId,
                  this.chatCloudLinkService.isOffline(),
                  this.config ? this.config.enableIndexDb : false,
                  'indexedDB' in window
                ]
              });
              if (this.aiMode) {
                await this.maybeInitializeAiConversation();
              } else if (this.chatCloudLinkService.jsonMessagesAsSource) {
                this.loadConversation();
              } else if (!this.chatCloudLinkService.isOffline() || !this.indexDbEnabled()) {
                let conversationInDb: IChatDbConversation;
                try {
                  conversationInDb = await this.chatDatabaseService.getConversationFromDb(
                    this.conversationId,
                    this.chatAuthService.accountId
                  );
                } catch (ex) {
                  this.chatLoggingService.error({
                    logMessage: 'chat.component.ngOnChanges(): Error checking if conversation exists in db',
                    logParameters: [ex]
                  });
                }
                if (!conversationInDb) {
                  this.loading.set(true);
                  this.cdRef.markForCheck();
                } else if (conversationInDb.lastReadMessageDate) {
                  this.dbLastReadMessageDate = <any>conversationInDb.lastReadMessageDate;
                }
                // Make sure we are subscribed to msg subscription before loading messages
                // If the socket is not yet up, it'll reload the conversation when it comes up so that is fine also
                this.messageSubscriptionSub.unsubscribe();
                this.messageSubscriptionSub = new Subscription();
                this.messageSubscriptionSub.add(
                  this.chatCloudLinkService.messageSubscriptionReady$
                    .pipe(takeUntil(this.ngUnsubscribe))
                    .subscribe(value => {
                      this.messageSubscriptionSub.unsubscribe();
                      this.chatLoggingService.info({
                        logMessage: 'chat.component.ngOnChanges(): Loading conversation',
                        logParameters: [this.conversationId, value]
                      });
                      if (value) {
                        this.onConversationLoadStart.emit({
                          conversationId: this.conversationId,
                          loadType: ConversationLoadType[ConversationLoadType.Initial].toString()
                        });
                        this.loadConversation(false, true);
                      }
                    })
                );
              } else if (this.indexDbEnabled()) {
                this.chatLoggingService.info({
                  logMessage: 'chat.component.ngOnChanges(): App is offline, attempt to load conversation from DB'
                });
                this.loadConversation();
              }
            },
            er => {
              //Let chat component handle error
            }
          )
      );
    }
    if (changes?.config) {
      if (this.config?.jsonMessagesAsSource) {
        this.chatCloudLinkService.jsonMessagesAsSource = true;
      } else {
        this.chatCloudLinkService.jsonMessagesAsSource = false;
      }
      if (this.config?.userReplacements) {
        this.chatAuthService.ready$.pipe(take(1), takeUntil(this.ngUnsubscribe)).subscribe(
          v => {
            this.chatAdminService.replaceUsers(this.config.userReplacements);
          },
          er => {
            //Let chat component handle error
          }
        );
      }
      if (this.config?.maxInputCharacters && typeof this.config?.maxInputCharacters == 'number') {
        this.textAreaProps.maxInputCharacters = this.config.maxInputCharacters;
      }
      if (this.config?.enableDebugLogs != undefined) {
        this.chatLoggingService.isDebug = this.config.enableDebugLogs;
      }

      if (this.config?.disableLogs != undefined) {
        this.chatLoggingService.disabled = this.config.disableLogs;
      }

      if (this.config?.logLevel != undefined) {
        this.chatLoggingService.logLevel = this.config.logLevel;
      }

      if (this.config?.consoleOutput != undefined) {
        this.chatLoggingService.consoleOutput = this.config.consoleOutput;
      }

      if (this.config?.useNicknames != undefined) {
        this.chatAdminService.useNicknames = this.config.useNicknames;
      }
      if (this.config?.reverseNames != undefined) {
        this.chatAdminService.reverseNames = this.config.reverseNames;
      }
      if (this.config?.maxRecordingTime != undefined) {
        this.maxRecordingTime = this.config.maxRecordingTime;
      }
      //Legacy support
      if (this.config?.hideActionsAddFiles != undefined && this.config?.hideAddFiles == undefined) {
        this.config.hideAddFiles = this.config.hideActionsAddFiles;
      }
      //Legacy support
      if (this.config?.hideActionsShareAudio != undefined && this.config?.hideShareAudio == undefined) {
        this.config.hideShareAudio = this.config.hideActionsShareAudio;
      }
      //Legacy support
      if (this.config?.hideActionsShareLocation != undefined && this.config?.hideShareLocation == undefined) {
        this.config.hideShareLocation = this.config.hideActionsShareLocation;
      }
      if (this.config?.getMessagePageSize) {
        this.chatConversationService.pageSize = this.config.getMessagePageSize;
        this.defaultNext = { $skip: 0, $top: this.config.getMessagePageSize };
      }
      if (this.config?.disableTextArea != undefined) {
        this.textAreaDisabled = this.chatCloudLinkService.isOffline() || this.config.disableTextArea;
        this.setOfflineSending();
      }
      if (this.config?.maxParticipantLoad) {
        this.maxParticipantLoad = this.config.maxParticipantLoad;
      }
      if (this.config?.indexDbConversationListMax) {
        this.chatConversationService.databaseConversationListMax = this.config.indexDbConversationListMax;
      }
      if (this.config?.showMessageOptionsDelete || this.config?.showMessageOptionsEdit) {
        this.chatAuthService.getEditDeletePolicy();
      }
      if (this.config?.logMessageEvents) {
        this.chatCloudLinkService.logMessageEvents = true;
      } else {
        this.chatCloudLinkService.logMessageEvents = false;
      }
      if (this.config?.maxFileSizeMb != undefined) {
        this.maxFileSizeMb = this.config.maxFileSizeMb;
      }

      if (this.config?.isOneToOneChat) {
        SharedHelpers.oneToOneChatDisplayName = this.config.headerTitle;
      } else {
        SharedHelpers.oneToOneChatDisplayName = '';
      }

      if (this.config?.useCustomSystemAvatar && this.config?.customAvatarFile) {
        this.chatLoggingService.info({
          logMessage: 'chat.component - use Custom System Avatar'
        });
      }

      // @labs: Demo purposes
      if (!!localStorage.getItem('@mitel/labs/let-there-be-video')) {
        this.config.showShareVideo = true;
      }

      this.updatePaddingRight();
    }
    if (changes?.locale) {
      this.setLocale();
    }

    if (changes?.theme) {
      const themeMode: 'light' | 'dark' = this.theme === 'dark' ? 'dark' : 'light';
      this.primeThemeService.setTheme(themeMode);
    }

    if (typeof this.hostOffline === 'boolean') {
      this.chatCloudLinkService.setHostOffline(this.hostOffline);
    }
    // Assigning conf to SharedHelpers object so that can be access in services folder
    SharedHelpers.region = (this.config as any)?.regionCode;
    SharedHelpers.cloudlinkLocale = (this.config as any)?.userCloudlinkLocaleCode;

    this.cdRef.markForCheck();
  }

  private updatePaddingRight() {
    if (this.stackedTextArea) {
      this.textAreaPaddingRight = 10;
    } else if (this.config?.smsMode) {
      this.textAreaPaddingRight = 76;
    } else {
      let configToCheck = ['hideEmojis', 'hideAddFiles', 'hideShareAudio', 'hideShareLocation'];
      let configToCheck2 = ['showShareVideo'];
      let configToCheck3 = ['showCharCounter'];
      let paddingRight = 50;
      configToCheck.forEach(c => {
        if (!this.config || !this.config[c]) {
          if (!(c === 'hideEmojis' && this.isMobile) && !(this.message && this.isMobile)) {
            paddingRight += 26;
            if (this.isMobile) {
              paddingRight += 24;
            }
          }
        }
      });
      configToCheck2.forEach(c => {
        if (this.config && this.config[c]) {
          if (!(this.message && this.isMobile)) {
            paddingRight += 26;
            if (this.isMobile) {
              paddingRight += 18;
            }
          }
        }
      });
      configToCheck3.forEach(c => {
        if (this.config && this.config[c]) {
          if (this.message) {
            this.textAreaProps.maxInputCharacters < 1000 ? (paddingRight += 48) : (paddingRight += 68);
          }
        }
      });
      if (this.isMobile && this.message) {
        paddingRight += 6 + 44;
      }
      this.textAreaPaddingRight = paddingRight;
    }
    this.cdRef.markForCheck();
  }

  public updateMobileSendButton() {
    setTimeout(() => {
      this.updatePaddingRight();
    }, 0);
  }

  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (this.isFullScreen && (event.key === KeyboardKeys.ESCAPE || event.key === KeyboardKeys.ESC)) {
      this.exitFullScreen();
    } else if (event.code == KeyboardKeys.PAGEUP) {
      this.scrollPage(false);
    } else if (event.code == KeyboardKeys.PAGEDOWN) {
      this.scrollPage(true);
    } else if (this.isFullScreen && (event.key === KeyboardKeys.SPACEBAR || event.key === ' ')) {
      this.resetFullScreenImage();
    } else if (this.emojisDropdown?.overlayVisible) {
      this.detectChanges();
    }
  }

  @HostListener('window:resize', ['$event']) onResize(event) {
    this.detectChanges();
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (this.audioLoaded && this.textArea.nativeElement.offsetWidth > this.minWidthForWaveforms) {
        AudioHelpers.drawAudio(
          this.audio.nativeElement.src,
          this.audioContext,
          this.chatLoggingService,
          'chat',
          this.waveformsCanvas,
          this.progressCanvas,
          '#FFFFFF'
        );
      }
    }, 250);
  }

  private setLocale() {
    var locale = this.locale;
    this.dateTimeLocale = this.locale;
    const lang: string = locale.substr(0, 2);
    switch (lang.toLowerCase()) {
      case 'da':
        break;
      case 'de':
        this.locale = 'de-DE';
        break;
      case 'en':
        break;
      case 'es':
        if (locale !== 'es-CL' && locale !== 'es-MX') {
          this.locale = 'es-ES';
        }
        break;
      case 'fi':
        this.locale = 'fi';
        break;
      case 'fr':
        if (locale !== 'fr-CA') {
          this.locale = 'fr-FR';
        }
        break;
      case 'it':
        this.locale = 'it-IT';
        break;
      case 'nb':
        this.locale = 'nb-NO';
        break;
      case 'nl':
        break;
      case 'no':
        this.dateTimeLocale = 'nb-NO';
        this.locale = 'no';
        break;
      case 'pl':
        break;
      case 'pt':
        if (locale !== 'pt-BR') {
          this.locale = 'pt-PT';
        }
        break;
      case 'ru':
        this.locale = 'ru-RU';
        break;
      case 'sv':
        this.locale = 'sv-SE';
        break;
      case 'zh':
        if (locale !== 'zh-CN' && locale !== 'zh-TW') {
          this.locale = 'zh';
        }
        break;
      case 'cy':
        this.locale = 'cy-GB';
        break;
      default:
        this.dateTimeLocale = 'en';
        break;
    }
    this.translate.use(this.locale);
    this.chatLoggingService.info({
      logMessage: 'chat.component: use locale',
      logParameters: [this.locale]
    });
    moment.locale(this.dateTimeLocale);
  }

  private initialize() {
    if (this.postKeepAlive && (!this.chatCloudLinkService.isOffline() || !this.indexDbEnabled())) {
      this.subs.add(
        interval(10 * 60 * 1000)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(() => {
            try {
              const notification = {
                topic: 'Chat-Keep-Alive',
                method: 'POST',
                subject: `/conversations/${this.conversationId}`,
                accountId: this.chatAuthService.accountId,
                content: { conversationId: this.conversationId, participantId: this.chatAuthService.userId }
              };
              this.chatCloudLinkService.notify.publish({
                body: notification
              });
            } catch (ex) {
              this.chatLoggingService.error({
                logMessage: 'chat.component.initialize(): Error sending keep alive',
                logParameters: [ex]
              });
            }
          })
      );
    }
    this.subs.add(
      this.chatCloudLinkService.reloadConversation$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(s => {
        this.reloadConversation();
      })
    );
    this.subs.add(
      this.chatCloudLinkService.online$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(online => {
        if (online) {
          this.chatLoggingService.info({
            logMessage: 'chat.component.initialize(): Chat online'
          });
          if (!this.config || !this.config.disableTextArea) {
            this.textAreaDisabled = false;
          }
          if (this.chatCloudLinkService.offlineLogin) {
            this.chatLoggingService.info({
              logMessage: 'chat.component.initialize(): Chat component online from offline login',
              logParameters: [this.conversationId]
            });
            this.loading.set(true);
            this.cdRef.markForCheck();
            this.chatCloudLinkService.checkForNotify();
            this.chatAuthService.loginUser();
            this.waitForServices(true);
          } else if (this.conversationId) {
            this.chatLoggingService.info({
              logMessage: 'chat.component.initialize(): Chat component online again, reloading conversation',
              logParameters: [this.conversationId]
            });
            this.resyncChat();
            this.cdRef.markForCheck();
          }
        } else {
          this.chatLoggingService.info({
            logMessage: 'chat.component.initialize(): Chat offline'
          });
          this.textAreaDisabled = true;
          this.setOfflineSending();
          this.clearResync();
          this.cdRef.markForCheck();
        }
      })
    );
    this.subs.add(
      this.chatCloudLinkService.participantsChanged$
        .pipe(
          map(event => <Participant>event),
          filter(p => p.conversationId === this.conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resource => {
          try {
            this.onParticipantChanged(resource as ParticipantEx);
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe participantAdded',
              logParameters: [ex]
            });
          }
        })
    );
    this.subs.add(
      this.chatCloudLinkService.participantsDeleted$
        .pipe(
          filter(p => p.conversationId === this.conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(deleted => {
          try {
            this.onParticipantRemoved(deleted);
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe participant removed',
              logParameters: [ex]
            });
          }
        })
    );

    this.subs.add(
      this.chatCloudLinkService.messagesChanged$
        .pipe(
          map(event => <Message>event),
          filter(msg => msg.conversationId === this.conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resource => {
          try {
            if (this.config?.logMessageEvents) {
              this.chatLoggingService.info({
                logMessage: 'chat.component.initialize(): Received messagesChanged$',
                logParameters: [resource.conversationId, resource.messageId]
              });
            }
            this.onMessageReceived(resource as MessageEx);
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe messageReceived',
              logParameters: [ex]
            });
          }
        })
    );
    this.subs.add(
      this.chatCloudLinkService.messagesDeleted$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(resourceId => {
        try {
          this.onMessageRemoved(resourceId);
        } catch (ex) {
          // stifle exception to prevent unsubscribing
          this.chatLoggingService.error({
            logMessage: 'chat.component.initialize(): Error in subscribe messageRemoved',
            logParameters: [ex]
          });
        }
      })
    );
    this.subs.add(
      this.chatCloudLinkService.conversationsChanged$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
        conv => {
          try {
            this.chatLoggingService.info({
              logMessage: 'chat.component.initialize(): Received conversationsChanged$',
              logParameters: [conv.conversationId]
            });
            this.onConversationChanged(conv);
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe conversationsChanged',
              logParameters: [ex]
            });
          }
        },
        ex => {
          this.chatLoggingService.error({
            logMessage: 'chat.component.initialize(): Error in subscribe conversationsChanged',
            logParameters: [ex]
          });
        }
      )
    );
    this.subs.add(
      this.chatCloudLinkService.conversationsDeleted$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
        resourceId => {
          try {
            this.onConversationRemoved(resourceId);
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe conversationremoved',
              logParameters: [ex]
            });
          }
        },
        ex => {
          this.chatLoggingService.error({
            logMessage: 'chat.component.initialize(): Error in subscribe conversationremoved',
            logParameters: [ex]
          });
        }
      )
    );
    this.subs.add(
      this.chatConversationService.attachmentUploadAck$
        .pipe(
          filter(resource => resource.conversationId === this.conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resource => {
          if (this.pendingAttachmentIds.indexOf(resource.id) > -1) {
            this.pendingAttachmentIds.splice(this.pendingAttachmentIds.indexOf(resource.id), 1);
          }
        })
    );
    this.subs.add(
      this.chatConversationService.attachmentUploadSuccess$
        .pipe(
          filter(resource => resource.conversationId === this.conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resource => {
          try {
            if (this.lastAttachmentIds.indexOf(resource.id) > -1) {
              this.chatLoggingService.info({
                logMessage: 'chat.component.attachmentUploadSuccess()',
                logParameters: [resource.id, resource.name]
              });
              this.lastAttachmentIds.splice(this.lastAttachmentIds.indexOf(resource.id), 1);
              this.onUploadEnd.emit(resource.name);
              this.clearUploadFlag();
              this.cdRef.markForCheck();
              // TODO Don't think we need this, but leave it for now in case of future issues
              // if ((<any>this.currentConversation).generateSystemMessages) {
              //   let chatItem = this.chatItems.find(c => c.attachmentId === resource.id);
              //   if (chatItem) {
              //     chatItem.parseMessage();
              //   }
              // }
            }
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe messageReceived',
              logParameters: [ex]
            });
          }
        })
    );
    this.subs.add(
      this.chatConversationService.attachmentUploadFail$
        .pipe(
          filter(resource => resource.conversationId === this.conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resource => {
          try {
            if (this.lastAttachmentIds.indexOf(resource.id) > -1) {
              this.chatLoggingService.messageError({
                exceptionLogEvent: {
                  logMessage: 'chat.component.attachmentUploadFail()',
                  logParameters: [resource.id, resource.name]
                },
                errorMessage: this.translate.instant('cl_chat.failedToCreateAttachmentMessage')
              });
              this.lastAttachmentIds.splice(this.lastAttachmentIds.indexOf(resource.id), 1);
              this.onUploadError.emit(resource.name);
              this.clearUploadFlag();
              this.cdRef.markForCheck();
            }
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe messageReceived',
              logParameters: [ex]
            });
          }
        })
    );
    this.subs.add(
      this.chatCloudLinkService.attachmentsDeleted$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
        resourceId => {
          try {
            this.onAttachmentRemoved(resourceId);
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe attachmentsDeleted',
              logParameters: [ex]
            });
          }
        },
        ex => {
          this.chatLoggingService.error({
            logMessage: 'chat.component.initialize(): Error in subscribe attachmentsDeleted',
            logParameters: [ex]
          });
        }
      )
    );
    this.subs.add(
      this.chatAdminService.userChanged$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(_ => {
        try {
          this.chatItems.forEach(c => {
            c.markForCheck();
            if (c.isSystemMessage) {
              c.parseMessage();
            }
          });
          this.cdRef.markForCheck();
        } catch (ex) {
          // stifle exception to prevent unsubscribing
          this.chatLoggingService.error({
            logMessage: 'chat.component.initialize(): Error in subscribe userChanged',
            logParameters: [ex]
          });
        }
      })
    );

    this.subs.add(
      this.chatCloudLinkService.reactionsChanged$
        .pipe(
          filter(r => r.conversationId === this.conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resource => {
          try {
            if (!this.reactions[resource.messageId]) {
              this.reactions[resource.messageId] = {};
            }
            if (!this.reactions[resource.messageId][resource.reaction]) {
              this.reactions[resource.messageId][resource.reaction] = [];
            }
            //Make sure reaction doesn't already exist
            if (!this.reactions[resource.messageId][resource.reaction].some(r => r.userId === resource.createdBy)) {
              var nearBottom = this.scrollingService.isNearBottom();
              this.reactions[resource.messageId][resource.reaction].push({
                userId: resource.createdBy,
                reactionId: resource.reactionId
              });
              var chatItem = this.chatItems.find(c => c.item().messageId === resource.messageId);
              if (chatItem) {
                chatItem.detectChanges();
                if (nearBottom) {
                  setTimeout(() => {
                    this.scrollingService.scrollToBottom();
                  }, 0);
                }
              }
            }
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe reactions',
              logParameters: [ex]
            });
          }
        })
    );

    this.subs.add(
      this.chatCloudLinkService.reactionsDeleted$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(resource => {
        try {
          var deleted = false;
          for (let messageKey of Object.keys(this.reactions)) {
            if (deleted) {
              break;
            }
            for (let reactionKey of Object.keys(this.reactions[messageKey])) {
              if (deleted) {
                break;
              }
              for (let reaction of this.reactions[messageKey][reactionKey]) {
                if (reaction.reactionId === resource) {
                  this.reactions[messageKey][reactionKey] = this.reactions[messageKey][reactionKey].filter(
                    r => r.reactionId !== resource
                  );
                  if (this.reactions[messageKey][reactionKey].length == 0) {
                    delete this.reactions[messageKey][reactionKey];
                  }
                  if (Object.keys(this.reactions[messageKey]).length == 0) {
                    delete this.reactions[messageKey];
                  }
                  deleted = true;
                  var chatItem = this.chatItems.find(c => c.item().messageId === messageKey);
                  if (chatItem) {
                    chatItem.detectChanges();
                  }
                  break;
                }
              }
            }
          }
        } catch (ex) {
          // stifle exception to prevent unsubscribing
          this.chatLoggingService.error({
            logMessage: 'chat.component.initialize(): Error in subscribe delete reactions',
            logParameters: [ex]
          });
        }
      })
    );

    this.subs.add(
      this.chatCloudLinkService.translationsChanged$
        .pipe(
          filter(t => t.conversationId === this.conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resource => {
          try {
            this.zone.run(() => {
              //Let message event be received first or this logic won't work
              setTimeout(
                () => {
                  if (this.chatAuthService.user.languageCode === resource.languageCode) {
                    const i: number = findIndex(
                      this.messages,
                      (listMessage: Message) => listMessage.messageId === resource.messageId
                    );
                    if (i >= 0) {
                      if (!this.messages[i]._embedded) {
                        this.messages[i]._embedded = {};
                      }
                      this.messages[i]._embedded.translations = {
                        _embedded: {
                          items: [resource]
                        },
                        count: 1,
                        total: 1,
                        _links: {
                          self: ''
                        }
                      };
                      var chatItem = this.chatItems.find(c => c.item().messageId === resource.messageId);
                      if (chatItem && chatItem.item() && chatItem.item().body !== resource.body) {
                        chatItem.parseMessage();
                      }
                    }
                  }
                },
                this.config && this.config.translationDelay ? this.config.translationDelay : 500
              );
            });
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.initialize(): Error in subscribe translations',
              logParameters: [ex]
            });
          }
        })
    );

    this.subs.add(
      this.chatAuthService.triggerChangeDetection$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
        try {
          this.cdRef.markForCheck();
        } catch (ex) {
          // stifle exception to prevent unsubscribing
          this.chatLoggingService.error({
            logMessage: 'chat.component.initialize(): Error in subscribe triggerChangeDetection',
            logParameters: [ex]
          });
        }
      })
    );

    if (!this.chatCloudLinkService.isOffline() || !this.indexDbEnabled()) {
      this.subs.add(
        this.chatCloudLinkService.notify
          .asDiagnosticsObservable()
          .pipe(
            filter(event => event?.type === 'Connectivity' || event?.type === 'Websocket'),
            takeUntil(this.ngUnsubscribe)
          )
          .subscribe(async message => {
            if (message.isError) {
              this.chatLoggingService.error({
                logMessage: 'chat.component.initialize(): WebSocket error',
                logParameters: [message.data]
              });
            }
            if (message?.category === 'MqttSocket' && message?.data?.type === 'disconnected') {
              this.chatLoggingService.info({
                logMessage: 'chat.component.initialize(): WebSocket disconnected',
                logParameters: [message.data]
              });
              this.isDisconnected.set(true);
              this.cdRef.markForCheck();
            }
            // handle connection (or reconnection) to the event socket
            else if (message?.category === 'MqttSocket' && message?.data?.type === 'connected') {
              this.chatLoggingService.info({
                logMessage: 'chat.component.initialize(): WebSocket connected',
                logParameters: [message.data]
              });
              var refresh = true;
              //First connect, let's see if we actually need to reload to get missed messages.
              //Schedule randomized delay to mitigate Elastic Search
              const min = 7000;
              const max = 15000;
              let randomizedTimeout = Math.floor(Math.random() * (max - min + 1)) + min;
              setTimeout(async () => {
                if (!this.isDisconnected() && this.messages.length) {
                  const resMessages = await this.chatCloudLinkService.chat.getMessages({
                    conversationId: this.conversationId,
                    skip: 0,
                    top: 1
                  });
                  let messages = resMessages.body;
                  if (messages._embedded && messages._embedded.items && messages._embedded.items.length) {
                    if (messages._embedded.items[0].messageId === this.messages[this.messages.length - 1].messageId) {
                      refresh = false;
                    }
                  }
                } else if (!this.isDisconnected() && !this.messages.length) {
                  refresh = false;
                }
                this.isDisconnected.set(false);
                // We have just connected to the mqtt socket. Requery the conversation to make sure we didnt miss any items
                // while the socket was down (or getting connected in the first place)
                if (this.conversationId && refresh) {
                  this.resyncChat();
                }
                this.appRef.tick();
              }, randomizedTimeout);
            }
          })
      );
    }

    this.setupLocalTypingIndicators();
    this.setupRemoteTypingIndicators();
    this.setupScrolling();
  }

  private resyncChat() {
    if (this.resyncTimeout) {
      clearTimeout(this.resyncTimeout);
      this.chatLoggingService.info({
        logMessage: '[chat.component.resyncChat]: Resync timeout exists, waiting 30 seconds before resyncing'
      });
      this.resyncTimeout = setTimeout(() => {
        this.chatLoggingService.info({
          logMessage: '[chat.component.resyncChat]: Resync timeout expired, resyncing'
        });
        this.resubscribeChatSubscriptions();
        this.resyncTimeout = setTimeout(() => {
          this.chatLoggingService.info({
            logMessage: '[chat.component.resyncChat]: Resync timeout expired'
          });
          this.resyncTimeout = null;
        }, 30000);
      }, 30000);
    } else {
      this.chatLoggingService.info({
        logMessage: '[chat.component.resyncChat]: Reloading conversation'
      });
      this.resyncTimeout = setTimeout(() => {
        this.chatLoggingService.info({
          logMessage: '[chat.component.resyncChat]: Resync timeout expired'
        });
        this.resyncTimeout = null;
      }, 30000);
      this.resubscribeChatSubscriptions();
    }
  }

  private clearResync() {
    if (this.resyncTimeout) {
      clearTimeout(this.resyncTimeout);
      this.chatLoggingService.info({
        logMessage: '[chat.component.clearResync]: Clearing resync timeout'
      });
      this.resyncTimeout = setTimeout(() => {
        this.chatLoggingService.info({
          logMessage: '[chat.component.clearResync]: Resync timeout expired'
        });
        this.resyncTimeout = null;
      }, 30000);
    }
  }

  public quotedMessageChange(quotedMessage: QuoteMessageCite) {
    if (this.overQuotedMessageCharacterLimit(this.convertMentionsToText(false), true)) {
      this.showWarning(this.translate.instant('cl_chat.currentTextInputCharacterLengthExceeds'));
      return;
    }

    this.quotedMessage = quotedMessage;
    //Need to replace the carriage returns with empty and tabs with space or the count gets messed up and you go over 1024
    this.quotedMessage.body = this.quotedMessage.body.replace(/[\r\t]/g, ' ');

    this.quotedMessageDisplay = escape(this.quotedMessage.body);
    //Handle mentions
    var mentions = this.quotedMessageDisplay.match(SharedHelpers.mentionsRegexGlobal);
    if (mentions?.length) {
      // @ts-ignore
      mentions = MentionHelpers.getFilteredMentions(mentions);
      var replaceMentions = uniqBy(mentions, v => v);
      var error = false;
      replaceMentions.forEach(m => {
        this.quotedMessageDisplay = MentionHelpers.replaceWithMentionPlaceholder(this.quotedMessageDisplay, m);
      });
      setTimeout(() => {
        mentions.forEach(u => {
          try {
            if (!error) {
              var getElement = () => {
                return this.quoteDisplay;
              };
              let mentionReturn = MentionHelpers.replaceWithMentionElement(
                u,
                getElement,
                this.resolver,
                this.injector,
                this.appRef
              );
              if (mentionReturn.ref) {
                this.mentionRefs.push(mentionReturn.ref);
              }
            }
          } catch (ex) {
            this.chatLoggingService.error({
              logMessage: 'chat.component.quotedMessageChange(): Error handling mentions',
              logParameters: [ex]
            });
            error = true;
          }
        });
        if (error) {
          this.quotedMessageDisplay = this.quotedMessage.body;
        }
        this.focusTextArea();
        this.cdRef.markForCheck();
      }, 0);
    }
    this.handleEmojis(this.quotedMessageDisplay);
    this.focusTextArea();
    this.cdRef.markForCheck();
  }

  private handleEmojis(item) {
    if (this.isMobile || !this.config?.enableTwemoji) {
      return;
    }
    let emojis = unescape(item).match(SharedHelpers.emojiRejex);
    var error = false;
    var oldBody = item;
    if (emojis && emojis.length) {
      setTimeout(() => {
        emojis.forEach(emoji => {
          this.zone.run(() => {
            try {
              if (!error) {
                let codePoint = SharedHelpers.getEmojiUnicode(emoji);
                let emojiSvg = this.emojiPipe.transform(codePoint)['changingThisBreaksApplicationSecurity'] as string;
                //replace native emoji with emojiSvg in the message
                item = SharedHelpers.replaceAll(item, emoji, emojiSvg, false);
              }
            } catch (ex) {
              this.chatLoggingService.error({
                logMessage: 'chat.component.handleEmojis(): Error handling emojis',
                logParameters: [ex]
              });
              error = true;
            }
          });
        });
        if (error) {
          item = oldBody;
        }
        this.quotedMessageDisplay = item;
        this.cdRef.markForCheck();
      }, 0);
    }
  }

  // pagehide rather than unload - Chrome blocks unload listeners by default (Permissions Policy).
  // Unlike unload, the page can come back from the back/forward cache with this component still
  // alive, so keep inputSaveRestored as it was - saveTextInput() clears it for a conversation switch.
  @HostListener('window:pagehide')
  onPageHide() {
    if (this.conversationId) {
      const inputSaveRestored = this.inputSaveRestored;
      this.saveTextInput(this.conversationId);
      this.inputSaveRestored = inputSaveRestored;
    }
  }

  ngOnDestroy() {
    this.chatLoggingService.info({
      logMessage: 'chat.component.ngOnDestroy()',
      logParameters: [this.conversationId]
    });
    if (this.conversationId) {
      this.saveTextInput(this.conversationId);
    }
    this.cancelOneDriveUploads();

    if (this.dropdownVisible['emoji']) {
      this.emojisDropdown?.hide();
    } else if (this.emojiContainerEl?.isConnected) {
      // cl-26767 PrimeNG's ngOnDestroy ran first (children before parents), cleared its own
      // container reference, but left the DOM node in <body> — remove it directly.
      this.emojiContainerEl.remove();
    }

    this.emojiContainerEl = null;
    this.isActive = false;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.subs.unsubscribe();
    this.messageSubscriptionSub.unsubscribe();
    this.errorSub.unsubscribe();
    if (this.scrollingSub) {
      this.scrollingSub.unsubscribe();
    }
    if (this.scrollingSubUp) {
      this.scrollingSubUp.unsubscribe();
    }
    if (this.scrollingSubDown) {
      this.scrollingSubDown.unsubscribe();
    }
    if (this.resyncTimeout) {
      clearTimeout(this.resyncTimeout);
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.joinFailedSub.unsubscribe();
    this.updateLastReadMessageDateSub.unsubscribe();
    this.updateHasUnreadMessageSub.unsubscribe();
    this.userLoadedSub.unsubscribe();
    if (this.textArea) {
      this.textArea.nativeElement.removeEventListener('input', this.inputFunction);
    }
    ResizeSensor.detach(this.maincontent.nativeElement, this.resizeSensorFunc);
    ResizeSensor.detach(this.dialogBoxes.nativeElement, this.dialogBoxesResizeFunc);
    if (this.mentionRefs.length) {
      this.mentionRefs.forEach(r => {
        this.appRef.detachView(r.hostView);
        r.destroy();
      });
    }
    //Manually destroy, seems to help prevent memory leaks
    if (this.chatItems.length) {
      this.chatItems.forEach(c => {
        if ((<any>c).cdRef) {
          (<any>c).cdRef.destroy();
        }
      });
    }

    document.removeEventListener('keydown', this.keydownEventListener);

    // Can't unsubscribe on destroy. When quickly creating/destroying the component, turns out
    // the destroy from the last component can run AFTER the init of the new component
    // Would need to refactor unsubscribe design given this scenario, so we'll just unsubscibe on ngOnChanges

    // this.chatAuthService.auth.getToken().then(token => {
    //     if (token) {
    //         //this.chatCloudLinkService.unsubscribeReaction();
    //         this.chatCloudLinkService.unsubscribeRemoteTypingIndicators()
    //         this.chatCloudLinkService.userSubscriptionUserIds.forEach((id) => {
    //             this.chatCloudLinkService.unsubscribeUser(id).then(() => {
    //                 var userSubIndex = this.chatCloudLinkService.userSubscriptionUserIds.indexOf(id);
    //                 this.chatCloudLinkService.userSubscriptionUserIds.splice(userSubIndex, 1);
    //             }).catch((ex) => {
    //                 this.setError(ex);
    //                 var userSubIndex = this.chatCloudLinkService.userSubscriptionUserIds.indexOf(id);
    //                 this.chatCloudLinkService.userSubscriptionUserIds.splice(userSubIndex, 1);
    //             });
    //         });
    //     }
    // });

    if (this.reloadUser) {
      this.chatCloudLinkService.reset();
      this.chatAuthService.reset();
    }
  }

  public async startVideoPreview(): Promise<void> {
    try {
      await this.recordService.startVideoPreview(
        {
          video: {
            width: 480,
            height: 270
          },
          audio: {
            echoCancellation: true
          }
        },
        this.videoPreview.nativeElement
      );
    } catch (ex) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat.component.startVideoPreview(): Error',
          logParameters: [ex]
        },
        errorMessage: this.translate.instant(this.getVideoServiceErrorKey())
      });
      this.detectChanges();
    }
  }

  public stopVideoPreview(): void {
    this.recordService.stopVideoPreview(this.videoPreview.nativeElement);
  }

  public startVideoRecording(): void {
    try {
      this.recordService.startVideoRecording(this.videoPreview.nativeElement);
      this.isVideoRecording = true;
    } catch (ex) {
      this.isVideoRecording = false;
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat.component.startVideoRecording(): Error',
          logParameters: [ex]
        },
        errorMessage: this.translate.instant(this.getVideoServiceErrorKey())
      });
    }
    this.detectChanges();
  }

  private getVideoServiceErrorKey(): string {
    if (this.recordService.isPermissionDenied) {
      return 'cl_chat.videoPermissionDenied';
    } else if (this.recordService.isDeviceNotFound) {
      return 'cl_chat.videoDeviceNotFound';
    }
    return 'cl_chat.videoServiceNotAvailable';
  }

  public async stopVideoRecording(): Promise<void> {
    this.isVideoRecording = false;
    this.currentVideoRecording = await this.recordService.stopVideoRecording();
    this.detectChanges();
  }

  public sendVideoRecording(): void {
    this.uploadVideoRecording();
    this.currentVideoRecording = undefined;
    this.detectChanges();
  }

  public isContinuationMessage(index: number, message: Message) {
    let retVal: boolean = false;

    if (index > 0) {
      let previous = this.messages[index - 1];
      let cur: Date = new Date(message.createdOn);
      let prev: Date = new Date(previous.createdOn);
      let diff: number = cur.getTime() - prev.getTime();
      let minutes: number = Math.floor(diff / 1000 / 60);
      minutes = Math.abs(Math.round(minutes));
      // Handle system messages sent by the user
      if (
        previous.createdBy === message.createdBy &&
        ((previous?.contentType === ContentTypes.SYSTEMMESSAGE &&
          message?.contentType !== ContentTypes.SYSTEMMESSAGE) ||
          (previous?.contentType !== ContentTypes.SYSTEMMESSAGE && message?.contentType === ContentTypes.SYSTEMMESSAGE))
      ) {
        retVal = false;
      } else if (previous.createdBy === message.createdBy && minutes <= 5) {
        retVal = true;
      }
    }

    return retVal;
  }

  private keydownEventListener = (e): void => {
    const isTabPressed = e.key === 'Tab' || e.code === 'Tab';

    if (!isTabPressed) {
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === this.imageFullScreenDownload?.nativeElement) {
        this.imageFullScreenZoomIn?.nativeElement.focus();
        e.preventDefault();
      } else if (document.activeElement === this.fullScreenClose?.nativeElement && !this.fullScreenId) {
        this.imageFullScreenZoomIn?.nativeElement.focus();
        e.preventDefault();
      } else if (document.activeElement === this.pdfFullScreenDownload?.nativeElement) {
        this.pdfFullScreenExit?.nativeElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === this.imageFullScreenZoomIn?.nativeElement && this.fullScreenId) {
        this.imageFullScreenDownload?.nativeElement.focus();
        e.preventDefault();
      } else if (document.activeElement === this.imageFullScreenZoomIn?.nativeElement && !this.fullScreenId) {
        this.fullScreenClose?.nativeElement.focus();
        e.preventDefault();
      } else if (document.activeElement === this.pdfFullScreenExit?.nativeElement && this.fullScreenId) {
        this.pdfFullScreenDownload?.nativeElement.focus();
        e.preventDefault();
      } else if (document.activeElement === this.pdfFullScreenExit?.nativeElement && !this.fullScreenId) {
        this.pdfFullScreenExit?.nativeElement.focus();
        e.preventDefault();
      }
    }
  };

  public headerButtonClick(id: string) {
    this.onHeaderButtonClick.emit(id);
    if (this.error()) {
      this.clearError();
    }
  }

  public headerSearch($event) {
    if ($event.trim() == '') {
      this.goToLatest();
    } else {
      this.searchChat($event.trim());
    }
  }

  public async searchChat(search) {
    this.activateSelectMessagesMode(false);
    this.onConversationLoadStart.emit({
      conversationId: this.conversationId,
      loadType: ConversationLoadType[ConversationLoadType.Search].toString()
    });
    this.resetSearch();
    this.isSearch = true;
    await this.fetchMessages(FetchType.Normal, {
      search: search,
      loadAttempt: this.conversationLoadAttempt
    });
    this.cdRef.markForCheck();
  }

  private onConversationChanged(conversation: Conversation): void {
    try {
      if (
        this.conversationId === conversation.conversationId &&
        conversation.hasOwnProperty('generateSystemMessages')
      ) {
        (<any>this.currentConversation).generateSystemMessages = (<any>conversation).generateSystemMessages;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.onConversationChanged(): Error',
        logParameters: [ex]
      });
    }
  }

  // A "conversationsDeleted$"/participant-removed event for a conversationId can be genuinely
  // stale by the time it's handled: it can arrive after the same conversationId has already
  // been legitimately reopened (e.g. re-creating a direct conversation with the same
  // participant(s) reuses the id rather than getting a new one), and even a same-moment
  // re-verification against the server isn't fully reliable, since a truthful "gone" answer can
  // itself resolve after a newer load already superseded it (the query was accurate when sent,
  // not when it resolves) - or the server's own read path can briefly lag just behind a
  // near-simultaneous delete-then-recreate of the same id, answering "not found" a moment too
  // early. Confirmed live: readOnly stuck true on a fully (re)loaded, actively-participating
  // conversation, conversationLoadAttempt unchanged across the whole handling window - i.e. not
  // a newer-load race, but the verification query itself answering stale. One short retry
  // absorbs that; the loadAttempt check still guards against a newer load happening around it.
  private async confirmConversationGone(conversationId: string): Promise<boolean> {
    const loadAttemptAtStart = this.conversationLoadAttempt;
    try {
      // Confirmed live: right after a delete-then-recreate of the same conversationId, a GET
      // for it can genuinely 404 from the server for longer than a token retry window, even
      // though the create already succeeded and the component has already loaded content for
      // this exact id (so it's not a client-side ordering bug - the server's own read path is
      // briefly behind its own write). loadConversation() below uses this same retry-on-404
      // strategy for its own getConversation() call for that reason; reuse it here so this
      // verification is at least as patient as the load it's trying not to undo.
      const current = await defer(() => this.chatConversationService.getConversation(conversationId))
        .pipe(
          retryWhen(
            SharedHelpers.genericRetryStrategy(
              '[chat.component.confirmConversationGone()] - get conversation ' + conversationId,
              this.chatLoggingService,
              3,
              1000,
              true
            )
          )
        )
        .toPromise();
      if (current?.body) {
        return false;
      }
    } catch {
      // All retries exhausted with no success - conclude it's actually gone, subject to the
      // loadAttempt/conversationId checks below.
    }
    return this.conversationId === conversationId && this.conversationLoadAttempt === loadAttemptAtStart;
  }

  private async onConversationRemoved(conversationId: string): Promise<void> {
    try {
      if (this.conversationId === conversationId) {
        if (!(await this.confirmConversationGone(conversationId))) {
          return;
        }
        this.chatLoggingService.info({
          logMessage: 'chat.component.onConversationRemoved()',
          logParameters: [conversationId]
        });
        this.setReadOnly('onConversationRemoved');
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.onConversationRemoved(): Error',
        logParameters: [ex]
      });
    }
  }

  private onAttachmentRemoved(attachmentId: string): void {
    try {
      this.chatLoggingService.info({
        logMessage: 'chat.component.onAttachmentRemoved()',
        logParameters: [attachmentId]
      });
      let chatItem = this.chatItems.find(c => c.attachmentId() === attachmentId);
      if (chatItem && MessageUtils.isAttachmentSystemMessage(chatItem.item())) {
        this.chatCloudLinkService.sendMessagesDeleted(chatItem.item().messageId, true);
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.onAttachmentRemoved(): Error',
        logParameters: [ex]
      });
    }
  }

  public async startRecording() {
    this.audioObj.audioStart = '00:00';
    this.stopRecording(true);
    this.chatLoggingService.info({
      logMessage: 'chat.component.startRecording()'
    });
    try {
      this.onRecordStart.emit();
      if (this.recordStartOverride) {
        this.recordStartOverride();
      } else {
        await this.recordService.startRecording({ audio: true, video: false });
      }
      this.setIsRecording(true);
      this.cdRef.markForCheck();
      var duration = moment.duration(0);
      if (this.recordingInterval) {
        clearInterval(this.recordingInterval);
      }
      this.recordingInterval = setInterval(() => {
        duration.add(1000);
        if (duration.asMilliseconds() > this.maxRecordingTime) {
          clearInterval(this.recordingInterval);
          this.showWarning(this.translate.instant('cl_chat.maxRecordingLimitReached'));
          this.stopRecording();
        } else {
          if (this.chatCloudLinkService.browser.name.toLowerCase() === 'safari') {
            this.recordingTime = moment.utc(duration.as('milliseconds')).subtract(1000).format('mm:ss');
          } else {
            this.recordingTime = moment.utc(duration.as('milliseconds')).format('mm:ss');
          }
          this.cdRef.markForCheck();
        }
      }, 1000);
    } catch (ex) {
      this.onRecordEnd.emit();
      this.setIsRecording(false);
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: "chat.component.startRecording(): Can't start recording",
          logParameters: [ex]
        },
        errorMessage: this.translate.instant(this.getAudioServiceErrorKey())
      });
    }
    this.cdRef.markForCheck();
  }

  private getAudioServiceErrorKey(): string {
    if (this.recordService.isPermissionDenied) {
      return 'cl_chat.audioPermissionDenied';
    } else if (this.recordService.isDeviceNotFound) {
      return 'cl_chat.audioDeviceNotFound';
    } else if (this.recordService.isDeviceBusy) {
      return 'cl_chat.audioDeviceBusy';
    }
    return 'cl_chat.audioServiceNotAvailable';
  }

  private setIsRecording(isRecording) {
    this.isRecording = isRecording;
  }

  public async stopRecording(clearRecording = false) {
    clearInterval(this.recordingInterval);
    if (!this.isRecording) return;

    this.setIsRecording(false);
    this.chatLoggingService.info({
      logMessage: 'chat.component.stopRecording()'
    });

    let analyticObj: AnalyticObject = {
      eventName: AnalyticEvents.analytic_events.common.event_name.chat.finish_voice_recording_message,
      attributes: ChatAnalyticAttributes
    };
    this.onAnalyticEvent.emit(analyticObj);
    this.onRecordEnd.emit();
    if (this.recordEndOverride) {
      await this.recordEndOverride(this.updateCurrentRecording);
    } else {
      var blob = await this.recordService.stopRecording();
      this.updateCurrentRecording(blob);
    }
    if (clearRecording) {
      this.removeRecording();
    } else if (!this.isMobile) {
      this.playRecordingBtn?.nativeElement.focus();
    }
  }

  private async preloadAndDraw() {
    //preload audio and draw waveforms before playing
    if (this.currentRecording && !this.audioLoaded) {
      // Set up audio context
      var AudioContext =
        (<any>window).AudioContext || // Default
        (<any>window).webkitAudioContext || // Safari and old versions of Chrome
        false;
      this.audioContext = new AudioContext();
      setTimeout(async () => {
        let audio: HTMLAudioElement = this.audio?.nativeElement;
        audio.src = await this.preloadRecording();
        audio.load();
        this.audioLoaded = true;
        if (this.textArea.nativeElement.offsetWidth > this.minWidthForWaveforms) {
          AudioHelpers.drawAudio(
            audio.src,
            this.audioContext,
            this.chatLoggingService,
            'chat',
            this.waveformsCanvas,
            this.progressCanvas,
            '#FFFFFF'
          );
        }
      }, 0);
      this.cdRef.markForCheck();
    }
  }

  private updateCurrentRecording = async (data: Blob) => {
    this.currentRecording = data;
    // detectChanges required: audio ViewChild must be in DOM before preloadAndDraw's setTimeout fires
    this.cdRef.detectChanges();
    this.preloadAndDraw();
  };

  public sendRecording(): void {
    this.chatLoggingService.info({
      logMessage: 'chat.component.sendRecording()'
    });
    this.uploadRecording(this.currentRecording);
    this.removeRecording();
  }

  public removeRecording(): void {
    if (this.currentRecording) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.removeRecording()'
      });
      this.recordingTime = '00:00';
      this.currentRecording = null;
      this.audioLoaded = false;
      this.cdRef.markForCheck();
    }
  }

  public removeVideoRecording(): void {
    if (!!this.currentVideoRecording) {
      this.currentVideoRecording = undefined;
    }
    this.detectChanges();
  }

  public dropdownChange($event, key) {
    if (key === 'video_recording' && $event === false) {
      this.stopVideoPreview();
    } else if (key === 'emoji' && $event === true) {
      this.emojiContainerEl = this.emojisDropdown?.container ?? null;
    } else if (key === 'emoji' && $event === false && this.emojiPicker) {
      this.emojiPicker.clearSearch();
    }
    this.dropdownVisible[key] = $event;
    this.cdRef.markForCheck();
  }

  private uploadRecording = (data: Blob) => {
    this.cdRef.markForCheck();
    const fr = new FileReader();
    fr.onloadend = e => {
      const now = moment();
      const name = `voicenote-${now.format('YYYYMMDDHHmmss')}.wav`;
      var attachmentId = uuid() + moment().format('YYYYMMDDHHmmss') + '.wav';
      let data = (e.target as any).result;
      this.setUploadFlag();
      let attachmentGroup = this.createAttachmentGroup();
      this.uploadAttachment(
        {
          name: name,
          data: data,
          id: attachmentId,
          type: 'application/octet-stream'
        },
        attachmentGroup
      );
      this.removeRecording();
    };

    fr.readAsArrayBuffer(data);
  };

  getAudioButtonText() {
    if (this.isMobile) {
      return '';
    } else {
      return this.audioObj.audioPlaying
        ? this.translate.instant('cl_chat.stop')
        : this.translate.instant('cl_chat.playAudio');
    }
  }

  public async playRecording() {
    try {
      let audio: HTMLAudioElement = this.audio?.nativeElement;
      if (this.chatCloudLinkService.browser.os.toLowerCase() === 'ios') {
        audio.play();
        if (this.textArea?.nativeElement.offsetWidth > this.minWidthForWaveforms) {
          AudioHelpers.drawAudio(
            audio.src,
            this.audioContext,
            this.chatLoggingService,
            'chat',
            this.waveformsCanvas,
            this.progressCanvas,
            '#FFFFFF'
          );
        }
      } else {
        audio.pause();
      }
      if (this.audioObj.audioPlaying) {
        audio.pause();
        setTimeout(() => {
          this.audioObj.audioPlaying = false;
          this.cdRef.markForCheck();
        }, 100);
      } else {
        audio.play().then(() => {
          this.audioObj.audioPlaying = true;
          this.cdRef.markForCheck();
        });
        this.recordService.currentlyPlaying$.next('recording');
        if (this.textArea.nativeElement.offsetWidth > this.minWidthForWaveforms) {
          AudioHelpers.drawAudio(
            audio.src,
            this.audioContext,
            this.chatLoggingService,
            'chat',
            this.waveformsCanvas,
            this.progressCanvas,
            '#FFFFFF'
          );
        }
        this.chatLoggingService.info({
          logMessage: 'chat.component.playRecording()'
        });
      }
      this.cdRef.markForCheck();
    } catch (ex) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat.component.playRecording(): Error'
        },
        errorMessage: this.translate.instant('cl_chat.failedToPlayAudio')
      });
    }
  }

  private async preloadRecording() {
    return new Promise<string>(async (resolve, reject) => {
      try {
        this.chatLoggingService.info({
          logMessage: 'chat.component.preloadRecording()'
        });
        let reader = new FileReader();
        reader.onloadend = e => {
          let binary = AudioHelpers.convertDataURIToBinary(reader.result);
          let audioBlob = new Blob([binary], { type: 'audio/wav' });

          if (this.chatCloudLinkService.browser.name === 'ios-webview') {
            var typeRegex = new RegExp('data:application/.*?;');
            var match = (<string>reader.result).match(typeRegex);
            var audioData = <string>reader.result;
            if (match) {
              audioData = (<string>reader.result).replace(match[0], 'data:audio/wav;');
            } else if (audioData.indexOf('data:;base64') === 0) {
              audioData = audioData.replace('data:;base64', 'data:audio/wav;base64');
            }
            resolve(audioData);
          } else {
            this.blobUrl = URL.createObjectURL(audioBlob);
            resolve(this.blobUrl);
          }
        };
        reader.readAsDataURL(this.currentRecording);
      } catch (ex) {
        reject(ex);
      }
    });
  }

  public audioUpdateTime() {
    AudioHelpers.audioUpdateTime(this.audio, this.audioObj);
    this.cdRef.markForCheck();
  }

  private uploadVideoRecording(): void {
    const fileReader = new FileReader();
    fileReader.onloadend = ev => {
      const now = moment();

      const name = `videonote-${now.format('YYYYMMDDHHmmss')}.webm`;
      const attachmentId = `${uuid()}${now.format('YYYYMMDDHHmmss')}.webm`;
      const data = (ev.target as any).result;

      this.setUploadFlag();
      const attachmentGroup = this.createAttachmentGroup();
      this.uploadAttachment(
        {
          name: name,
          data: data,
          id: attachmentId,
          type: 'video/webm; codecs="opus,vp8"'
        },
        attachmentGroup
      );
    };

    fileReader.readAsArrayBuffer(this.currentVideoRecording);
  }

  public dragOver(e: any): void {
    if (!this.config || !this.config.disableDragDrop) {
      if (e.dataTransfer && e.dataTransfer.types && e.dataTransfer.types.find(x => x.toLowerCase() === 'files')) {
        e.preventDefault();
        e.stopPropagation();
        if (!this.dragging) {
          this.draggingError = false;
          this.dragging = true;
          this.cdRef.markForCheck();
        }
      }
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  public dragLeave(e: any): void {
    if (!this.config || !this.config.disableDragDrop) {
      e.preventDefault();
      e.stopPropagation();
      if (this.dragging) {
        this.dragging = false;
        this.cdRef.markForCheck();
      }
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  public drop(e: any): void {
    if (!this.config || !this.config.disableDragDrop) {
      e.preventDefault();
      e.stopPropagation();
      let files = e.dataTransfer.files;
      if (files.length > 0) {
        for (var i = 0; i < files.length; i++) {
          this.uploadFileToClient(files[i]);
        }
        this.focusTextArea();
      }

      this.dragging = false;
      this.cdRef.markForCheck();
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  //Need this for mobile to determine the key that was pressed down
  public textInput(event) {
    MentionHelpers.textInputEvent(event, this.textAreaProps);
  }

  public enterMention(mention: User) {
    let loadedMentionObservables = MentionHelpers.enterMention(
      mention,
      this.chatLoggingService,
      'chat.component',
      this.mentionsDropdown,
      this.textAreaProps,
      this.textArea,
      this.mentionRefs,
      this.resolver,
      this.injector,
      this.appRef,
      this.cdRef,
      this.setMessage.bind(this)
    );
    if (loadedMentionObservables.length) {
      MentionHelpers.waitForMentionsLoaded(
        loadedMentionObservables,
        this.ngUnsubscribe,
        this.cdRef,
        this.textArea,
        this.textAreaProps,
        this.chatLoggingService,
        this.chatCloudLinkService,
        'chat.component',
        () => this.setCaret(false),
        this.setMessage.bind(this),
        false
      );
    }
    if (this.isMobile) {
      this.mobileTextFocus();
    }
  }

  private setMessage(message) {
    this.message = message;
  }

  private setupLocalTypingIndicators() {
    this.subs.add(
      this.keyDownSubject
        .pipe(
          tap(() => {
            MentionHelpers.removeTooltip();
          }),
          filter(
            event =>
              (event.code == KeyboardKeys.B || event.code == KeyboardKeys.I || event.code == KeyboardKeys.U) &&
              event.ctrlKey
          ),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(event => {
          //disable bold/italic/underline
          event.preventDefault();
        })
    );

    this.subs.add(
      this.keyPressSubject
        .pipe(
          filter(
            event =>
              this.overCharacterLength(this.convertMentionsToText(false).length + event.key.length) ||
              this.overQuotedMessageCharacterLimit(this.convertMentionsToText(false) + event.key)
          ),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(event => {
          //disable over character limit
          event.preventDefault();
          this.showWarning(this.translate.instant('cl_chat.maximumTextInputCharacterLimitReached'));
        })
    );

    this.subs.add(
      this.keyDownSubject
        .pipe(
          filter(
            event =>
              (event.code == KeyboardKeys.ENTER || event.code == KeyboardKeys.NUMPADENTER || event.keyCode == ENTER) &&
              !event.shiftKey
          ),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(event => {
          //we're sending
          if (this.isMobile) {
            this.textAreaProps.commandPending = false;
            this.textAreaProps.mentionPending = false;
            this.closeTextInputDropdowns();
            this.cdRef.markForCheck();
          }
          event.preventDefault();
        })
    );

    this.subs.add(
      this.keyDownSubject.pipe(takeUntil(this.ngUnsubscribe)).subscribe(event => {
        this.lastMessageInput = this.textArea.nativeElement.innerHTML;
      })
    );

    this.subs.add(
      this.keyUpSubject.pipe(takeUntil(this.ngUnsubscribe)).subscribe(event => {
        try {
          var key = event.key;
          if (event.keyCode === 229) {
            key = this.textAreaProps.lastInputValue;
          }
          this.getCaretPosition(
            this.lastMessageInput === this.textArea.nativeElement.innerHTML &&
              (key === KeyboardKeys.BACKSPACE ||
                event.code === KeyboardKeys.DELETE ||
                event.code === KeyboardKeys.BACKSPACE)
          );
          //Sometimes the mention doesn't properly delete, need to manually handle it
          if (MentionHelpers.isBackspaceMention(key, event.code, this.textAreaProps)) {
            MentionHelpers.deleteBackspaceMention(
              this.chatCloudLinkService,
              this.chatLoggingService,
              'chat.component',
              this.textAreaProps,
              this.textArea
            );
          } else if (
            (key === KeyboardKeys.BACKSPACE || event.code === KeyboardKeys.DELETE) &&
            this.textAreaProps.lastNode &&
            this.textAreaProps.lastNode.className === 'text-area' &&
            this.textAreaProps.lastNode.innerHTML === '<br>'
          ) {
            this.textAreaProps.lastNode.innerHTML = '';
            this.message = '';
            MentionHelpers.removeTooltip();
            this.closeTextInputDropdowns();
          } else {
            MentionHelpers.checkForEmojis(
              event,
              this.emojiDictionary,
              this.textAreaProps,
              this.textArea,
              this.chatCloudLinkService,
              this.chatLoggingService,
              'chat.component',
              this.setMessage.bind(this)
            );
            // aiMode has no CloudLink participants or commands to look up
            if (key && !this.aiMode) {
              if (!this.config || !this.config.hideMentions) {
                this.checkForMentions(key);
              }
              if ((!this.config || !this.config.hideCommands) && !this.chatCloudLinkService.isOffline()) {
                this.checkForCommands(key);
              }
            }
          }
          this.updatePaddingRight();
          this.cdRef.markForCheck();
        } catch (ex) {
          this.chatLoggingService.error({
            logMessage: 'chat.component.keyUpSubject(): Error',
            logParameters: [ex]
          });
        }
      })
    );

    // typing flags locally
    this.subs.add(
      this.keyUpSubject
        .pipe(
          tap(() => this.startTyping()),
          debounceTime(1500),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(event => {
          try {
            this.endTyping(event);
          } catch (ex) {
            // Subscriber automatically calls unsubscribe on an exception
            // so prevent the exception
            this.chatLoggingService.error({
              logMessage: 'chat.component.setupLocalTypingIndicators(): Error on key up subject',
              logParameters: [ex]
            });
          }
        })
    );

    // enter
    this.subs.add(
      this.keyUpSubject
        .pipe(
          filter(
            event =>
              (event.code == KeyboardKeys.ENTER || event.code == KeyboardKeys.NUMPADENTER || event.keyCode == ENTER) &&
              !event.shiftKey
          ),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(event => {
          try {
            if (this.preventSend) {
              this.preventSend = false;
            } else {
              this.sendTypedMessage();
            }
          } catch (ex) {
            // Subscriber automatically calls unsubscribe on an exception
            // so prevent the exception
            this.chatLoggingService.error({
              logMessage: 'chat.component.setupLocalTypingIndicators(): Error on key up subject send',
              logParameters: [ex]
            });
          }
        })
    );

    // enter key up
    this.subs.add(
      this.keyUpSubject
        .pipe(
          filter(
            event =>
              event.code == KeyboardKeys.ENTER || event.code == KeyboardKeys.NUMPADENTER || event.keyCode == ENTER
          ),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(event => {
          this.enterKeyDown = false;
        })
    );

    // enter key down
    this.subs.add(
      this.keyDownSubject
        .pipe(
          filter(
            event =>
              event.code == KeyboardKeys.ENTER || event.code == KeyboardKeys.NUMPADENTER || event.keyCode == ENTER
          ),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(event => {
          this.enterKeyDown = true;
        })
    );

    //shift key up
    this.subs.add(
      this.keyUpSubject
        .pipe(
          filter(event => (event.shiftKey || event.key === 'Shift') && this.enterKeyDown),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(event => {
          this.preventSend = true;
        })
    );

    // audit sending typing flag
    this.subs.add(
      this.keyUpSubject
        .pipe(
          filter(
            event =>
              event.code != KeyboardKeys.ENTER &&
              event.keyCode != ENTER &&
              event.code != KeyboardKeys.NUMPADENTER &&
              event.code != KeyboardKeys.PAGEUP &&
              event.code != KeyboardKeys.PAGEDOWN
          ),
          auditTime(5000),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(event => {
          try {
            // only send if still typing
            if (this.typing) {
              this.sendTypingIndicator();
              this.cdRef.markForCheck();
            }
          } catch (ex) {
            // Subscriber automatically calls unsubscribe on an exception
            // so prevent the exception
            this.chatLoggingService.error({
              logMessage: 'chat.component.setupLocalTypingIndicators(): Error on key up subject typing',
              logParameters: [ex]
            });
          }
        })
    );
  }

  private setupRemoteTypingIndicators() {
    this.subs.add(
      this.chatCloudLinkService.typingChanged$
        .pipe(
          filter(e => {
            return e.userId !== this.chatAuthService.userId && e.conversationId === this.conversationId;
          }),
          map(e => {
            // string parse conversation and user from subject
            return { conversationId: e.conversationId, userId: e.userId };
          }),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(e => {
          if (this.remoteTypingTimeouts[e.userId]) {
            clearTimeout(this.remoteTypingTimeouts[e.userId]);
            delete this.remoteTypingTimeouts[e.userId];
          }
          this.remoteTypingTimeouts[e.userId] = setTimeout(() => {
            delete this.remoteTypingTimeouts[e.userId];
            this.clearRemoteTyping(e.userId);
          }, 4000);
          if (this.remoteTyping.indexOf(e.userId) === -1) {
            this.remoteTyping.push(e.userId);
            this.cdRef.markForCheck();
          }
          if (this.scrollingService.isNearBottom()) {
            this.scrollingService.scrollToBottom();
          }
        })
    );
  }

  private overCharacterLength(length): boolean {
    if (length > this.textAreaProps.maxInputCharacters) {
      return true;
    }
    return false;
  }

  private overQuotedMessageCharacterLimit(input: string, forceCheck = false): boolean {
    let inputLength = SharedHelpers.getQuotedBodyLength(input);
    if ((this.quotedMessage || forceCheck) && inputLength > ChatMessageLimits.MaxQuotedMessageReply) {
      return true;
    }
    return false;
  }

  private clearRemoteTyping(userId) {
    var remoteTypingIndex = this.remoteTyping.indexOf(userId);
    if (remoteTypingIndex > -1) {
      this.remoteTyping.splice(remoteTypingIndex, 1);
    }
    this.cdRef.markForCheck();
  }

  private announceNewMessage(text: string): void {
    if (this.newMessageAnnouncement() === text) {
      // Setting a live region to text it already contains doesn't change the DOM, so most
      // screen readers won't re-announce it (e.g. two messages in a row from the same sender).
      // Clear first, then set on the next tick, so there's an actual mutation to react to.
      this.newMessageAnnouncement.set('');
      setTimeout(() => this.newMessageAnnouncement.set(text));
    } else {
      this.newMessageAnnouncement.set(text);
    }
  }

  private shouldAnnounceNewMessage(message: MessageEx): boolean {
    return message.createdBy !== this.chatAuthService.userId && !MessageUtils.isSystemMessage(message);
  }

  public getRemoteText(): string {
    if (this.remoteTyping.length === 1) {
      // aiMode's "typing" indicator is really the assistant generating a reply, not a human
      // typing - only ever one entry here (the assistant), never the 2/3/4+ multi-user cases.
      const key = this.aiMode ? 'cl_chat.isThinkingBold' : 'cl_chat.isTypingBold';
      return this.translate.instant(key, {
        name: this.getDisplayName(this.remoteTyping[0])
      });
    } else if (this.remoteTyping.length === 2) {
      return this.translate.instant('cl_chat.twoAreTyping', {
        nameOne: this.getDisplayName(this.remoteTyping[0]),
        nameTwo: this.getDisplayName(this.remoteTyping[1])
      });
    } else if (this.remoteTyping.length === 3) {
      return this.translate.instant('cl_chat.threeAreTyping', {
        nameOne: this.getDisplayName(this.remoteTyping[0]),
        nameTwo: this.getDisplayName(this.remoteTyping[1]),
        nameThree: this.getDisplayName(this.remoteTyping[2])
      });
    } else if (this.remoteTyping.length > 3) {
      return this.translate.instant('cl_chat.fourPlusAreTyping', {
        nameOne: this.getDisplayName(this.remoteTyping[0]),
        nameTwo: this.getDisplayName(this.remoteTyping[1]),
        nameThree: this.getDisplayName(this.remoteTyping[2])
      });
    }
    return '';
  }

  public updateStartOfDayForMessages() {
    this.dateTracker = {};
    this.messages.forEach(m => (m.clCompChat_isStartOfDay = false));
    this.messages.forEach(m => (m.clCompChat_isStartOfDay = this.isStartOfDay(m.createdOn)));
  }

  private isStartOfDay(date: Date) {
    //return true;
    let retVal: boolean = false;
    try {
      let newDate: Date = new Date(date);

      let flatDate: string = `${newDate.getFullYear()}-${newDate.getMonth()}-${newDate.getDate()}`;
      if (!this.dateTracker[flatDate]) {
        this.dateTracker[flatDate] = flatDate;
        retVal = true;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.isStartOfDay(): Error',
        logParameters: [ex]
      });
    }

    return retVal;
  }

  private setAttachmentType(message: MessageEx) {
    var attachmentType = MessageUtils.getAttachmentContentType(message, this.config?.jsonMessagesAsSource);
    if (attachmentType) {
      message.clCompChat_attachmentContentType = attachmentType;
    }
  }

  private scrollMoreUp(event: any) {
    if (!this.isSnapshot && this.moreMessages && this.fetchingMessages == 0) {
      this.scrollContainerHeightSnapshot = this.scrollingService.scrollingContainer.scrollHeight;
      this.fetchMessages(FetchType.Normal, {
        loadAttempt: this.conversationLoadAttempt
      });
    } else if (this.isSnapshot && this.moreSnapshotMessagesUp && this.fetchingMessages == 0) {
      this.scrollContainerHeightSnapshot = this.scrollingService.scrollingContainer.scrollHeight;
      this.fetchMessages(FetchType.SnapshotUp, {
        loadAttempt: this.conversationLoadAttempt
      });
    }
  }

  private endTyping(event) {
    this.typing = false;
    this.cdRef.markForCheck();
  }

  private startTyping() {
    if (!this.typing) {
      // first time... send typing indication
      this.sendTypingIndicator();
    }
    this.typing = true;
    this.cdRef.markForCheck();
  }

  private async sendTypingIndicator() {
    // Make sure the conversationId is already set before sending typing indicators. aiMode has
    // no real CloudLink conversation/participants to notify - chatCloudLinkService.chat isn't a
    // real SDK instance there, so this would just be a wasted, erroring call.
    if (this.conversationId && !this.aiMode && !this.chatCloudLinkService.isOffline()) {
      try {
        await this.chatCloudLinkService.chat.sendParticipantTyping({
          conversationId: this.conversationId,
          participantId: this.chatAuthService.userId
        });
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.component.sendTypingIndicator(): Error',
          logParameters: [ex]
        });
      }
    }
  }

  public onFocus() {
    let clearFirstUnread = true;
    if (this.ignoreFirstFocus) {
      this.ignoreFirstFocus = false;
      clearFirstUnread = false;
    }
    this.chatInputFocused = true;
    //If you have focus on the input, lose focus, and click on the main chat area, a focus
    //followed by a blur on the input is triggered. Add a delay to handle that scenario
    setTimeout(() => {
      if (
        this.chatInputFocused &&
        this.hasUnreadMessage &&
        this.messages &&
        this.messages.length &&
        (this.firstUnreadMessageVisible() || this.scrollingService.isNearBottom())
      ) {
        this.updateLastReadMessageDate(this.messages[this.messages.length - 1]);
        if (clearFirstUnread) {
          this.clearFirstUnread();
        }
      } else if (this.chatInputFocused && !this.hasUnreadMessage && clearFirstUnread) {
        this.clearFirstUnread();
      }
    }, 0);
  }

  public onBlurChat() {
    this.chatFocused = false;
  }

  public onFocusChat() {
    this.chatFocused = true;
    if (
      this.hasUnreadMessage &&
      this.messages &&
      this.messages.length &&
      (this.firstUnreadMessageVisible() || this.scrollingService.isNearBottom())
    ) {
      this.updateLastReadMessageDate(this.messages[this.messages.length - 1]);
    }
  }

  public onBlur(e) {
    this.chatInputFocused = false;
  }

  private clearInput() {
    this.message = '';
    this.textAreaHasTwemoji = false;
    if (this.textArea && this.textArea.nativeElement) {
      this.textArea.nativeElement.innerText = '';
    }
    this.textAreaProps.lastNode = undefined;
    try {
      this.mentionRefs.forEach(r => {
        this.appRef.detachView(r.hostView);
        r.destroy();
      });
      this.mentionRefs = [];
    } catch (ex) {
      this.mentionRefs = [];
      this.chatLoggingService.error({
        logMessage: 'chat.component.clearInput(): Error',
        logParameters: [ex]
      });
    }
  }

  public onInput(event) {
    //This is for iOS dictate to prevent it from inserting the text again if the send button is clicked
    if (
      this.chatCloudLinkService.browser.os.toLowerCase() === 'ios' &&
      this.justSentButton &&
      event.data &&
      event.data.length > 1 &&
      this.message === ''
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.clearInput();
    } else if (
      this.chatCloudLinkService.browser.os.toLowerCase() === 'ios' &&
      this.justSentButton &&
      event.data &&
      event.data.length > 1 &&
      this.message.indexOf(event.data) > -1
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.textArea.nativeElement.innerText = this.message;
    } else {
      this.message = event.target.innerText;
      this.cdRef.markForCheck();
    }
    this.clearJustSentButton();
  }

  private clearJustSentButton() {
    if (this.justSentButtonTimeout) {
      clearTimeout(this.justSentButtonTimeout);
      this.justSentButtonTimeout = null;
      this.justSentButton = false;
    }
  }

  //Detects whether or not the oldest unread message is visible on screen
  private firstUnreadMessageVisible() {
    let headerHeight = 0;
    if (this.header && this.header.nativeElement) {
      headerHeight = this.header.nativeElement.clientHeight;
    }

    if (this.firstUnreadMessage) {
      let firstUnreadMessageElement = document.getElementById(this.firstUnreadMessage.messageId);
      if (firstUnreadMessageElement?.firstElementChild) {
        let msgElement = firstUnreadMessageElement.firstElementChild as HTMLElement;
        let containerTop = this.scrollingService.scrollingContainer.scrollTop;
        let containerBottom = containerTop + this.scrollingService.scrollingContainer.clientHeight;

        let elementTop = msgElement.offsetTop - headerHeight;
        let elementBottom = elementTop + msgElement.clientHeight;

        let totalView = elementTop >= containerTop && elementBottom <= containerBottom;
        let partialView =
          (elementTop < containerTop && elementBottom > containerTop) ||
          (elementBottom > containerBottom && elementTop < containerBottom);
        return totalView || partialView;
      }
    }
    return true;
  }

  public clearUnreadMessages() {
    if (this.messages && this.messages.length) {
      this.updateLastReadMessageDate(this.messages[this.messages.length - 1]);
      this.clearFirstUnread();
    }
  }

  public focusTextArea() {
    if (this.textArea) {
      this.textArea.nativeElement.focus();
    }
  }

  private clearFirstUnread(clearUpToDate = null) {
    let firstUnread = find(this.messages, m => {
      return m.clCompChat_firstUnread;
    });
    if (firstUnread) {
      let clear = true;
      if (clearUpToDate) {
        if (new Date(firstUnread.createdOn) > new Date(clearUpToDate)) {
          clear = false;
        }
        if (clear) {
          let newUnread = find(this.messages, m => new Date(m.createdOn) > new Date(clearUpToDate));
          if (newUnread) {
            newUnread.clCompChat_firstUnread = true;
          }
        }
      }
      if (clear) {
        firstUnread.clCompChat_firstUnread = false;
        this.firstUnreadMessageDateString = '';
      }
    }
    this.cdRef.markForCheck();
  }

  private updateHasUnreadMessage(conversationId: string) {
    if (conversationId !== this.conversationId) {
      return;
    }
    if (!this.messages || !this.messages.length) {
      this.hasUnreadMessage = false;
    } else if (!this.lastReadMessageDate) {
      this.hasUnreadMessage = true;
    }
    if (this.lastReadMessageDate && this.messages.length) {
      this.hasUnreadMessage =
        new Date(this.messages[this.messages.length - 1].createdOn) > new Date(this.lastReadMessageDate);
    }
    if (this.hasUnreadMessage) {
      var newMessages = this.messages;
      if (this.lastReadMessageDate) {
        var lastReadMessageDateObject = new Date(this.lastReadMessageDate);
        var newMessages = this.messages.filter(m => new Date(m.createdOn) > lastReadMessageDateObject);
      }
      this.unreadMessageCount = newMessages.length.toString();
      if (newMessages.length == this.messages.length && this.moreMessages) {
        this.unreadMessageCount += '+';
      }
      this.clearFirstUnread();
      this.firstUnreadMessage = newMessages[0];
      if (this.firstUnreadMessage) {
        this.firstUnreadMessage.clCompChat_firstUnread = true;
        this.firstUnreadMessageDateString = this.chatDatePipe.transform(
          this.firstUnreadMessage.createdOn,
          this.dateTimeLocale,
          this.config?.showDayOfWeek
        );
      }
      //Missed an event, need to update last read message date (only if not dateTime min, as host app prob marked as unread)
      var myNewMessages = newMessages.filter(m => m.createdBy === this.chatAuthService.userId);
      if (
        myNewMessages.length &&
        new Date(this.lastReadMessageDate).getTime() !== new Date('0001-01-01T00:00:00Z').getTime()
      ) {
        var lastMessage = myNewMessages[myNewMessages.length - 1];
        this.updateLastReadMessageDate(lastMessage);
      }

      if (!this.lastReadMessageDate) {
        this.setAndStoreLastReadMessageDate(
          this.currentConversation.createdOn,
          this.currentConversation.conversationId
        );
      }
      this.unreadMessageString = this.translate.instant('cl_chat.unreadSince', {
        count: this.unreadMessageCount,
        date: this.lastReadMessageDateString
      });
    }
    this.cdRef.markForCheck();
  }

  public updateLastReadMessageDate(message: Message, updateParticipant: boolean = true) {
    this.chatLoggingService.info({
      logMessage:
        'chat.component.updateLastReadMessageDate(): updateParticipant, lastReadMessageDate, message.createdOn, message.tag:',
      logParameters: [
        this.conversationId,
        updateParticipant,
        this.lastReadMessageDate,
        message?.createdOn,
        message?.tag
      ]
    });

    if (
      !this.lastReadMessageDate ||
      new Date(this.lastReadMessageDate) < new Date(message?.createdOn) ||
      this.checkLastSend(message)
    ) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.updateLastReadMessageDate(): proceed to updateLastRead',
        logParameters: [
          this.conversationId,
          updateParticipant,
          this.lastReadMessageDate,
          message?.createdOn,
          message?.tag
        ]
      });
      this.setAndStoreLastReadMessageDate(message.createdOn, message.conversationId);
      if (message.createdBy === this.chatAuthService.userId) {
        this.clearFirstUnread();
      }
      if (updateParticipant) {
        // aiMode has no real CloudLink participant to update - the conversation is synthetic
        // (there's no SDK-side participant record for it at all), so calling into the SDK here
        // throws (updateParticipant() requires a non-blank participantId).
        if (!this.aiMode) {
          this.chatConversationService.updateLastRead(this.conversationId, this.lastReadMessageDate);
        }
        this.onUnreadUpdated.emit({
          conversationId: this.conversationId,
          lastReadMessageDate: this.lastReadMessageDate as any
        });
      }
    }
    if (message?.conversationId) {
      this.updateHasUnreadMessage(message.conversationId);
    }
  }

  private checkLastSend(message: Message) {
    if (message?.tag) {
      try {
        let tag = JSON.parse(message.tag);
        if (Object.keys(tag).length === 1 && tag.sendId) {
          if (
            this._lastMessageSend?.sendId === tag.sendId &&
            this._lastMessageSend?.createdOn === this.lastReadMessageDate
          ) {
            return true;
          }
        }
      } catch (ex) {}
    }
    return false;
  }

  private async setAndStoreLastReadMessageDate(lastReadMessageDate: Date, conversationId: string) {
    //Race condition when quickly switching conversations, make sure the conversation ids are aligned
    if (conversationId !== this.conversationId) {
      return;
    }
    this.lastReadMessageDate = lastReadMessageDate;
    // aiMode has no real accountId (chatAuthService.accountId only gets set here if the host app
    // supplies aiConfig.userId, which is optional) - the Dexie key is [accountId, conversationId],
    // and IDBKeyRange rejects undefined, so skip the write rather than crash on every AI message.
    // aiMode's own history comes from MAIS's chats endpoint, not this offline cache.
    if (this.aiMode) {
      return;
    }
    try {
      await this.chatDatabaseService.updateConversationLastReadMessageDate(
        this.chatAuthService.accountId,
        this.conversationId,
        <any>this.lastReadMessageDate
      );
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.setAndStoreLastReadMessageDate(): Error adding last read message date to db',
        logParameters: [ex, this.conversationId]
      });
    }
  }

  public onFileAttach(event: any) {
    var files = event.srcElement.files;
    const target = this.attachTarget;
    this.attachTarget = 'auto';
    if (files.length > 0) {
      for (var i = 0; i < files.length; i++) {
        this.uploadFileToClient(files[i], target);
      }
      //Need to clear or some browsers won't trigger event when same file is selected
      this.attachmentInput.nativeElement.value = null;
      this.focusTextArea();
    }
  }

  private uploadFile(file: File, attachmentGroup: string) {
    this.setUploadFlag();
    this.readAndUploadFile(file, attachmentGroup);
  }

  private uploadFileToClient(file: File, target: AttachTarget = 'auto') {
    //Prevent dups
    if (this.pendingAttachments) {
      var match = this.pendingAttachments.find(a => a.file.name === file.name && a.file.size === file.size);
      if (match) {
        return;
      }
    }
    if (this.pendingAttachments.length === 10) {
      this.error.set(this.translate.instant('cl_chat.only10FilesCanBeUploadedAtATime'));
      this.cdRef.markForCheck();
      return;
    }
    if (file?.size === 0) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat.component.uploadFileToClient(): Zero size file'
        },
        errorMessage: this.translate.instant('cl_chat.fileNotSupportedUploadAsZip')
      });
      return;
    }
    // The token can expire while the picker is open - say so rather than put the file in the chat against the choice
    if (target === 'onedrive' && !this.oneDriveAttachAvailable()) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat.component.uploadFileToClient(): OneDrive no longer connected'
        },
        errorMessage: this.translate.instant('cl_chat.oneDriveReconnect', { fileName: file.name })
      });
      return;
    }
    // From the menu the user chose; dropped and pasted files go to OneDrive only when too large for the chat
    const oneDrive = target === 'onedrive' || (target === 'auto' && this.canShareViaOneDrive(file));
    if (this.checkAllowedFileType(file.type) && (oneDrive || this.checkFileSize(file))) {
      var imageUrl = null;
      if (file.type.indexOf('image/') === 0) {
        imageUrl = window.URL.createObjectURL(file);
      }
      this.pendingAttachments.push({
        imageSafeUrl: imageUrl ? this.sanitizer.bypassSecurityTrustUrl(imageUrl) : null,
        imageUrl: imageUrl,
        file: file,
        fileName: file.name,
        fileSize: SharedHelpers.getFileSize(file.size),
        oneDrive
      });
    }
    this.cdRef.markForCheck();
  }

  private canShareViaOneDrive(file: File): boolean {
    return (
      !!this.config?.enableOneDriveLargeFiles &&
      file?.size >= this.maxFileSizeMb * 1024 * 1024 &&
      this.chatOneDriveService.isConnected()
    );
  }

  // Uploads to the user's OneDrive, then posts the share link as a message in this conversation
  private async shareViaOneDrive(file: File) {
    const upload: OneDriveUpload = {
      id: uuid(),
      fileName: file.name,
      progress: 0,
      abortController: new AbortController()
    };
    this.oneDriveUploads.update(uploads => [...uploads, upload]);
    this.onUploadStart.emit(file.name);
    try {
      const shared = await this.chatOneDriveService.shareFile(
        file,
        progress =>
          this.oneDriveUploads.update(uploads => uploads.map(u => (u.id === upload.id ? { ...u, progress } : u))),
        upload.abortController.signal
      );
      // The file is in OneDrive now; a failed send shows on the message itself, with retry
      await this.sendOneDriveLink(shared);
      this.onUploadEnd.emit(file.name);
    } catch (ex) {
      const code = ex instanceof ChatOneDriveError ? ex.code : ChatOneDriveErrorCode.Failed;
      // Every onUploadStart gets an end or an error, so a host's upload indicator clears - a cancel included
      this.onUploadError.emit(file.name);
      if (code !== ChatOneDriveErrorCode.Cancelled) {
        this.chatLoggingService.messageError({
          exceptionLogEvent: {
            logMessage: 'chat.component.shareViaOneDrive(): Failed',
            logParameters: [code, ex?.status]
          },
          errorMessage: this.translate.instant(this.getOneDriveErrorKey(code), { fileName: file.name })
        });
      }
    } finally {
      this.oneDriveUploads.update(uploads => uploads.filter(u => u.id !== upload.id));
      this.cdRef.markForCheck();
    }
  }

  // Not send(): the upload finishes whenever it finishes, so this must leave alone whatever the user is typing or
  // replying to by then - send() would clear the composer, quote the open reply and parse the name as typed input
  private async sendOneDriveLink(shared: ChatOneDriveSharedFile) {
    const now = new Date();
    const link: ChatCloudFileLink = {
      provider: 'onedrive',
      name: shared.name,
      webUrl: shared.webUrl,
      size: shared.size,
      itemId: shared.itemId
    };
    const msg: MessageEx = {
      conversationId: this.conversationId,
      accountId: this.chatAuthService.accountId,
      messageId: this.getLocalMessageId(),
      body: JSON.stringify(link),
      // What clients that don't know the content type show
      fallbackText: `${shared.name} (${SharedHelpers.getFileSize(shared.size)})\n${shared.webUrl}`,
      contentType: ContentTypes.CLOUDFILELINK,
      createdOn: now,
      createdBy: this.chatAuthService.userId,
      modifiedOn: now,
      modifiedBy: this.chatAuthService.userId,
      _links: {}
    };
    await this.sendMessage(msg, false, false);
  }

  private getOneDriveErrorKey(code: ChatOneDriveErrorCode): string {
    switch (code) {
      case ChatOneDriveErrorCode.NotConnected:
      case ChatOneDriveErrorCode.Unauthorized:
        return 'cl_chat.oneDriveReconnect';
      case ChatOneDriveErrorCode.QuotaExceeded:
        return 'cl_chat.oneDriveFull';
      default:
        return 'cl_chat.oneDriveShareFailed';
    }
  }

  public cancelOneDriveUpload(upload: OneDriveUpload) {
    upload.abortController.abort();
  }

  // Leaving the conversation stops its uploads, so a link never lands in a chat the user has moved away from
  private cancelOneDriveUploads() {
    this.oneDriveUploads().forEach(upload => upload.abortController.abort());
  }

  private checkFileSize(file): boolean {
    const maxSizeInBytes = this.maxFileSizeMb * 1024 * 1024;
    if (file && file.size && file.size >= maxSizeInBytes) {
      if (this.dragging) {
        this.dragging = false;
        this.draggingError = true;
        setTimeout(() => {
          this.dragDropOkBtn?.nativeElement?.focus();
        }, 0);
      } else if (!this.draggingError) {
        this.error.set(
          this.translate.instant('cl_chat.fileTooLarge') +
            ': ' +
            this.translate.instant('cl_chat.maxFileSizeMB', {
              size: this.maxFileSizeMb
            })
        );
      }
      this.cdRef.markForCheck();
      return false;
    }
    return true;
  }

  private readAndUploadFile(file: File, attachmentGroup: string) {
    const fr = new FileReader();
    fr.onloadend = e => {
      let name = file.name;
      let data = (e.target as any).result;
      var attachmentExtension = '';
      var lastPeriod = name.lastIndexOf('.');
      if (lastPeriod > -1) {
        attachmentExtension = name.substring(lastPeriod + 1, name.length);
      }

      var attachmentId = uuid() + moment().format('YYYYMMDDHHmmss') + '.' + attachmentExtension;

      let fileType = file.type;
      this.chatLoggingService.info({
        logMessage: 'chat.component.readAndUploadFile(): checking File Type',
        logParameters: [file.type]
      });

      if (
        !fileType ||
        fileType.endsWith('zip') ||
        fileType.endsWith('compressed') ||
        fileType.endsWith('zip2') ||
        fileType.endsWith('json') ||
        fileType.toLowerCase().includes('ms-excel.sheet.macroenabled') //.xlsm
      ) {
        // CL-13867 CL-15277 CL-17219 CL-17357
        fileType = 'application/octet-stream';
      }

      const attachment = {
        name: name,
        data: data,
        id: attachmentId,
        type: fileType
      };

      this.uploadAttachment(attachment, attachmentGroup);
    };

    fr.readAsArrayBuffer(file);
  }

  // HTML5 compatible types, source: https://www.w3schools.com/html/html5_video.asp
  // The attachments API allows us to specify a Content-Type which will later be used
  // when picking either a stream source buffer or dealing with the video element.
  //
  // TL;DR - If there is no way for us to play it back we should not offer a player
  // but a download link.
  private isCompatibleVideoType(mimeType: string): boolean {
    const supportedTypes = ['video/mp4', 'video/webm', 'video/ogg'];

    for (const type of supportedTypes) {
      if (mimeType.indexOf(type) > 0) {
        return true;
      }
    }

    return false;
  }

  public closeDraggingError() {
    this.draggingError = false;
    this.cdRef.markForCheck();
  }

  private async uploadAttachment(
    attachment: { name: string; data: any; id: string; type?: string },
    attachmentGroup: string
  ) {
    if (this.quotedMessage) {
      let msg = this.parseQuotedMessage();
      await this.sendMessage(msg, false);
    }

    if (!attachment?.data || !attachment?.data?.byteLength) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat.component.uploadAttachment(): Unsupported file',
          logParameters: [attachment?.id]
        },
        errorMessage: this.translate.instant('cl_chat.fileNotSupportedUploadAsZip')
      });
      this.clearUploadFlag();
      return;
    }

    let conversationId = this.conversationId;
    let createdOn = new Date();
    this.chatConversationService.pendingAttachments[attachment.id] = {
      attachmentName: attachment.name,
      attachmentGroup: attachmentGroup
    };
    let attachmentMessage: MessageEx = {
      conversationId: conversationId,
      body: attachment.id,
      contentType: ContentTypes.ATTACHMENT,
      fallbackText: attachment.name,
      tag: JSON.stringify({
        attachmentSize: SharedHelpers.getFileSize(attachment.data.byteLength)
      }),
      accountId: this.chatAuthService.accountId,
      messageId: '',
      createdOn: createdOn,
      createdBy: this.chatAuthService.userId,
      modifiedOn: createdOn,
      modifiedBy: this.chatAuthService.userId,
      _links: {}
    };
    if (!(<any>this.currentConversation).generateSystemMessages) {
      this.chatConversationService.pendingAttachmentMessages[attachmentGroup].push(attachmentMessage);
      this.pendingAttachmentIds.push(attachment.id);
    }
    this.lastAttachmentIds.push(attachment.id);
    this.chatLoggingService.info({
      logMessage: 'chat.component.uploadAttachment(): Uploading attachment',
      logParameters: [attachment.id, attachment.name, attachmentGroup]
    });
    let analyticObj: AnalyticObject = {
      eventName: AnalyticEvents.analytic_events.common.event_name.chat.send_attachment,
      attributes: ChatAnalyticAttributes
    };
    this.onAnalyticEvent.emit(analyticObj);
    this.onUploadStart.emit(attachment.name);
    try {
      var response = await this.chatCloudLinkService.chat.updateAttachmentBinary({
        conversationId: this.conversationId,
        attachmentId: attachment.id,
        body: attachment.data,
        options: { headers: { 'content-type': attachment.type } }
      });
      //Note can't use this response to update the attachment meta, as the PUT attach binary triggers an
      //s3 update which triggers a lambda which triggers an ES update with ES being eventually consistent
      this.chatLoggingService.info({
        logMessage: 'chat.component.uploadAttachment() binary success',
        logParameters: [attachment.id, attachment.name, attachmentGroup]
      });
      if (!(<any>this.currentConversation).generateSystemMessages) {
        //In case the PUT succeeds, but internet dies after resulting in no subscription event received. We'll just fail it for now.
        setTimeout(() => {
          if (this.pendingAttachmentIds.indexOf(attachment.id) > -1) {
            this.clearPendingAttachment(attachment, attachmentGroup);
            this.onUploadError.emit(attachment.name);
            this.chatLoggingService.messageError({
              exceptionLogEvent: {
                logMessage: 'chat.component.uploadAttachment(): Message not created'
              },
              errorMessage: this.translate.instant('cl_chat.failedToCreateAttachmentMessage')
            });
            //To populate chat info to stay in sync
            this.chatConversationService.attachmentManualNotify$.next({
              conversationId: conversationId,
              name: attachment.id,
              displayName: attachment.id,
              createdOn: createdOn,
              createdBy: this.chatAuthService.userId
            });
          }
        }, 10000);
      }
    } catch (ex) {
      this.clearPendingAttachment(attachment, attachmentGroup);
      this.onUploadError.emit(attachment.name);
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat.component.uploadAttachment(): Error binary',
          logParameters: [ex]
        },
        errorMessage: this.translate.instant('cl_chat.uploadFailedTryAgain')
      });
    }
  }

  private clearPendingAttachment(attachment: { name: string; data: any; id: string; type?: string }, attachmentGroup) {
    this.lastAttachmentIds.splice(this.lastAttachmentIds.indexOf(attachment.id), 1);
    this.pendingAttachmentIds.splice(this.pendingAttachmentIds.indexOf(attachment.id), 1);
    if (this.chatConversationService.pendingAttachmentMessages[attachmentGroup]) {
      this.chatConversationService.pendingAttachmentMessages[attachmentGroup].splice(
        this.chatConversationService.pendingAttachmentMessages[attachmentGroup].findIndex(
          p => p.body === attachment.id
        ),
        1
      );
    }
    if (this.chatConversationService.pendingAttachmentMessages[attachmentGroup].length === 0) {
      delete this.chatConversationService.pendingAttachmentMessages[attachmentGroup];
    }
    delete this.pendingAttachments[attachment.id];
    this.clearUploadFlag();
  }

  private setUploadFlag() {
    this.uploadCount++;
    this.uploading = true;
    this.cdRef.markForCheck();
  }

  private clearUploadFlag(clearAll: boolean = false, detectChanges = true) {
    if (this.uploadCount > 0) {
      this.uploadCount--;
    }
    if (clearAll) {
      this.uploadCount = 0;
    }
    if (this.uploadCount === 0) {
      this.uploading = false;
    }
    if (detectChanges) {
      this.cdRef.markForCheck();
    }
  }

  public async acceptInvite(): Promise<void> {
    try {
      await this.chatConversationService.acceptInvite(this.conversationId);
      this.loadConversation();
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.acceptInvite(): Error',
        logParameters: [ex]
      });
    }
  }

  public async join(): Promise<void> {
    try {
      await this.chatConversationService.joinConversation(this.conversationId);
      this.loadConversation();
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.join(): Error',
        logParameters: [ex]
      });
    }
  }

  public async leave(): Promise<void> {
    if (!this.currentConversation.stream) return;

    try {
      await this.chatConversationService.leaveStream(this.conversationId);
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.leave(): Error',
        logParameters: [ex]
      });
    }

    this.onGoHome.emit();
  }

  private hasChatFocus() {
    return (
      this.chatInputFocused ||
      this.chatFocused ||
      (this.config && this.config.useDocumentFocusForUnread && document.hasFocus())
    );
  }

  private hasFocus() {
    if (this.config && this.config.useDocumentVisibleForUnread) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.hasFocus() - using document visibility for unread check',
        logParameters: [document.visibilityState, this.chatFocused, this.chatInputFocused]
      });
    }
    return (
      this.chatInputFocused ||
      this.chatFocused ||
      (this.config && this.config.useDocumentFocusForUnread && document.hasFocus()) ||
      (this.config && this.config.useDocumentVisibleForUnread && document.visibilityState === 'visible')
    );
  }

  private setupScrolling() {
    // setup scrolling
    if (this.maincontent) {
      let domElement = this.maincontent.nativeElement as HTMLElement;

      this.scrollingService.setContainer(domElement);

      if (this.scrollingSub) {
        this.scrollingSub.unsubscribe();
      }
      if (this.scrollingSubUp) {
        this.scrollingSubUp.unsubscribe();
      }
      if (this.scrollingSubDown) {
        this.scrollingSubDown.unsubscribe();
      }

      this.scrollingSub = this.scrollingService
        .getScrollObservable()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(e => {
          if (
            this.hasUnreadMessage &&
            this.hasFocus() &&
            this.messages &&
            this.messages.length &&
            (this.firstUnreadMessageVisible() || this.scrollingService.isNearBottom())
          ) {
            this.updateLastReadMessageDate(this.messages[this.messages.length - 1]);
          }
        });

      this.scrollingSubUp = this.scrollingService
        .getScrollUpObservable()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(e => {
          try {
            this.scrollMoreUp(e);
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.setupScrolling(): Error in scrollingSubUp',
              logParameters: [ex]
            });
          }
        });

      this.scrollingSubDown = this.scrollingService
        .getScrollDownObservable()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(e => {
          //scroll chat in inactive window should also remove unread indicator
          if (
            this.hasUnreadMessage &&
            !this.hasChatFocus() &&
            this.messages &&
            this.messages.length &&
            (this.firstUnreadMessageVisible() || this.scrollingService.isNearBottom())
          ) {
            this.updateLastReadMessageDate(this.messages[this.messages.length - 1]);
          }
          try {
            if (this.isSnapshot && this.moreSnapshotMessagesDown && this.fetchingMessages == 0) {
              this.fetchMessages(FetchType.SnapshotDown, {
                loadAttempt: this.conversationLoadAttempt
              });
            }
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat.component.setupScrolling(): Error in scrollingSubDown',
              logParameters: [ex]
            });
          }
        });
    } else {
      this.chatLoggingService.error({
        logMessage: 'chat.component.setupScrolling()=> scrolling container not ready?'
      });
    }
  }

  private async resetState(isReload: boolean = false, restoreTextInput = false) {
    if (isReload && !this.isSearch && !this.isSnapshot) {
      this.messages.forEach(m => {
        m.clCompChat_needsResync = true;
      });
      this.isReload = true;
    } else {
      this.currentConversation = <any>{
        conversationId: '',
        name: 'No Conversation Selected',
        participants: [],
        stream: false
      };
      this.clearInput();
      this.focusedOnLoad = false;
      this.isReload = false;
      this.firstLoad = true;
      this.reactions = {};
      this.messages = [];
      this.lastReadMessageDate = undefined;
      this.quotedMessage = null;
      this.isLargeParticipantLoad = false;
      this.participantTotal = 0;
      this.exitFullScreen();
      if (restoreTextInput) {
        this.restoreTextInput();
      }
    }
    if (!isReload) {
      await this.stopRecording(true);
      this.removeRecording();
    }
    this.hasUnreadMessage = false;
    this.noDbConversation.set(false);
    this.fetchNormalCount = 0;
    this.clearCopiedAttachments(false);
    this.joinFailed$ = new ReplaySubject(1);
    this.clearUploadFlag(true, false);
    this.readOnly = false;
    this.isSearch = false;
    this.isSnapshot = false;
    this.dateTracker = {};
    this.search = '';
    this.remoteTyping = [];
    this.next = JSON.parse(JSON.stringify(this.defaultNext));

    this.participants = [];
    this.moreMessages = false;
    this.conversationLoadAttempt++;
    this.error.set(undefined);
    this.cdRef.markForCheck();
  }

  private resetSearch(): void {
    this.isSearch = false;
    this.contentLoading.set(true);
    this.isSnapshot = false;
    this.next = JSON.parse(JSON.stringify(this.defaultNext));
    this.messages = [];
    this.moreMessages = false;
    this.firstLoad = true;
    this.isReload = false;
    this.conversationLoadAttempt++;
    this.error.set(undefined);
    this.cdRef.markForCheck();
  }

  private async fetchMessages(
    fetchType: FetchType = FetchType.Normal,
    options?: {
      search?: string;
      loadAttempt?: number;
      filter?: string;
      orderBy?: string;
    }
  ): Promise<any> {
    if (this.chatCloudLinkService.isOffline()) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.fetchMessages(): chatCloudlink service is offline, ignoring fetch messages',
        logParameters: [this.conversationId, new Date().toISOString()]
      });
      return;
    }

    if (this.conversationId) {
      let fetchConversationId = this.conversationId;
      this.fetchingMessages++;

      let params: any = {
        conversationId: this.conversationId,
        expand: "reactions,translation($filter=languageCode eq '" + this.chatAuthService.user.languageCode + "')"
      };

      if (fetchType == FetchType.Normal) {
        params.skip = this.next.$skip;
        params.skipToken = this.next.$skipToken;
        params.top = this.next.$top;
        if (options && options.search && options.search != '') {
          params.search = `${options.search}`;
        }
        if (this.next && this.next.$search && this.next.$search != '') {
          params.search = `${this.next.$search}`;
        }
      } else if (fetchType == FetchType.SnapshotUp) {
        params.skip = this.snapshotUp.$skip;
        params.skipToken = this.snapshotUp.$skipToken;
        params.top = this.snapshotUp.$top;
        params.filter = `${this.snapshotUp.$filter}`;
        params.orderby = `${this.snapshotUp.$orderBy}`;
      } else if (fetchType == FetchType.SnapshotDown) {
        params.skip = this.snapshotDown.$skip;
        params.skipToken = this.snapshotDown.$skipToken;
        params.top = this.snapshotDown.$top;
        params.filter = `${this.snapshotDown.$filter}`;
        params.orderby = `${this.snapshotDown.$orderBy}`;
      } else if (fetchType == FetchType.Snapshot) {
        params.skip = 0;
        params.top = 1;
        params.filter = `${options.filter}`;
      }

      var loadAttempt: number | undefined = undefined;
      if (options && options.loadAttempt) {
        loadAttempt = options.loadAttempt;
      }

      this.chatLoggingService.info({
        logMessage: 'chat.component.fetchMessages() fetch start at ' + new Date().toISOString(),
        logParameters: [this.conversationId, FetchType[fetchType], this.messages.length, loadAttempt]
      });

      try {
        this.fetchUnsubscribe.next();
        this.fetchUnsubscribe.complete();
        this.fetchUnsubscribe = new Subject();
        let messageCollection = await defer(() =>
          this.chatCloudLinkService.chat.getMessages(params).then(res => res?.body)
        )
          .pipe(
            retryWhen(
              SharedHelpers.genericRetryStrategy(
                `[chat.component.fetchMessages()] - fetch start at ${new Date().toISOString()}, ${this.conversationId}`,
                this.chatLoggingService
              )
            ),
            takeUntil(this.fetchUnsubscribe)
          )
          .toPromise();
        let reloadNearBottom = this.scrollingService.isNearBottom();
        if ((loadAttempt && loadAttempt !== this.conversationLoadAttempt) || !this.isActive || !messageCollection) {
          throw this.oldRequestError;
        }
        if (fetchType == FetchType.Normal) {
          this.moreMessages = messageCollection?._links?.next ? true : false;
          if (this.moreMessages) {
            const next: string = messageCollection._links.next || '';
            const queryString: any = SharedHelpers.parseQueryString(next);
            this.next.$skip = queryString.$skip;
            this.next.$skipToken = queryString.$skipToken;
            this.next.$top = queryString.$top;
            this.next.$search = queryString.$search;
          }
        } else if (fetchType == FetchType.SnapshotUp) {
          this.moreSnapshotMessagesUp = messageCollection?._links?.next ? true : false;
          if (this.moreSnapshotMessagesUp) {
            const next: string = messageCollection._links.next || '';
            const queryString: any = SharedHelpers.parseQueryString(next);
            this.snapshotUp.$skip = queryString.$skip;
            this.snapshotUp.$skipToken = queryString.$skipToken;
            this.snapshotUp.$top = queryString.$top;
          }
        } else if (fetchType == FetchType.SnapshotDown) {
          this.moreSnapshotMessagesDown = messageCollection?._links?.next ? true : false;
          if (this.moreSnapshotMessagesDown) {
            const next: string = messageCollection._links.next || '';
            const queryString: any = SharedHelpers.parseQueryString(next);
            this.snapshotDown.$skip = queryString.$skip;
            this.snapshotDown.$skipToken = queryString.$skipToken;
            this.snapshotDown.$top = queryString.$top;
          } else {
            //Moving this after we goToMessage in snapshot to fix race condition with scroll to bottom
            //this.isSnapshot = false;
          }
        }
        let messages = getItemsFromCollection(messageCollection);

        if (
          messages.length === 0 &&
          fetchType === FetchType.Normal &&
          !params.skipToken &&
          params.skip === 0 &&
          !params.search
        ) {
          if (this.config?.retryFetchOnEmpty) {
            // CL-18399 The original Elastic Search issue requires a retry to retrieve the missing Pre-Route Greeting.
            let retryResults = await timer(11000)
              .pipe(
                switchMap(() => defer(() => this.chatCloudLinkService.chat.getMessages(params).then(res => res?.body))),
                retryWhen(
                  SharedHelpers.genericRetryStrategy(
                    `[chat.component.fetchMessages()] - fetch start at ${new Date().toISOString()}, ${
                      this.conversationId
                    }`,
                    this.chatLoggingService
                  )
                ),
                takeUntil(this.fetchUnsubscribe)
              )
              .toPromise();

            messages = getItemsFromCollection(retryResults);
            this.chatLoggingService.info({
              logMessage: 'chat.component.fetchMessages(): waiting for 11 seconds and retry',
              logParameters: [this.conversationId, messages.length]
            });
          }
        }

        if (fetchType == FetchType.Snapshot) {
          if (messages && messages[0]) {
            this.snapshotDown.$filter = "createdOn gt '" + messages[0].createdOn + "'";
            this.snapshotDown.$orderBy = 'createdOn asc';
            this.snapshotUp.$filter = "createdOn lt '" + messages[0].createdOn + "'";
            this.snapshotUp.$orderBy = 'createdOn desc';
          }
        }
        if ((loadAttempt && loadAttempt !== this.conversationLoadAttempt) || !this.isActive) {
          throw this.oldRequestError;
        }
        if (!(messages && messages.length)) {
          messages = new Array<MessageEx>();
        }

        //Only want to process on a regular first fetch
        if (fetchType === FetchType.Normal && !params.skipToken && params.skip === 0 && !params.search) {
          try {
            // Check cloudlink service for recent messages
            let recentMessageMatches = this.chatCloudLinkService.recentMessages.filter(
              m => m.conversationId === this.conversationId
            );
            recentMessageMatches.forEach(m => {
              //No match found and newer than most recent message
              if (
                !messages.find(message => message.messageId === m.messageId) &&
                (messages.length === 0 || new Date(m.createdOn) > new Date(messages[0].createdOn))
              ) {
                messages.push(m);
                this.chatLoggingService.info({
                  logMessage: 'chat.component.fetchMessages(): Adding recent message from cloudlink service',
                  logParameters: [this.conversationId, m.messageId]
                });
              }
            });
            //Remove any recently deleted messages
            messages = messages.filter(
              m => !this.chatCloudLinkService.recentDeletedMessages.find(d => d === m.messageId)
            );
          } catch (ex) {
            this.chatLoggingService.error({
              logMessage: 'chat.component.fetchMessages(): Error adding recent message from cloudlink service',
              logParameters: [ex]
            });
          }
        }

        await new Promise<void>(async (resolve, reject) => {
          if (messages && messages.length) {
            // check on messages & length.

            // Need to normalize messages that have the same createdOn by adding .001 to the newer one so that sort works properly on cached messages
            messages = messages.sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
            try {
              for (let i = 0; i < messages.length; i++) {
                //Need to check all subsequent messages in case of multiple matches
                for (let y = i + 1; y < messages.length; y++) {
                  if (new Date(messages[i].createdOn).getTime() === new Date(messages[y].createdOn).getTime()) {
                    (<any>messages[y]).createdOn = new Date(
                      new Date(messages[y].createdOn).getTime() + 1
                    ).toISOString();
                  } else {
                    break;
                  }
                }
              }
            } catch (ex) {
              this.chatLoggingService.error({
                logMessage: 'chat.component.fetchMessages(): Error normalizing messages',
                logParameters: [ex]
              });
            }

            let newMessages = this.mapToMessageEx(messages, false);
            newMessages.forEach(m => {
              const i: number = findIndex(this.messages, (listMessage: Message) => {
                return (
                  listMessage.messageId === m.messageId ||
                  (listMessage.messageId.indexOf('local') === 0 && listMessage.tag === m.tag)
                );
              });
              if (i >= 0) {
                let parseMessage =
                  m.body !== this.messages[i].body ||
                  (m._embedded && m._embedded.reactions) ||
                  (m._embedded && m._embedded.translations) ||
                  (m._embedded && m._embedded.replies);
                if (this.messages[i].messageId.indexOf('local') === 0) {
                  this.messages[i].clCompChat_state = 'sent';
                  this.cdRef.markForCheck();
                }
                Object.assign(this.messages[i], m);
                if (parseMessage) {
                  let chatItem = this.chatItems.find(c => c.item().messageId === m.messageId);
                  if (chatItem) {
                    chatItem.parseMessage();
                  }
                }
              } else {
                this.messages.push(m);
              }
            });
            this.chatLoggingService.info({
              logMessage: 'chat.component.fetchMessages(): Finish parsing new messages',
              logParameters: [this.conversationId, new Date().toString()]
            });
            // Sort the items
            this.messages = this.messages.sort(
              (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
            );

            // TODO is the duplicate part obsolete now? 12/4/2019
            // remove hidden items and duplicates. Duplicates can happen if we get a notification
            // of a message before we finish loading this request, that is also returned by the call to messages
            this.messages = this.messages.filter((item, pos, ary) => {
              let notHidden = item?.contentType !== ContentTypes.HIDDEN;
              let notChild = !item.parentMessageId;

              // since we are sorted, we can just check if we match the item before us (based on messageId)
              let notDup = !pos || item.messageId != ary[pos - 1].messageId;

              return notDup && notHidden && notChild;
            });

            this.messages = this.messages.filter(msg => {
              return MessageUtils.checkAllowedPlatformSystemMessage(msg, this.config);
            });
            this.chatLoggingService.info({
              logMessage: 'chat.component.fetchMessages(): Finish filtering out allowed platform system messages',
              logParameters: [this.conversationId, new Date().toISOString()]
            });
            this.messages = this.filterOutMatchingAttachmentMessages(this.messages);
            this.chatLoggingService.info({
              logMessage: 'chat.component.fetchMessages(): Finish filtering out matching attachment messages',
              logParameters: [this.conversationId, new Date().toISOString()]
            });
            this.updateStartOfDayForMessages();
            this.chatLoggingService.info({
              logMessage: 'chat.component.fetchMessages(): Finish updating start of the day for messages',
              logParameters: [this.conversationId, new Date().toISOString()]
            });
            this.updateLastReadMessageToCheck();
            this.chatLoggingService.info({
              logMessage: 'chat.component.fetchMessages(): Finish updating last message to check for read',
              logParameters: [this.conversationId, new Date().toISOString()]
            });
            if (fetchType == FetchType.Normal) {
              this.fetchNormalCount++;
            }
            var earliestDbOrResyncMessage = this.messages.find(m => m.clCompChat_isDb || m.clCompChat_needsResync);
            if (earliestDbOrResyncMessage) {
              var earliestRegularMessage = this.messages.find(m => !m.clCompChat_isDb && !m.clCompChat_needsResync);
              if (
                this.fetchNormalCount < 3 &&
                (!earliestRegularMessage ||
                  !(
                    new Date(earliestRegularMessage.createdOn).getTime() <
                    new Date(earliestDbOrResyncMessage.createdOn).getTime()
                  ))
              ) {
                this.chatLoggingService.info({
                  logMessage: 'chat.component.fetchMessages(): Fetch to sync db/resync messages',
                  logParameters: [this.conversationId, params.skipToken, this.messages.length, loadAttempt]
                });
                await this.fetchMessages(FetchType.Normal, {
                  loadAttempt: loadAttempt
                });
                this.cdRef.markForCheck();
                resolve();
              } else {
                var countBefore = this.messages.length;

                //First let's remove all the old messages
                let oldestCloudMessage = this.messages.find(m => !m.clCompChat_isDb && !m.clCompChat_needsResync);
                if (oldestCloudMessage) {
                  this.messages = this.messages.filter(
                    m =>
                      new Date(m.createdOn) >= new Date(oldestCloudMessage.createdOn) &&
                      m.clCompChat_state !== 'pending'
                  );
                  this.chatLoggingService.info({
                    logMessage: 'chat.component.fetchMessages(): Removed old db messages',
                    logParameters: [countBefore - this.messages.length]
                  });
                }

                //Now we check deleted messages
                let deletedMessages = this.messages.filter(
                  m =>
                    (m.clCompChat_isDb || m.clCompChat_needsResync) &&
                    !MessageUtils.isWhisperUserMessage(m) &&
                    m.clCompChat_state !== 'failed' &&
                    m.clCompChat_state !== 'pending'
                );
                for (const msg of deletedMessages) {
                  //Let's just ignore deletes in the last 15 seconds and consider them false flags to be completely safe.
                  //TODO revisit the 15 second change after we introduce deleting, 3 seconds was too short
                  if (
                    Math.abs(new Date().getTime() - new Date(msg.createdOn).getTime()) <=
                    this.chatCloudLinkService.elasticDelay
                  ) {
                    msg.clCompChat_isDb = false;
                    msg.clCompChat_needsResync = false;
                    this.chatLoggingService.info({
                      logMessage: 'chat.component.fetchMessages(): Falsely flagged deleted db message',
                      logParameters: [msg.messageId]
                    });
                  } else {
                    this.messages = this.messages.filter(m => m.messageId != msg.messageId);
                    this.chatLoggingService.info({
                      logMessage: 'chat.component.fetchMessages(): Removed deleted db message',
                      logParameters: [msg.messageId]
                    });
                  }
                }

                var countAfter = this.messages.length;
                this.chatLoggingService.info({
                  logMessage: 'chat.component.fetchMessages(): Removed db/resync messages',
                  logParameters: [countBefore, countAfter]
                });
              }
            }

            this.cdRef.markForCheck();

            if (fetchType == FetchType.Normal || fetchType == FetchType.SnapshotUp) {
              setTimeout(() => {
                this.scrollingService.adjustScrollTopForMore(this.scrollContainerHeightSnapshot);
                this.scrollContainerHeightSnapshot = null;
                // we may not have enough messages to fit the scrolling container.. which blocks the scroll bar from allowing you to fetch more
                if (fetchType == FetchType.Normal && this.scrollingService.isScrollFullHeight() && this.moreMessages) {
                  this.chatLoggingService.info({
                    logMessage: 'chat.component.fetchMessages(): Fetching more to fill height'
                  });
                  this.fetchMessages(FetchType.Normal, {
                    loadAttempt: loadAttempt
                  });
                  this.firstLoadFetch = true;
                }
                resolve();
              }, 0);
            } else {
              resolve();
            }
          } else {
            //Need to delete any cached messages
            if (this.firstLoad && fetchType === FetchType.Normal && this.messages.length) {
              this.messages = [];
              this.cdRef.markForCheck();
            }
            resolve();
          }
        });
        if ((loadAttempt && loadAttempt !== this.conversationLoadAttempt) || !this.isActive) {
          throw this.oldRequestError;
        }
        let firstLoadParticipantRead = false;
        if (this.firstLoad) {
          // first load... scroll to bottom after load
          if (!this.firstLoadFetch) {
            this.firstLoad = false;
            firstLoadParticipantRead = true;
            setTimeout(() => {
              this.scrollingService.scrollToBottom();
            }, 0);
          } else {
            this.firstLoadFetch = false;
          }
        } else if (this.isReload) {
          if (reloadNearBottom) {
            setTimeout(() => {
              this.scrollingService.scrollToBottom();
            }, 0);
          }
          this.isReload = false;
        }
        //First load of this conversation, update last read message date if it has focus
        if (!this.lastReadMessageDate && this.messages && this.messages.length && this.hasChatFocus()) {
          this.updateLastReadMessageDateSub.unsubscribe();
          this.updateLastReadMessageDateSub = new Subscription();
          this.updateLastReadMessageDateSub.add(
            this.myParticipantLastReadLoaded$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
              this.updateLastReadMessageDateSub.unsubscribe();
              this.updateLastReadMessageDate(this.messages[this.messages.length - 1]);
            })
          );
        }
        this.updateHasUnreadMessageSub.unsubscribe();
        this.updateHasUnreadMessageSub = new Subscription();
        let currentConversationId = this.conversationId;
        this.updateHasUnreadMessageSub.add(
          this.myParticipantLastReadLoaded$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            this.updateHasUnreadMessageSub.unsubscribe();
            this.updateHasUnreadMessage(currentConversationId);
            //Need to check for DateTime min in case of host app marking as unread, thus db being out of sync
            if (
              firstLoadParticipantRead &&
              fetchType == FetchType.Normal &&
              !options.search &&
              this.config?.focusOnLoad &&
              (!this.focusedOnLoad ||
                new Date(this.lastReadMessageDate).getTime() === new Date('0001-01-01T00:00:00Z').getTime())
            ) {
              let manuallyCallOnFocus = false;
              //Was from db out of sybc
              if (this.focusedOnLoad) {
                manuallyCallOnFocus = true;
              }
              this.focusedOnLoad = true;
              if (this.ignoreFirstFocusTimeout) {
                clearTimeout(this.ignoreFirstFocusTimeout);
              }
              this.ignoreFirstFocus = true;
              if (this.hasChatFocus() && manuallyCallOnFocus) {
                this.onFocus();
              } else {
                this.focusTextArea();
              }
              //Just in case the focus text area doesn't take effect
              this.ignoreFirstFocusTimeout = setTimeout(() => {
                this.ignoreFirstFocus = false;
              }, 1000);
            }
          })
        );
        if (fetchType == FetchType.Normal) {
          if (this.loading() || this.contentLoading() || this.syncing()) {
            this.chatLoggingService.info({
              logMessage: 'chat.component.fetchMessages() firstLoad!',
              logParameters: [this.conversationId]
            });
            this.onConversationLoadEnd.emit({
              conversationId: this.conversationId
            });
          }
          this.loading.set(false);
          this.contentLoading.set(false);
          this.syncing.set(false);
        }
        if (
          fetchType === FetchType.Normal &&
          (!params.skip || params.skip === 0) &&
          !params.skipToken &&
          !this.isSearch &&
          !this.isSnapshot
        ) {
          let recentMessage = null;
          if (this.messages?.length) {
            recentMessage = this.messages[this.messages.length - 1];
          }
          this.onConversationFirstFetch.emit({
            conversationId: this.conversationId,
            recentMessage: recentMessage
          });
          this.chatLoggingService.info({
            logMessage: 'chat.component.fetchMessages(): conversation first fetch of the recent message',
            logParameters: [this.conversationId, recentMessage?.messageId, recentMessage?.createdOn]
          });
        }
        this.fetchingMessages--;
        this.cdRef.markForCheck();
        //Need to make avatar disappear if it's a continuing thread of messages from the same user on loading more messages
        this.chatLoggingService.info({
          logMessage: 'chat.component.fetchMessages() fetch end at' + new Date().toISOString(),
          logParameters: [this.conversationId, FetchType[fetchType], this.messages?.length, loadAttempt]
        });
        return fetchConversationId;
      } catch (ex) {
        this.fetchingMessages--;
        if (ex === this.oldRequestError) {
          this.chatLoggingService.info({
            logMessage: 'chat.component.fetchMessages(): Ignoring fetch messages, old request',
            logParameters: [this.conversationId, loadAttempt, this.conversationLoadAttempt]
          });
          return null;
        } else if (params.skipToken && ex?.body?.message?.indexOf('Given NextToken has already expired') > -1) {
          this.chatLoggingService.error({
            logMessage: 'chat.component.fetchMessages(): Expired NextToken, retrying without token',
            logParameters: [this.conversationId, params.skipToken]
          });
          this.next = JSON.parse(JSON.stringify(this.defaultNext));
          this.snapshotUp = JSON.parse(JSON.stringify(this.defaultNext));
          this.snapshotDown = JSON.parse(JSON.stringify(this.defaultNext));
          this.cdRef.markForCheck();
          await this.fetchMessages(fetchType, options);
          return;
        } else {
          this.onConversationLoadError.emit({
            conversationId: this.conversationId
          });
          if (ex?.body?.message.indexOf('User is not an accepted participant of the conversation') > -1) {
            //Wait for joinFailed before showing error
            if (this.config && this.config.autoJoinStream) {
              this.joinFailedSub.unsubscribe();
              this.joinFailedSub = new Subscription();
              this.joinFailedSub.add(
                this.joinFailed$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
                  this.joinFailedSub.unsubscribe();
                  this.chatLoggingService.messageError({
                    exceptionLogEvent: {
                      logMessage: 'chat.component.fetchMessages(): Error not an accepted participant on auto join',
                      logParameters: [ex]
                    },
                    errorMessage: this.translate.instant('cl_chat.userIsNotAnAcceptedParticipant')
                  });
                })
              );
            } else {
              this.chatLoggingService.messageError({
                exceptionLogEvent: {
                  logMessage: 'chat.component.fetchMessages(): Error not an accepted participant',
                  logParameters: [ex]
                },
                errorMessage: this.translate.instant('cl_chat.userIsNotAnAcceptedParticipant')
              });
            }
          } else {
            this.chatLoggingService.messageError({
              exceptionLogEvent: {
                logMessage: 'chat.component.fetchMessages(): Error',
                logParameters: [ex]
              },
              errorMessage:
                this.translate.instant('cl_chat.failedToLoadMessages') +
                ' ' +
                this.translate.instant('cl_chat.problemCommunicatingWithChat')
            });
          }
          this.setLoadingError();
        }
        this.cdRef.markForCheck();
      }
    }
  }

  private setLoadingError() {
    //Let's keep the db messages visible on a failure
    if (!this.messages?.length) {
      this.loading.set(true);
    }
    this.syncing.set(false);
  }

  private callWithRetry<T>(action: () => Promise<T>, actionName: string): Promise<T> {
    const pause = duration => new Promise(res => setTimeout(res, duration));

    const backoff = (retries, fn, delay = 500) =>
      fn().catch(err =>
        retries > 1 ? pause(delay).then(() => backoff(retries - 1, fn, delay * 2)) : Promise.reject(err)
      );

    // attempt to get the conversation up to 3 times with a backoff
    return backoff(3, () => {
      return action();
    }).catch(ex => {
      this.chatLoggingService.error({
        logMessage: 'chat.component.loadConversation(): Consistent Error',
        logParameters: [actionName, ex]
      });
      throw ex;
    });
  }

  public reloadConversation() {
    this.chatLoggingService.info({
      logMessage: 'chat.component.reloadConversation()',
      logParameters: [this.conversationId]
    });
    // Make sure we are subscribed to msg subscription before loading messages
    this.messageSubscriptionSub.unsubscribe();
    this.messageSubscriptionSub = new Subscription();
    this.messageSubscriptionSub.add(
      this.chatCloudLinkService.messageSubscriptionReady$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
        this.messageSubscriptionSub.unsubscribe();
        if (value) {
          this.onConversationLoadStart.emit({
            conversationId: this.conversationId,
            loadType: ConversationLoadType[ConversationLoadType.Reconnect].toString()
          });
          this.loadConversation(true);
        }
      })
    );
  }

  private async loadConversationFromDb(conversationId: string, loadAttempt: number) {
    if (!this.indexDbEnabled()) {
      return;
    }
    this.chatLoggingService.info({
      logMessage: 'chat.component.loadConversationFromDb()',
      logParameters: [conversationId]
    });
    let chatDbUsers: IChatUser[];
    try {
      chatDbUsers = await this.chatDatabaseService.loadUsers();
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.loadConversationFromDb(): Error loading users from db',
        logParameters: [conversationId, ex]
      });
    }

    if (chatDbUsers) {
      chatDbUsers.forEach(u => {
        this.chatAdminService.setCacheValues(u.principalId, u.displayName, u.email, u.avatars);
      });
    }
    let chatDbConv = await this.chatDatabaseService.getConversation(conversationId, this.chatAuthService.accountId);
    if (chatDbConv && chatDbConv.messages.length && this.messages.length === 0 && !this.config?.snapshotMessageOnLoad) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.loadConversationFromDb() Loaded from db',
        logParameters: [conversationId, chatDbConv.messages.length]
      });
      var dbMessages: MessageEx[] = lodashMap(chatDbConv.messages, (m: IChatDbMessage) =>
        this.chatDatabaseService.convertDbToMessage(m)
      );
      //Need to sort opposite first when mapping in case of locations
      dbMessages = dbMessages.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
      dbMessages = this.mapToMessageEx(dbMessages, true);
      dbMessages = dbMessages.sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
      if (loadAttempt === this.conversationLoadAttempt) {
        this.messages = dbMessages;
        this.updateStartOfDayForMessages();
        if (this.dbLastReadMessageDate && !this.lastReadMessageDate) {
          this.lastReadMessageDate = <any>this.dbLastReadMessageDate;
          this.updateHasUnreadMessage(conversationId);
        }
        this.loading.set(false);
        this.cdRef.markForCheck();
        this.scrollingService.scrollToBottom();
        await this.loadParticipantsFromDatabase(loadAttempt);
      } else {
        this.chatLoggingService.info({
          logMessage: 'chat.component.loadConversationFromDb() Ignoring, old load attempt',
          logParameters: [conversationId, loadAttempt]
        });
      }
    } else if (
      this.chatCloudLinkService.isOffline() &&
      chatDbConv &&
      chatDbConv.messages.length === 0 &&
      this.messages.length === 0
    ) {
      let conversationFromDb = await this.chatDatabaseService.getConversationFromDb(
        conversationId,
        this.chatAuthService.accountId
      );
      this.noDbConversation.set(!conversationFromDb);
    }

    if (this.config?.focusOnLoad && !this.focusedOnLoad && this.chatDatabaseService.synced[this.conversationId]) {
      this.focusedOnLoad = true;
      if (this.ignoreFirstFocusTimeout) {
        clearTimeout(this.ignoreFirstFocusTimeout);
      }
      this.ignoreFirstFocus = true;
      this.focusTextArea();
      //Just in case the focus text area doesn't take effect
      this.ignoreFirstFocusTimeout = setTimeout(() => {
        this.ignoreFirstFocus = false;
      }, 1000);
    }
  }

  private async loadConversationFromJsonPayload() {
    this.chatLoggingService.info({
      logMessage: 'chat.component.loadConversationFromJsonPayload() - use JSON messages payload as source',
      logParameters: [this.config.jsonMessagesPayload?.messages?.length]
    });

    this.messages = this.mapToMessageEx(this.config.jsonMessagesPayload.messages, false);
    this.participants = this.config.jsonMessagesPayload.participants;
    this.chatAdminService.getPrincipalsFromJsonPayload(this.participants);
    this.chatLoggingService.info({
      logMessage: 'chat.component.loadConversationFromJsonPayload() - loaded principals',
      logParameters: [this.participants.length]
    });
    this.participantEvent();

    // Filter out hidden messages
    this.messages = this.messages.filter(item => {
      return item != null && item.contentType !== ContentTypes.HIDDEN;
    });

    this.messages = this.messages.sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
    this.updateStartOfDayForMessages();
    this.updateLastReadMessageToCheck();

    this.loading.set(false);
    this.contentLoading.set(false);
    this.syncing.set(false);

    setTimeout(() => {
      this.cdRef.markForCheck();
    }, 0);
  }

  private async loadConversation(isReload: boolean = false, restoreTextInput = false) {
    await this.resetState(isReload, restoreTextInput);
    let conversationId: string = this.conversationId;
    var loadAttempt = this.conversationLoadAttempt;

    if (this.config.jsonMessagesAsSource) {
      if (
        Array.isArray(this.config?.jsonMessagesPayload?.messages) &&
        this.config.jsonMessagesPayload.messages.length > 0 &&
        Array.isArray(this.config?.jsonMessagesPayload?.participants) &&
        this.config.jsonMessagesPayload.participants.length > 0
      ) {
        this.loadConversationFromJsonPayload();
        this.onConversationLoadEnd.emit({
          conversationId: this.conversationId
        });
        this.cdRef.markForCheck();
        this.chatLoggingService.info({
          logMessage: 'chat.component.loadConversation() - loaded from JSON messages, participant count',
          logParameters: [this.conversationId, this.messages.length, this.participants.length]
        });
      } else {
        this.chatLoggingService.error({
          logMessage: 'chat.component.loadConversation() - not a valid JSON payload',
          logParameters: [this.conversationId]
        });
      }

      return;
    }

    if (conversationId) {
      if (this.isActive) {
        try {
          if (this.chatCloudLinkService.isOffline() || this.config?.onlyLoadFromDb) {
            //To be able to load Unknown User as display name if applicable
            this.chatAdminService.participantsLoaded$ = new ReplaySubject<boolean>(1);
            this.chatAdminService.participantsLoaded$.next(true);
          }
          this.syncing.set(false);
          await this.loadConversationFromDb(conversationId, loadAttempt);
          if (this.messages.length && !this.chatCloudLinkService.isOffline()) {
            this.syncing.set(!this.chatDatabaseService.synced[this.conversationId]);
            if (!this.syncing()) {
              this.onConversationLoadEnd.emit({
                conversationId: this.conversationId
              });
            }
            this.contentLoading.set(false);
            this.cdRef.markForCheck();
          }
        } catch (ex) {
          this.syncing.set(false);
          this.chatLoggingService.error({
            logMessage: 'chat.component.loadConversation(): Error loading conversation from db',
            logParameters: [ex]
          });
        }
      }
      if (
        this.isActive &&
        (!this.chatCloudLinkService.isOffline() || !this.indexDbEnabled()) &&
        !this.config?.onlyLoadFromDb
      ) {
        this.chatLoggingService.info({
          logMessage: 'chat.component.loadConversation() Getting conversation',
          logParameters: [this.conversationId, loadAttempt]
        });
        this.myParticipantLastReadLoaded$ = new ReplaySubject<boolean>(1);
        this.chatAdminService.participantsLoaded$ = new ReplaySubject<boolean>(1);

        this.loadUnsubscribe.next();
        this.loadUnsubscribe.complete();
        this.loadUnsubscribe = new Subject();

        defer(() => this.chatConversationService.getConversation(conversationId))
          .pipe(
            retryWhen(
              SharedHelpers.genericRetryStrategy(
                '[chat.component.loadConversation()] - get conversation ' + this.conversationId,
                this.chatLoggingService
              )
            ),
            takeUntil(this.loadUnsubscribe)
          )
          .toPromise()
          .then(
            async res => {
              const conversation = res?.body;
              if ((loadAttempt && loadAttempt !== this.conversationLoadAttempt) || !this.isActive || !conversation) {
                this.chatLoggingService.info({
                  logMessage: 'chat.component.loadConversation(): Ignoring load conversation, old request',
                  logParameters: [
                    this.conversationId,
                    loadAttempt,
                    this.conversationLoadAttempt,
                    !!this.isActive,
                    !!conversation
                  ]
                });
              } else if (conversation) {
                this.currentConversation = conversation;

                //Race condition, if messages load before conversation
                if (this.messages?.length) {
                  this.messages.forEach(m => this.setIsSMSMessage(m));
                }

                // TODO once SDK is updated with generateSystemMessages
                // if (this.config?.usePlatformAttachmentSystemMessages && !(<any>this.currentConversation).generateSystemMessages) {
                //   try {
                //     // await this.chatCloudLinkService.chat.updateConversation({
                //     //   conversationId: this.currentConversation.conversationId,
                //     //   body: {
                //     //     'generateSystemMessages': true
                //     //   }
                //     // })
                //     (<any>this.currentConversation).generateSystemMessages = true;
                //   } catch(ex) {
                //     this.chatLoggingService.error({
                //       logMessage: 'chat.component.loadConversation() Error updating generateSystemMessages to true',
                //       logParameters: [conversationId]
                //     });
                //   }
                // }

                if (conversation?._embedded?.participants?._embedded?.items?.length) {
                  conversation._embedded.participants._embedded.items.forEach(p => {
                    if (p?.userId && p?._embedded?.users?.name) {
                      this.chatAdminService.setCacheValues(p.userId, p._embedded.users.name, null, null);
                    }
                  });
                }
                if (conversation?._embedded?.participants?.total) {
                  this.participantTotal = conversation?._embedded?.participants?.total;
                  this.participantTotalEvent();
                }
                if (this.participantTotal > this.maxParticipantLoad) {
                  this.isLargeParticipantLoad = true;
                  this.chatConversationService.participantsUpdated$.next({
                    conversationId: this.conversationId,
                    largeLoad: true
                  });
                  await this.loadParticipantsFromConversation(loadAttempt, true);
                  this.chatAdminService.participantsLoaded$.next(true);
                } else {
                  await this.loadParticipantsFromConversation(loadAttempt, false, isReload);
                  this.chatAdminService.participantsLoaded$.next(true);
                }
                let matchingParticipant = this.participants.find(p => p.userId === this.chatAuthService.userId);
                if (
                  this.currentConversation.stream &&
                  this.config &&
                  this.config.autoJoinStream &&
                  !matchingParticipant
                ) {
                  try {
                    await this.join();
                    //Need to get the principal for logged in user here, because they won't have been in the conversation participants
                    //on load, so loadParticipantsFromConversation won't initialize the logged in user
                    var principal = await this.chatAdminService.getPrincipal(this.chatAuthService.userId);
                    this.cdRef.markForCheck();
                  } catch (ex) {
                    this.joinFailed$.next(true);
                    this.chatLoggingService.error({
                      logMessage: 'chat.component.loadConversation(): Join failed',
                      logParameters: [ex]
                    });
                  }
                } else if (this.config?.autoAcceptInvite && matchingParticipant?.status === 'INVITED') {
                  try {
                    await this.acceptInvite();
                  } catch (ex) {
                    this.joinFailed$.next(true);
                    this.chatLoggingService.error({
                      logMessage: 'chat.component.loadConversation(): Accept failed',
                      logParameters: [ex]
                    });
                  }
                } else if (this.config?.autoJoinStream || this.config?.autoAcceptInvite) {
                  this.joinFailed$.next(true);
                }
                this.cdRef.markForCheck();
              } else {
                this.chatLoggingService.error({
                  logMessage: 'chat.component.loadConversation() => could not load!',
                  logParameters: [conversationId]
                });
              }
            },
            ex => {
              this.joinFailed$.next(true);
              this.myParticipantLastReadLoaded$.next(false);
              this.chatAdminService.participantsLoaded$.next(false);
              if (ex?.body?.message.indexOf('User is not an accepted participant of the conversation') > -1) {
                this.chatLoggingService.messageError({
                  exceptionLogEvent: {
                    logMessage: 'chat.component.loadConversation(): Error not an accepted participant',
                    logParameters: [ex]
                  },
                  errorMessage: this.translate.instant('cl_chat.userIsNotAnAcceptedParticipant')
                });
              } else {
                this.chatLoggingService.messageError({
                  exceptionLogEvent: {
                    logMessage: 'chat.component.loadConversation(): Error during load',
                    logParameters: [ex]
                  },
                  errorMessage:
                    this.translate.instant('cl_chat.failedToLoadConversation') +
                    ' ' +
                    this.translate.instant('cl_chat.problemCommunicatingWithChat')
                });
              }
              this.onConversationLoadError.emit({
                conversationId: this.conversationId
              });
              if (ex?.statusCode === 404 || ex?.statusCode === 403) {
                this.onGoHome.emit();
              } else if (this.chatCloudLinkService.isOffline()) {
                this.chatLoggingService.error({
                  logMessage: 'chat.component.loadConversation(): Error offline'
                });
                this.textAreaDisabled = true;
                this.setOfflineSending();
              }
              this.cdRef.markForCheck();
            }
          );
        this.chatAdminService.getPrincipal(this.chatAuthService.userId);
        if (this.chatAdminService.usersLoaded$[this.chatAuthService.userId]) {
          this.userLoadedSub.unsubscribe();
          this.userLoadedSub = new Subscription();
          this.userLoadedSub.add(
            this.chatAdminService.usersLoaded$[this.chatAuthService.userId]
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(async () => {
                this.userLoadedSub.unsubscribe();
                let result;
                if (this.config?.snapshotMessageOnLoad) {
                  this.loadMessageSnapshot(this.config.snapshotMessageOnLoad);
                  this.config.snapshotMessageOnLoad = null;
                } else {
                  result = await this.fetchMessages(FetchType.Normal, {
                    loadAttempt: loadAttempt
                  });
                }
                try {
                  //Weird race conditions if you quick click between conversations, maybe rxjs would help solve these?
                  if (result === this.conversationId && !this.isSearch && !this.isSnapshot) {
                    await this.chatDatabaseService.addConversationToDb(
                      this.conversationId,
                      this.chatAuthService.accountId,
                      this.messages,
                      <any>this.lastReadMessageDate
                    );
                  }
                } catch (ex) {
                  this.chatLoggingService.error({
                    logMessage: 'chat.component.loadConversation(): Error adding conversation to db',
                    logParameters: [ex]
                  });
                }
              })
          );
        }
      } else if (this.isActive) {
        if (!this.config?.onlyLoadFromDb) {
          this.textAreaDisabled = true;
          this.setOfflineSending();
        }
        this.loading.set(false);
        this.syncing.set(false);
        this.cdRef.markForCheck();
        if (this.config?.onlyLoadFromDb) {
          this.chatLoggingService.info({
            logMessage: 'chat.component.loadConversation(): Only loading from db'
          });
        } else {
          this.chatLoggingService.info({
            logMessage: 'chat.component.loadConversation(): Offline, not attempting to load from cloud'
          });
        }
      } else {
        this.chatLoggingService.info({
          logMessage: 'chat.component.loadConversation(): Component destroyed'
        });
      }
    } else {
      this.chatLoggingService.info({
        logMessage: 'chat.component.loadConversation(): No conversation id',
        logParameters: [loadAttempt]
      });
    }
  }

  public clearError() {
    this.error.set('');
    this.cdRef.markForCheck();
  }

  public showInfo(info) {
    this.infoDialogText = info;
    if (this.infoDialogTimeout) {
      clearTimeout(this.infoDialogTimeout);
    }
    this.infoDialogTimeout = setTimeout(
      () => {
        this.infoDialogText = '';
        this.cdRef.markForCheck();
      },
      this.config && this.config.dialogVisibleTimeout ? this.config.dialogVisibleTimeout : 5000
    );
    this.cdRef.markForCheck();
  }

  public showWarning(warning) {
    this.warningDialogText = warning;
    if (this.warningDialogTimeout) {
      clearTimeout(this.warningDialogTimeout);
    }
    this.warningDialogTimeout = setTimeout(
      () => {
        this.warningDialogText = '';
        this.cdRef.markForCheck();
      },
      this.config && this.config.dialogVisibleTimeout ? this.config.dialogVisibleTimeout : 5000
    );
    this.cdRef.markForCheck();
  }

  public retrySubscriptions() {
    this.chatLoggingService.info({
      logMessage: 'chat.component.retrySubscriptions()'
    });
    this.subscriptionError.set(false);
    this.cdRef.markForCheck();
    this.resubscribeChatSubscriptions(false);
  }

  private async loadParticipantsFromDatabase(loadAttempt: number) {
    let participants = await this.chatDatabaseService.getParticipants(
      this.chatAuthService.accountId,
      this.conversationId
    );
    if ((loadAttempt && loadAttempt !== this.conversationLoadAttempt) || !this.isActive) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.loadParticipantsFromDatabase(): Ignoring load participants, old request'
      });
      return;
    }
    if (participants?.length) {
      this.participants = participants.map(p => {
        let participant: Participant = {
          userId: p.principalId,
          accountId: p.accountId,
          conversationId: p.conversationId,
          lastReadMessageDate: <any>p.lastReadMessageDate,
          createdBy: null,
          createdOn: null,
          modifiedBy: null,
          modifiedOn: null,
          status: null,
          participantType: null,
          _links: {
            self: null
          },
          _embedded: {}
        };
        return participant;
      });
      this.messages.forEach(m => this.updateMessageReadIndicator(m));
      this.updateLastReadMessageToCheck();
      this.cdRef.markForCheck();
    }
  }

  private async loadParticipantsFromConversation(loadAttempt: number, largeLoad = false, reload = false) {
    let ps = getItemsFromCollection(this.currentConversation?._embedded?.participants);

    if (ps.length === 0) {
      this.chatLoggingService.info({
        logMessage:
          'chat.component.loadParticipantsFromConversation() Zero participants found, attempting participants get again'
      });
      await new Promise((resolve, reject) => {
        setTimeout(() => resolve(true), 2000);
      });
      const resCheckParticipants = await defer(() =>
        this.chatCloudLinkService.chat.getParticipants({
          conversationId: this.conversationId,
          skip: 0,
          top: 100
        })
      )
        .pipe(
          retryWhen(
            SharedHelpers.genericRetryStrategy(
              '[chat.component.loadParticipantsFromConversation()] - ' + this.conversationId,
              this.chatLoggingService
            )
          ),
          takeUntil(this.loadUnsubscribe)
        )
        .toPromise();
      let checkParticipants = resCheckParticipants.body;
      ps = getItemsFromCollection(checkParticipants);
    }

    if ((loadAttempt && loadAttempt !== this.conversationLoadAttempt) || !this.isActive) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.loadParticipantsFromConversation(): Ignoring load participants (1), old request'
      });
      return;
    }

    if (!largeLoad) {
      var next = this.currentConversation._embedded.participants._links.next;
      if (next) {
        ps = [];
        const resNewParticipants = await defer(() =>
          this.chatCloudLinkService.chat.getParticipants({
            conversationId: this.conversationId,
            skip: 0,
            top: 100
          })
        )
          .pipe(
            retryWhen(
              SharedHelpers.genericRetryStrategy(
                '[chat.component.loadParticipantsFromConversation()] get next - ' + this.conversationId,
                this.chatLoggingService
              )
            ),
            takeUntil(this.loadUnsubscribe)
          )
          .toPromise();
        if ((loadAttempt && loadAttempt !== this.conversationLoadAttempt) || !this.isActive) {
          this.chatLoggingService.info({
            logMessage: 'chat.component.loadParticipantsFromConversation(): Ignoring load participants (2), old request'
          });
          return;
        }
        let newParticipants = resNewParticipants.body;
        ps = getItemsFromCollection(newParticipants);
        next = newParticipants._links.next;
        while (next) {
          const queryString: any = SharedHelpers.parseQueryString(next);
          const res = await this.chatCloudLinkService.chat.getParticipants({
            conversationId: this.conversationId,
            skipToken: queryString.$skipToken,
            top: queryString.$top
          });
          newParticipants = res.body;
          ps.push(...getItemsFromCollection(newParticipants));
          next = newParticipants._links.next;
        }
      }

      var userChunks = chunk(lodashMap(ps, 'userId'), 40);

      for (const c of userChunks) {
        await this.chatAdminService.getPrincipals(c, false, reload);
        this.chatItems.forEach(c => {
          c.markForCheck();
          if (c.isSystemMessage) {
            c.parseMessage();
          }
        });
        this.cdRef.markForCheck();
      }

      if ((loadAttempt && loadAttempt !== this.conversationLoadAttempt) || !this.isActive) {
        this.chatLoggingService.info({
          logMessage: 'chat.component.loadParticipantsFromConversation(): Ignoring load participants (3), old request'
        });
        return;
      }
    } else {
      let match = this.participants.find(p => p.userId === this.chatAuthService.userId);
      if (!match) {
        try {
          const resGetMyParticipant = await defer(() =>
            this.chatCloudLinkService.chat.getParticipant({
              conversationId: this.conversationId,
              participantId: this.chatAuthService.userId
            })
          )
            .pipe(
              retryWhen(
                SharedHelpers.genericRetryStrategy(
                  '[chat.component.loadParticipantsFromConversation()] get large load me - ' + this.conversationId,
                  this.chatLoggingService
                )
              ),
              takeUntil(this.loadUnsubscribe)
            )
            .toPromise();
          let getMyParticipant = resGetMyParticipant.body;
          if ((loadAttempt && loadAttempt !== this.conversationLoadAttempt) || !this.isActive) {
            this.chatLoggingService.info({
              logMessage:
                'chat.component.loadParticipantsFromConversation(): Ignoring load participants (4), old request'
            });
            return;
          }
          ps.push(getMyParticipant);
        } catch (ex) {
          if (ex?.statusCode === 403) {
            this.chatLoggingService.info({
              logMessage: 'chat.component.loadParticipantsFromConversation(): User not a participant',
              logParameters: [this.conversationId]
            });
          } else {
            this.chatLoggingService.error({
              logMessage: 'chat.component.loadParticipantsFromConversation(): Error getting user',
              logParameters: [ex]
            });
          }
        }
      }
    }

    var myParticipant: Participant | undefined = ps.find(p => p.userId === this.chatAuthService.userId);
    if (myParticipant) {
      if (myParticipant.lastReadMessageDate && myParticipant.lastReadMessageDate !== this.lastReadMessageDate) {
        if (
          !this.lastReadMessageDate ||
          new Date(this.lastReadMessageDate) < new Date(myParticipant.lastReadMessageDate)
        ) {
          this.setAndStoreLastReadMessageDate(myParticipant.lastReadMessageDate, myParticipant.conversationId);
        } else if (
          new Date(myParticipant.lastReadMessageDate).getTime() === new Date('0001-01-01T00:00:00Z').getTime()
        ) {
          //If host app marked conversation as unread
          this.setAndStoreLastReadMessageDate(<any>new Date('0001-01-01T00:00:00Z').toISOString(), this.conversationId);
          this.updateHasUnreadMessage(myParticipant.conversationId);
        } else if (new Date(myParticipant.lastReadMessageDate) < new Date(this.dbLastReadMessageDate)) {
          //Shouldn't happen...
          this.setAndStoreLastReadMessageDate(myParticipant.lastReadMessageDate, myParticipant.conversationId);
          this.updateHasUnreadMessage(myParticipant.conversationId);
        }
        if (
          this.dbLastReadMessageDate &&
          new Date(myParticipant.lastReadMessageDate) > new Date(this.dbLastReadMessageDate)
        ) {
          this.clearFirstUnread(myParticipant.lastReadMessageDate);
        }
      } else if (this.dbLastReadMessageDate && !myParticipant.lastReadMessageDate) {
        //Shouldn't happen... somehow got deleted
        this.setAndStoreLastReadMessageDate(<any>new Date('0001-01-01T00:00:00Z').toISOString(), this.conversationId);
        this.updateHasUnreadMessage(this.conversationId);
      }
    }
    this.myParticipantLastReadLoaded$.next(true);

    ps.forEach(p => {
      this.chatDatabaseService.addParticipant(
        p.accountId,
        p.conversationId,
        p.userId,
        p.lastReadMessageDate?.toString(),
        p.participantType
      );
    });

    this.participants = ps;
    this.participants.sort(this.participantSort);
    this.chatLoggingService.info({
      logMessage: 'chat.component.loadParticipantsFromConversation()',
      logParameters: [this.conversationId, this.participants.length]
    });
    this.participantEvent();
    this.messages.forEach(m => this.updateMessageReadIndicator(m));
    this.cdRef.markForCheck();
    if (this.scrollingService.isNearBottom()) {
      this.scrollingService.scrollToBottom();
    }
  }

  private async onParticipantRemoved(deletedParticipant: DeletedParticipant) {
    try {
      if (this.participants.length && deletedParticipant.conversationId === this.conversationId) {
        var matchingParticipant = this.participants.find(p => p.userId === deletedParticipant.userId);
        if (matchingParticipant) {
          if (matchingParticipant.userId === this.chatAuthService.userId) {
            // Same staleness risk as onConversationRemoved() - deleting a conversation also
            // removes the deleting user as a participant. See confirmConversationGone()'s
            // comment for why this needs both a server re-check and a retry, not just a
            // conversationId match.
            if (!(await this.confirmConversationGone(deletedParticipant.conversationId))) {
              return;
            }
            this.chatLoggingService.info({
              logMessage: 'chat.component.onParticipantRemoved(): You were removed',
              logParameters: [deletedParticipant.conversationId]
            });
            this.setReadOnly('onParticipantRemoved');
            remove(this.participants, (p: Participant) => p.userId === deletedParticipant.userId);
            this.participantEvent();
            this.cdRef.markForCheck();
          } else {
            this.chatLoggingService.info({
              logMessage: 'chat.component.onParticipantRemoved(): User was removed',
              logParameters: [deletedParticipant.userId, deletedParticipant.conversationId]
            });
            remove(this.participants, (p: Participant) => p.userId === deletedParticipant.userId);
            this.updateParticipantReadIndicator({
              userId: deletedParticipant.userId,
              accountId: this.chatAuthService.accountId,
              conversationId: deletedParticipant.conversationId,
              lastReadMessageDate: null,
              createdBy: null,
              createdOn: null,
              modifiedBy: null,
              modifiedOn: null,
              status: null,
              participantType: null,
              _links: {
                self: null
              },
              _embedded: {}
            });
            this.participantEvent();
            this.cdRef.markForCheck();
          }
        }
        if (this.isLargeParticipantLoad) {
          this.participantTotal--;
          this.participantTotalEvent();
        }
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.onParticipantRemoved(): Error',
        logParameters: [ex]
      });
    }
  }

  private async onParticipantChanged(participant: ParticipantEx) {
    try {
      if (this.isLargeParticipantLoad && participant.method === 'POST') {
        this.participantTotal++;
        this.participantTotalEvent();
      }
      if (participant.status === 'BLOCKED' && participant.userId === this.chatAuthService.userId) {
        this.chatLoggingService.info({
          logMessage: 'chat.component.onParticipantChanged(): User was blocked'
        });
        this.setReadOnly('onParticipantChanged-blocked');
      }
      const i: number = findIndex(
        this.participants,
        (listParticipant: Participant) => listParticipant.userId === participant.userId
      );
      if (i >= 0) {
        Object.assign(this.participants[i], participant);
      } else {
        this.chatLoggingService.info({
          logMessage: 'chat.component.onParticipantChanged(): New participant',
          logParameters: [participant.userId]
        });
        if (
          participant.method === 'POST' &&
          participant.status === 'ACCEPTED' &&
          participant.userId === this.chatAuthService.userId &&
          !this.firstLoad
        ) {
          this.chatLoggingService.info({
            logMessage: 'chat.component.onParticipantChanged(): User was set back to ACCEPTED, reloading'
          });
          this.reloadConversation();
          this.chatConversationService.chatInfoSetupContent$.next();
        }
        this.participants.push(participant);
        this.participants.sort(this.participantSort);
        this.chatAdminService.getPrincipal(participant.userId);
        if (this.chatAdminService.usersLoaded$[participant.userId]) {
          await this.chatAdminService.usersLoaded$[participant.userId]
            .pipe(take(1), takeUntil(this.ngUnsubscribe))
            .toPromise();
        }
        if (participant.conversationId === this.conversationId) {
          this.participantEvent();
          this.cdRef.markForCheck();
        }
      }
      this.updateParticipantReadIndicator(participant);
      this.cdRef.markForCheck();
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.onParticipantChanged(): Error',
        logParameters: [ex]
      });
    }
  }

  private participantSort(a: Participant, b: Participant): number {
    const status: (status: Participant.StatusEnum) => number = (status: Participant.StatusEnum) =>
      status === 'ACCEPTED' ? 0 : status === 'INVITED' ? 1 : 2;
    if (status(a.status) > status(b.status)) {
      return 1;
    } else if (status(b.status) > status(a.status)) {
      return -1;
    } else if (a.userId > b.userId) {
      return 1;
    } else if (b.userId > a.userId) {
      return -1;
    } else {
      return 1;
    }
  }

  delete() {
    this.chatConversationService.deleteStream(this.conversationId).then(() => {
      this.chatLoggingService.info({
        logMessage: 'chat.component.delete(): Stream deleted'
      });
      this.setReadOnly('delete-stream');
      this.onGoHome.emit();
    });
  }

  downloadFullScreen(): void {
    if (this.downloadAttachmentOverride) {
      this.downloadAttachmentOverride({
        conversationId: this.currentConversation.conversationId,
        attachmentId: this.fullScreenId,
        name: this.fullScreenName
      });
    } else {
      this.fullScreenDownload.nativeElement.click();
    }
  }

  private mobileTextFocus() {
    //Need to focus on a different input for iOS 13+, using blur/focus on the same element doesn't work
    if (
      this.chatCloudLinkService.iOSVersion &&
      this.chatCloudLinkService.iOSVersion.length &&
      this.chatCloudLinkService.iOSVersion[0] > 12
    ) {
      this.iphoneInput.nativeElement.focus();
    } else {
      this.textArea.nativeElement.blur();
    }
    this.textArea.nativeElement.focus();
  }

  public sendButtonClicked() {
    var sendButtonDelay = 0;
    //Needed for iOS to clear their autocomplete buffer
    if (
      this.chatCloudLinkService.browser.os.toLowerCase() === 'ios' &&
      document.activeElement === this.textArea.nativeElement
    ) {
      if (this.chatCloudLinkService.browser.os.toLowerCase() === 'ios') {
        this.clearJustSentButton();
        this.justSentButton = true;
        this.justSentButtonTimeout = setTimeout(() => {
          this.clearJustSentButton();
        }, 500);
      }
      this.mobileTextFocus();
      //Need this timeout to let the keyboard disappear/reappear on iOS webview :(
      if (this.chatCloudLinkService.browser.name === 'ios-webview') {
        sendButtonDelay = 200;
      }
    }
    setTimeout(() => {
      this.chatLoggingService.debug({
        logMessage: 'chat.component.sendButtonClicked() scroll and client heights',
        logParameters: [this.maincontent.nativeElement.scrollHeight, this.maincontent.nativeElement.clientHeight]
      });
      this.closeTextInputDropdowns();
      this.sendTypedMessage();
    }, sendButtonDelay);
  }

  public restoreTextAreaFocus() {
    MentionHelpers.restoreTextAreaFocus(this.textArea);
  }

  public closeTextInputDropdowns() {
    if (this.commandsDropdown) {
      this.commandsDropdown.hide();
    }
    if (this.mentionsDropdown) {
      this.mentionsDropdown.hide();
    }
  }

  private createAttachmentGroup(): string {
    let attachmentGroup = uuid();
    if (!(<any>this.currentConversation).generateSystemMessages) {
      this.chatConversationService.pendingAttachmentMessages[attachmentGroup] = [];
    }
    return attachmentGroup;
  }

  public async sendTypedMessage() {
    this.chatLoggingService.debug({
      logMessage: 'chat.component.sendTypedMessage()'
    });

    //Close mention tooltip so it's not sent
    MentionHelpers.removeTooltip();
    if (this.emojisDropdown?.overlayVisible) {
      this.emojisDropdown.hide();
    }

    if (this.sendOverride && (this.message.trim() !== '' || this.textAreaHasTwemoji)) {
      this.message = await this.sendOverride(this.message);
    }
    // OneDrive files leave the attachment flow: the caption goes now (with any regular attachments), each link once
    // its upload finishes
    const oneDriveAttachments = this.pendingAttachments.filter(a => a.oneDrive);
    if (oneDriveAttachments.length) {
      oneDriveAttachments.forEach(a => {
        this.clearCopiedAttachment(a);
        this.shareViaOneDrive(a.file);
      });
    }
    if (this.pendingAttachments.length) {
      this.message = this.message.trimEnd();
      let attachmentGroup = this.createAttachmentGroup();
      if (!(<any>this.currentConversation).generateSystemMessages) {
        if (this.message.trim() !== '') {
          this.convertMentionsToText();
          let attachmentMessage = this.parseMessage(this.message);
          this.clearInput();
          this.chatConversationService.pendingAttachmentMessages[attachmentGroup].push(attachmentMessage);
        }
      } else if (this.message.trim() !== '' || this.textAreaHasTwemoji) {
        this.convertMentionsToText();
        this.convertEmojiSvgToText();
        this.send(this.message);
        this.clearInput();
      }
      this.sendAttachments(attachmentGroup);
    }

    if (this.currentRecording) {
      this.sendRecording();
    }
    //Don't send empty messages
    if (this.message.trim() === '' && !this.textAreaHasTwemoji) {
      return;
    }

    var foundDebugCommand = false;
    if (this.message === '\\cl-debug-flag\\') {
      this.chatLoggingService.isDebug = true;
      this.chatLoggingService.logLevel = 'debug';
      foundDebugCommand = true;
    } else if (this.message === '\\cl-errors\\') {
      this.chatLoggingService.recentErrors.forEach(e => {
        var errorMessage = e.logMessage;
        if (e.logParameters) {
          e.logParameters.forEach(e => {
            errorMessage += ' ' + e.toString();
          });
        }
        this.sendLocalError(errorMessage);
      });
      foundDebugCommand = true;
    }

    if (foundDebugCommand) {
      this.clearInput();
      return;
    }

    this.chatLoggingService.debug({
      logMessage: 'chat.component.sendTypedMessage() message sending'
    });

    this.convertMentionsToText();
    this.convertEmojiSvgToText();
    this.send(this.message);
    this.cdRef.markForCheck();
  }

  private convertEmojiSvgToText(setMessage = true): string {
    return MentionHelpers.convertEmojisToText(
      this.textArea,
      this.chatLoggingService,
      'chat.component',
      setMessage ? this.setMessage.bind(this) : null
    );
  }

  private convertMentionsToText(setMessage = true): string {
    return MentionHelpers.convertMentionsToText(
      this.textArea,
      this.chatLoggingService,
      'chat.component',
      setMessage ? this.setMessage.bind(this) : null
    );
  }

  private async sendAttachments(attachmentGroup: string) {
    if (this.pendingAttachments.length) {
      this.chatLoggingService.debug({
        logMessage: 'chat.component.sendAttachments()',
        logParameters: [
          this.pendingAttachments.length,
          this.conversationId,
          (<any>this.currentConversation).generateSystemMessages
        ]
      });
      this.pendingAttachments.forEach(p => {
        this.uploadFile(p.file, attachmentGroup);
      });
      this.clearCopiedAttachments();
    }
  }

  public async send(body: string, fallbackText: string = '', throwError: boolean = false) {
    if (!this.conversationId) {
      throw 'chat.component.send : Unable to send message since chat is not yet loaded';
    }

    if (!body) {
      body = this.message;
    }

    body = MessageUtils.removeTrailingNewLine(body);

    if (body.length === 0) return;

    this.chatLoggingService.debug({
      logMessage: 'chat.component.send() scroll and client heights ',
      logParameters: [this.maincontent.nativeElement.scrollHeight, this.maincontent.nativeElement.clientHeight]
    });

    const isCommand = !this.chatCloudLinkService.isOffline() && this.isCommand(body);
    let sentCommand: CommandResultType = CommandResultType.NoMatch;
    if (isCommand) {
      this.clearInput();

      setTimeout(() => {
        this.scrollingService.scrollToBottom();
      }, 0);

      sentCommand = await this.sendCommand(body);
      if (sentCommand === CommandResultType.Failure) {
        this.sendLocalError(this.translate.instant('cl_chat.systemCommands.failure'));
      }
    }

    if (sentCommand === CommandResultType.NoMatch) {
      let msg = this.parseMessage(body, fallbackText);
      await this.sendMessage(msg, throwError);
    }
  }

  private parseMessage(body, fallbackText: string = ''): MessageEx {
    body = this.replaceEmojiOnSend(body);
    let now = new Date();
    let msg: MessageEx;
    if (this.quotedMessage) {
      msg = this.parseQuotedMessage(body, fallbackText);
    } else {
      msg = {
        conversationId: this.conversationId,
        accountId: this.chatAuthService.accountId,
        messageId: this.getLocalMessageId(),
        body: body,
        contentType: ContentTypes.TEXT,
        createdOn: now,
        createdBy: this.chatAuthService.userId,
        modifiedOn: now,
        modifiedBy: this.chatAuthService.userId,
        _links: {}
      };
    }
    return msg;
  }

  private parseQuotedMessage(body?, fallbackText: string = ''): MessageEx {
    let now = new Date();
    let msg: MessageEx;

    let quotedMessageCopy = Object.assign({}, this.quotedMessage);
    let textToTakeLength =
      ChatMessageLimits.MaxQuotedMessageReply +
      ChatMessageLimits.MaxQuotedMessageCite -
      SharedHelpers.getQuotedBodyLength(this.convertMentionsToText(false));
    let bodyToTake = SharedHelpers.shortenQuoteMessageCiteBody(textToTakeLength, this.quotedMessage.body);

    //Shorten original message
    if (bodyToTake) {
      quotedMessageCopy.body = bodyToTake;
    }

    var quoteBody;
    if (body) {
      quoteBody = {
        cite: quotedMessageCopy,
        content: {
          body: body,
          contentType: ContentTypes.TEXT,
          fallbackText
        }
      };
    } else {
      quoteBody = {
        cite: quotedMessageCopy,
        content: {
          contentType: ContentTypes.TEXT
        }
      };
    }

    msg = {
      conversationId: this.conversationId,
      accountId: this.chatAuthService.accountId,
      messageId: this.getLocalMessageId(),
      body: JSON.stringify(quoteBody),
      fallbackText: body
        ? '"' + quoteBody.cite.body + '": ' + quoteBody.content.body
        : '"' + quoteBody.cite.body + '": ',
      contentType: ContentTypes.QUOTED,
      createdOn: now,
      createdBy: this.chatAuthService.userId,
      modifiedOn: now,
      modifiedBy: this.chatAuthService.userId,
      _links: {}
    };
    this.undoQuotedMessage();
    return msg;
  }

  public sendLocalError(errorMessage: string) {
    let now = new Date();
    let msg: MessageEx;
    msg = {
      conversationId: this.conversationId,
      accountId: this.chatAuthService.accountId,
      messageId: 'commanderror',
      body: MessageUtils.errorString + errorMessage,
      contentType: ContentTypes.WHISPER,
      createdOn: now,
      createdBy: this.chatAuthService.userId,
      modifiedOn: now,
      modifiedBy: this.chatAuthService.userId,
      _links: {}
    };
    this.messages.push(msg);
    this.cdRef.markForCheck();
    if (this.scrollingService.isNearBottom()) {
      this.scrollingService.scrollToBottom();
    }
  }

  private setMostRecentSent(message: MessageEx) {
    const x: number = findIndex(this.messages, (listMessage: MessageEx) => listMessage.clCompChat_mostRecentSent);
    if (x > -1) {
      this.messages[x].clCompChat_mostRecentSent = false;
      var chatItem = this.chatItems.find(c => c.item().messageId === this.messages[x].messageId);
      if (chatItem) {
        chatItem.detectChanges();
      }
    }
    message.clCompChat_mostRecentSent = true;
  }

  private async sendMessage(msg: MessageEx, throwError: boolean = false, clearInput: boolean = true): Promise<Message> {
    if (!this.conversationId) {
      throw 'chat.component.sendMessage(): Unable to send message since chat is not yet loaded';
    }

    // add the message locally
    msg.clCompChat_state = 'sending';

    if (!this.aiMode && this.chatCloudLinkService.isOffline() && this.indexDbEnabled() && this.offlineSendingEnabled) {
      msg.clCompChat_state = 'pending';
    }

    this.setMostRecentSent(msg);
    var sendGuid = uuid();
    msg.clCompChat_sendId = sendGuid;
    msg.tag = JSON.stringify({
      sendId: sendGuid
    });
    this._lastMessageSend = {
      sendId: sendGuid,
      createdOn: msg.createdOn
    };
    this.updateLastReadMessageDate(msg, false);
    this.setAttachmentType(msg);
    this.updateMessageReadIndicator(msg);

    this.goToLatest();

    this.onMessageSendAttempt.emit(msg);
    this.messages.push(msg);
    this.updateLastReadMessageToCheck();
    this.updateStartOfDayForMessages();
    if (clearInput) {
      this.clearInput();
    }
    setTimeout(() => {
      //Detect changes must be in the timeout here so it doesn't affect the msg being used
      //in the body of the POST request by calling parseMessage in chat-item before the send
      //10/17/2019: ^ this might be obsolete now with the new display body variable, investigate later
      this.cdRef.markForCheck();
      this.scrollingService.scrollToBottom();
    }, 0);

    if (this.aiMode) {
      // Optimistic - v1 doesn't echo the user's own turn back, so there's nothing to reconcile
      // msg against. Captured up front (not read lazily off `this` later) so a conversationId
      // switch while this call is still in flight can't relabel its reply onto the new
      // conversation - onMessageReceived's own conversationId filter then does the right thing
      // and drops a late reply for a conversation the user has since navigated away from.
      const aiConversationId = this.conversationId;
      // Same idea for startNewAiSession(), which keeps conversationId - see aiSessionGeneration.
      const aiSessionGeneration = this.aiSessionGeneration;
      const isCurrentAiSession = () => aiSessionGeneration === this.aiSessionGeneration;
      msg.clCompChat_state = 'sent';
      var chatItem = this.chatItems.find(c => c.item().messageId === msg.messageId);
      if (chatItem) {
        chatItem.detectChanges();
      }
      const assistantUserId = this.aiConfig?.assistantUserId ?? 'ai-assistant';
      if (this.remoteTyping.indexOf(assistantUserId) === -1) {
        this.remoteTyping.push(assistantUserId);
        this.cdRef.markForCheck();
      }
      // Client-side round-trip timer backing clCompChat_stats.latencyMs - MAIS doesn't report
      // its own latency (metrics.app_overhead_ms is observed always null), so this is wall-clock
      // send-to-close time instead, network included.
      const aiSendStartedAt = Date.now();
      const pendingIds = new Set<string>();
      let chunkRouter: { chunks$: Subject<MaisStreamChunk>; subscription: Subscription } | undefined;
      try {
        if (this.aiConfig?.streaming === false) {
          const reply = await firstValueFrom(this.chatMaisService.chat(this.aiConfig, msg.body));
          if (!isCurrentAiSession()) {
            this.onMessageSendSuccess.emit(msg);
            return msg;
          }
          this.clearRemoteTyping(assistantUserId);
          const replyParts = this.splitAiReply(reply.textResponse ?? '', true);
          const replyMessage = this.buildAiAssistantMessage(
            reply.id,
            replyParts.body,
            new Date(),
            aiConversationId,
            assistantUserId,
            { ...reply, close: true },
            undefined,
            undefined,
            undefined,
            aiSendStartedAt
          );
          replyMessage.clCompChat_quickReplies = replyParts.quickReplies;
          this.onMessageReceived(replyMessage);
          if (reply.sources?.length) {
            this.onAiSourcesReceived.emit({ messageId: reply.id, sources: reply.sources });
          }
        } else {
          chunkRouter = this.subscribeToAiChunks(
            aiConversationId,
            assistantUserId,
            pendingIds,
            aiSendStartedAt,
            aiSessionGeneration
          );
          await this.chatMaisService.streamChat(this.aiConfig, msg.body, undefined, chunk =>
            chunkRouter.chunks$.next(chunk)
          );
        }
        this.onMessageSendSuccess.emit(msg);
        return msg;
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.component.sendMessage(): Error sending AI message',
          logParameters: [this.conversationId, ex]
        });
        // An old session's failure must not clear the new session's typing indicator or drop
        // its partial reply into the new chat - startNewAiSession() already cleaned both up.
        if (isCurrentAiSession()) {
          this.clearRemoteTyping(assistantUserId);
          this.failPendingAiMessages(pendingIds, aiConversationId, assistantUserId);
        }
        msg.clCompChat_state = 'failed';
        var aiChatItem = this.chatItems.find(c => c.item().messageId === msg.messageId);
        if (aiChatItem) {
          aiChatItem.detectChanges();
        }
        this.onMessageSendFail.emit(msg);
        this.onAiStreamError.emit({ messageId: msg.messageId, error: String(ex) });
        if (throwError) {
          throw ex;
        }
        return msg;
      } finally {
        chunkRouter?.chunks$.complete();
        chunkRouter?.subscription.unsubscribe();
      }
    } else if (this.chatCloudLinkService.isOffline() && this.indexDbEnabled() && this.offlineSendingEnabled) {
      try {
        msg.createdOn = <any>new Date(msg.createdOn).toISOString();
        msg.modifiedOn = <any>new Date(msg.createdOn).toISOString();
        await this.chatDatabaseService.addMessageIfConversationExists(msg);
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.component.send(): Error adding pending send message to db',
          logParameters: [msg.conversationId, ex]
        });
        msg.clCompChat_state = 'failed';
      }
      var chatItem = this.chatItems.find(c => c.item().messageId === msg.messageId);
      if (chatItem) {
        chatItem.detectChanges();
      }
    } else {
      try {
        var resp = await this.chatCloudLinkService.chat.createMessage({
          conversationId: this.conversationId,
          body: {
            body: msg.body,
            contentType: msg.contentType,
            fallbackText: msg.fallbackText,
            tag: JSON.stringify({
              sendId: sendGuid
            })
          }
        });
        let msgEx: MessageEx = resp.body as MessageEx;
        try {
          await this.chatDatabaseService.addMessageIfConversationExists(msgEx);
        } catch (ex) {
          this.chatLoggingService.error({
            logMessage: 'chat.component.send(): Error adding message to db',
            logParameters: [msgEx.conversationId, ex]
          });
        }

        msgEx.clCompChat_state = 'sent';
        this.setAttachmentType(msgEx);
        this.updateMessageReadIndicator(msgEx);
        //Fix race condition in case we get notification first
        if (msg.messageId.indexOf('local') > -1) {
          let diffBody = msg.body !== msgEx.body;
          Object.assign(msg, msgEx);
          if (diffBody) {
            let chatItem = this.chatItems.find(c => c.item().messageId === msg.messageId);
            if (chatItem) {
              chatItem.parseMessage();
            }
          }
        }
        this.messages = this.messages.sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
        this.updateLastReadMessageToCheck();
        this.cdRef.markForCheck();
        var chatItem = this.chatItems.find(c => c.item().messageId === msgEx.messageId);
        if (chatItem) {
          chatItem.detectChanges();
        }
        this.onMessageSendSuccess.emit(msg);
        return msgEx;
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.component.send(): Error',
          logParameters: [ex]
        });
        msg.clCompChat_state = 'failed';
        msg.createdOn = <any>new Date(msg.createdOn).toISOString();
        msg.modifiedOn = <any>new Date(msg.createdOn).toISOString();
        this.chatDatabaseService.addMessageIfConversationExists(msg);
        this.cdRef.markForCheck();
        var chatItem = this.chatItems.find(c => c.item().messageId === msg.messageId);
        if (chatItem) {
          chatItem.detectChanges();
        }
        this.onMessageSendFail.emit(msg);
        if (throwError) {
          throw ex;
        }
        return msg;
      }
    }
  }

  private isCommand(message: string) {
    return message.indexOf('/') === 0;
  }

  async enterCommand(cmd: Command): Promise<void> {
    let command: string = `/${cmd.commandId}`;

    for (const param of cmd.parameters) {
      if (!param['hidden'] && param.required) {
        command += ` ${param.parameterId}`;
      }
    }

    this.message = command;
    this.textArea.nativeElement.innerText = command;
    this.setCaret(false);
    if (this.commandsDropdown) {
      this.commandsDropdown.hide();
    }
    if (this.isMobile) {
      this.mobileTextFocus();
    }
  }

  async sendCommand(message: string, retry401: boolean = true): Promise<CommandResultType> {
    this.textAreaProps.commandPending = false;
    if (this.commandsDropdown) {
      this.commandsDropdown.hide();
    }

    let result: CommandResultType = CommandResultType.Failure;
    // look for standard command systax
    const cmdRe = /\/[\w+\-]*/;
    const cmdMatch = message.match(cmdRe);
    // double check that this is a command
    if (cmdMatch && cmdMatch.length > 0) {
      let commandId: string = cmdMatch[0];
      commandId = commandId.replace('/', '');

      try {
        //Disable hidden commands
        if (
          !commandId ||
          (this.config?.hiddenCommands && this.config?.hiddenCommands.find(c => c === commandId)) ||
          this.hiddenCommands.find(c => c === commandId)
        ) {
          return CommandResultType.NoMatch;
        }

        const resCommand = await this.chatCloudLinkService.chat.getCommand({
          commandId: commandId
        });
        const command = resCommand.body;
        this.updateCommandParams(command);
        // first setup the main data structure request
        const paramBody = {};
        for (const param of command.parameters) {
          if (param.parameterId.toLowerCase() === 'conversationid') {
            param['value'] = this.conversationId;
          }

          paramBody[param.parameterId] = param['value'];
        }

        // now we need to parse the  args if they exist on the commandline
        const paramRe = /\s(.*)/;
        const paramMatch = message.match(paramRe);

        if (paramMatch && paramMatch.length > 0) {
          let paramsRaw = paramMatch[0];
          paramsRaw = paramsRaw.trim();
          // convert string into arg chunks
          const args = this.commandArgs2Array(paramsRaw);

          // args are plain values and separated by spaces.. but quotes maybe involved
          for (let argIndex = 0; argIndex < args.length; argIndex++) {
            // handle quotes
            let arg = args[argIndex];
            if (arg && arg.indexOf('"') >= 0) {
              arg = arg.replace(/^"(.*)"$/, '$1');
            }
            //replace @ if first character for mentions
            arg = arg.replace(/^@/, '');

            // so this is the value of the x NOT hidden arg.. we need to back-track and fill in values if supplied
            let argCount: number = argIndex + 1;
            let paramCounter: number = 0;
            for (let paramIndex = 0; paramIndex < command.parameters.length; paramIndex++) {
              const param = command.parameters[paramIndex];
              if (!param['hidden']) {
                paramCounter += 1;
                if (argCount === paramCounter) {
                  paramBody[param.parameterId] = arg;
                }
              }
            }
          }
        }

        if (command.referenceCommandId === 'workflow') {
          // TEMP WORKAROUND as there is a bug on the platform
          try {
            await this.runWorkflowCommand(command, paramBody);
            result = CommandResultType.Success;
          } catch (ex) {
            if (ex.status === 202) {
              this.chatLoggingService.info({
                logMessage: 'chat.component.sendCommand()..sent workflow command',
                logParameters: [commandId]
              });
              result = CommandResultType.Success;
            } else if (ex.status == 401 && retry401 === true) {
              this.chatLoggingService.error({
                logMessage: 'chat.component.sendCommand(): Error 401 (attempt refresh) sending workflow command',
                logParameters: [commandId, paramBody, ex]
              });
              return this.chatAuthService.refreshToken().then(() => {
                return this.sendCommand(message, false);
              });
            } else {
              this.chatLoggingService.error({
                logMessage: 'chat.component.sendCommand(): Error sending workflow command',
                logParameters: [commandId, paramBody, ex]
              });
            }
          }
        } else {
          try {
            this.chatLoggingService.info({
              logMessage: 'chat.component.sendCommand()..sending command w/ body and token',
              logParameters: [commandId]
            });

            let url = SharedHelpers.createServiceUrl({
              service: 'chat',
              version: '2017-09-01'
            });
            url = `${url}/commands/${commandId}`;
            await this.httpClient
              .post(url, paramBody, {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: this.chatAuthService.token.access_token
                }
              })
              .toPromise();
            this.chatLoggingService.info({
              logMessage: 'chat.component.sendCommand()..sent',
              logParameters: [commandId]
            });
            // NO idea why some commands were giving 401.. SDK issue???
            //await this.chatCloudLinkService.chat.executeCommand({ commandId: commandId, body: paramBody });
            result = CommandResultType.Success;
          } catch (ex) {
            if (ex.status === 202) {
              this.chatLoggingService.info({
                logMessage: 'chat.component.sendCommand()..sent',
                logParameters: [commandId]
              });
              result = CommandResultType.Success;
            } else if (ex.status == 401 && retry401 === true) {
              this.chatLoggingService.error({
                logMessage: 'chat.component.sendCommand(): Error 401 (attempt refresh) sending command',
                logParameters: [commandId, paramBody, ex]
              });
              return this.chatAuthService.refreshToken().then(() => {
                return this.sendCommand(message, false);
              });
            } else {
              this.chatLoggingService.error({
                logMessage: 'chat.component.sendCommand(): Error sending command',
                logParameters: [commandId, paramBody, ex]
              });
            }
          }
        }
      } catch (ex) {
        if (ex && ex.statusCode === 404) {
          result = CommandResultType.NoMatch;
        } else {
          this.chatLoggingService.error({
            logMessage: 'chat.component.sendCommand(): Error unknown commandId ' + commandId,
            logParameters: [ex]
          });
        }
      }
    }

    return result;
  }

  private async runWorkflowCommand(command: CommandResponse, args: any) {
    // this is a temp workaround while we wait for a fix on the platform.. we will invoke wf directly
    if (command) {
      // get the workflow parameter
      const workflowParam = command.referenceCommandParameters.find(param => param.parameterId === 'workflow');
      const workflowId = workflowParam.value;
      // build the url
      let url = SharedHelpers.createServiceUrl({
        service: 'workflow',
        version: '2017-09-01'
      });
      url = `${url}/activities/${workflowId}/workers`;

      // now we got to build up our workflow argument
      const body = { inputs: {} };
      const inputKeys = command.parameters.map(arg => arg.parameterId);
      inputKeys.forEach(key => {
        body.inputs[key] = args[key];
      });

      // now invoke the event
      const response = await this.httpClient
        .post(url, body, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: this.chatAuthService.token.access_token
          }
        })
        .toPromise();
      this.chatLoggingService.info({
        logMessage: 'chat.component.runWorkflowCommand()..sent',
        logParameters: [url]
      });
    }
  }

  private commandArgs2Array(text: string) {
    const re = /^"[^"]*"$/; // Check if argument is surrounded with double-quotes
    const re2 = /^([^"]|[^"].*?[^"])$/; // Check if argument is NOT surrounded with double-quotes

    let arr = [];
    let argPart = null;

    text &&
      text.split(' ').forEach(arg => {
        if ((re.test(arg) || re2.test(arg)) && !argPart) {
          arr.push(arg);
        } else {
          argPart = argPart ? argPart + ' ' + arg : arg;
          // If part is complete (ends with a double quote), we can add it to the array
          if (/"$/.test(argPart) && !argPart.endsWith('\\"')) {
            argPart = SharedHelpers.replaceAll(argPart, '\\\\', '');
            arr.push(argPart);
            argPart = null;
          }
        }
      });

    return arr;
  }

  onMessageRemoved(messageId: string) {
    try {
      //Don't want to process a message removed in the middle of a fetch
      if (this.fetchingMessages > 0) {
        setTimeout(() => {
          this.onMessageRemoved(messageId);
        }, 500);
      } else {
        if (this.messages) {
          remove(this.messages, (message: Message) => message.messageId === messageId);
          this.updateMessageReadIndicator(this.messages[this.messages.length - 1]);
          this.updateLastReadMessageToCheck();
          //Need this in case we delete the first start message
          if (this.messages.length === 0) {
            this.hasUnreadMessage = false;
          }
          this.cdRef.markForCheck();
        }
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.onMessageRemoved(): Error',
        logParameters: [ex]
      });
    }
  }

  private onMessageReceived(message: MessageEx, isRetry: boolean = false) {
    if (!MessageUtils.checkAllowedPlatformSystemMessage(message, this.config)) {
      return;
    }

    //Don't want to process a message received in the middle of a fetch
    //CL-15337: Add retry once
    if (this.fetchingMessages > 0 && !isRetry) {
      if (this.config?.logMessageEvents) {
        this.chatLoggingService.info({
          logMessage: 'chat.component.onMessageReceived(): middle of a fetch, retry once',
          logParameters: [message.conversationId, message.messageId, this.fetchingMessages]
        });
      }
      setTimeout(() => {
        this.onMessageReceived(message, true);
      }, 500);
    } else {
      this.chatLoggingService.debug({
        logMessage: 'chat.component.onMessageReceived()',
        logParameters: [message.messageId]
      });
      // filter out messages for other conversations
      if (message.conversationId != this.conversationId) {
        return;
      }

      let isNearBottom = false;
      try {
        this.setAttachmentType(message);
        this.updateMessageReadIndicator(message);
        this.setIsSMSMessage(message);

        if (message?.contentType !== ContentTypes.HIDDEN && !message.parentMessageId) {
          const i: number = findIndex(
            this.messages,
            (listMessage: Message) => listMessage.messageId === message.messageId
          );
          let matchedMessage: MessageEx = null;
          if (i >= 0) {
            matchedMessage = this.messages[i];
          } else if (i === -1) {
            //Check tag to see if matches up with a local message sendId
            let foundMatch = this.messages.find(m => m.messageId.indexOf('local') === 0 && m.tag === message.tag);
            if (foundMatch) {
              matchedMessage = foundMatch;
              matchedMessage.clCompChat_state = 'sent';
            }
          }

          if (!matchedMessage && (this.isSearch || this.isSnapshot)) {
            //Search snapshot or go to message snapshot, we won't load new messages
            if (this.config?.logMessageEvents) {
              this.chatLoggingService.info({
                logMessage: 'chat.component.onMessageReceived(): search or snapshot',
                logParameters: [message.messageId]
              });
            }
            return;
          }

          if (matchedMessage) {
            let diffBody = matchedMessage.body !== message.body;
            Object.assign(matchedMessage, message);
            let chatItem = this.chatItems.find(c => c.item().messageId === message.messageId);
            if (
              (matchedMessage.clCompChat_state === 'sending' || matchedMessage.clCompChat_state === 'sent') &&
              !matchedMessage.clCompChat_sentReceived
            ) {
              matchedMessage.clCompChat_sentReceived = true;
              if (diffBody && chatItem) {
                chatItem.parseMessage();
              } else if (chatItem) {
                chatItem.detectChanges();
              }
            } else {
              if (chatItem) {
                chatItem.parseMessage();
              }
            }
          } else if (this.messages && this.messages.length) {
            let isOlder = new Date(message.createdOn) < new Date(this.messages[0].createdOn);
            //Probably an edit on an older message, not in view, so we can ignore.
            //However, small amount of messages, need to process in case of broken up message
            if (!isOlder || this.messages.length < 20) {
              this.clearRemoteTyping(message.createdBy);
              let isNewer = new Date(message.createdOn) > new Date(this.messages[this.messages.length - 1].createdOn);
              this.messages.push(message);
              if (
                message.createdBy === this.chatAuthService.userId &&
                !message.clCompChat_state &&
                isNewer &&
                !MessageUtils.isSystemMessage(message)
              ) {
                message.clCompChat_state = 'sent';
                message.clCompChat_sentReceived = true;
                this.setMostRecentSent(message);
              } else if (this.shouldAnnounceNewMessage(message)) {
                this.announceNewMessage(
                  this.translate.instant('cl_chat.newMessageFrom', { name: this.getDisplayName(message.createdBy) })
                );
              }
              if (message.clCompChat_forceDatabaseWrite) {
                this.chatDatabaseService.addMessageIfConversationExists(message);
              }
              if (this.scrollingService.isNearBottom()) {
                isNearBottom = true;
              }
            }
          } else {
            this.clearRemoteTyping(message.createdBy);
            this.messages = [message];
            if (
              message.createdBy === this.chatAuthService.userId &&
              !message.clCompChat_state &&
              !MessageUtils.isSystemMessage(message)
            ) {
              message.clCompChat_state = 'sent';
              this.setMostRecentSent(message);
            } else if (this.shouldAnnounceNewMessage(message)) {
              this.announceNewMessage(
                this.translate.instant('cl_chat.newMessageFrom', { name: this.getDisplayName(message.createdBy) })
              );
            }
            if (message.clCompChat_forceDatabaseWrite) {
              this.chatDatabaseService.addMessageIfConversationExists(message);
            }
            if (this.scrollingService.isNearBottom()) {
              isNearBottom = true;
            }
          }
          // AI streaming chunks share one fixed createdOn per message and arrive in rapid
          // bufferTime(40) bursts (see subscribeToAiChunks) - nothing new is "read" until the
          // reply finishes, so skip this bookkeeping on every intermediate token and only run it
          // once clCompChat_aiStreaming flips false on the closing chunk.
          if (!message.clCompChat_aiStreaming) {
            if (
              this.hasFocus() &&
              ((this.hasUnreadMessage && (this.firstUnreadMessageVisible() || this.scrollingService.isNearBottom())) ||
                (!this.hasUnreadMessage && this.scrollingService.isNearBottom()))
            ) {
              this.updateLastReadMessageDate(message);
              if (message.createdBy === this.chatAuthService.userId) {
                this.clearFirstUnread();
              }
            } else if (message.createdBy === this.chatAuthService.userId) {
              this.updateLastReadMessageDate(message);
              this.clearFirstUnread();
            } else {
              this.updateHasUnreadMessage(message.conversationId);
            }
          }
        }
        this.messages = this.filterOutMatchingAttachmentMessages(this.messages);
        this.messages = this.messages.sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
        this.updateStartOfDayForMessages();
        this.updateLastReadMessageToCheck();
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.component.onMessageReceived(): Error',
          logParameters: [ex]
        });
      }
      this.cdRef.markForCheck();
      //In case of messages received in quick succession, need to render them all properly
      takeRight(this.chatItems.toArray(), 10).forEach(c => {
        c.markForCheck();
      });
      if (isNearBottom) {
        setTimeout(() => {
          this.scrollingService.scrollToBottom();
        }, 0);
        //Sometimes (~10-20%) the 0 delay isn't enough for the DOM to be updated for some reason
        //in this scenario, so try again in 500ms just to be safe
        setTimeout(() => {
          if (this.scrollingService.isNearBottom()) {
            this.scrollingService.scrollToBottom();
          }
        }, 500);
      }
    }
  }

  onLocationClick(event: Event) {
    this.locationClick$.next(event);
  }

  async getLocationPreview($event) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.getLocationPreview()',
      logParameters: [this.isLocationProcessing]
    });

    if (this.isLocationProcessing) return;

    this.isLocationProcessing = true;
    try {
      await this.getCurrentLocation();

      if (
        this.locationService.isPermissionDenied ||
        this.locationService.failedToGetLocation ||
        this.locationService.invalidException
      ) {
        return;
      }

      const dialogRef = this.dialogService.open(ChatConfirmationDialogComponent, {
        data: {
          currentLocation: this.currentLocation,
          title: this.translate.instant('cl_chat.shareYourLocation'),
          okButtonText: this.translate.instant('cl_chat.send'),
          cancelButtonText: this.translate.instant('cl_chat.cancel')
        },
        showHeader: false,
        modal: true,
        dismissableMask: true,
        styleClass: 'chat-confirmation-modal',
        contentStyle: { padding: '0', overflow: 'visible' }
      });

      dialogRef.onClose.subscribe(result => {
        if (result) {
          this.sendLocation();
          this.focusTextArea();
        }
      });
    } finally {
      this.isLocationProcessing = false;
      setTimeout(() => {
        this.locationBtn?.nativeElement?.blur();
      }, 100);
    }
  }

  public async getCurrentLocation(throwError: boolean = false, sendLocation: boolean = false): Promise<any> {
    try {
      this.chatLoggingService.info({
        logMessage: 'chat.component.getCurrentLocation()'
      });
      let analyticObj: AnalyticObject = {
        eventName: AnalyticEvents.analytic_events.common.event_name.chat.share_location_message,
        attributes: ChatAnalyticAttributes
      };
      this.onAnalyticEvent.emit(analyticObj);
      let event = new CustomEvent('get-current-location', {
        bubbles: false,
        cancelable: true,
        detail: {}
      });
      this.onShareLocation.emit(event);
      if (!event.defaultPrevented) {
        this.currentLocation = await this.locationService.getCurrentPosition();
        if (
          this.error() === this.translate.instant('cl_chat.needAccessToLocation') ||
          this.error() === this.translate.instant('cl_chat.failedToGetLocation')
        ) {
          this.clearError();
        }
        this.cdRef.markForCheck();
        if (sendLocation) {
          return this.sendLocation();
        }
      } else {
        this.chatLoggingService.info({
          logMessage: 'chat.component.getCurrentLocation() Custom Event blocking',
          logParameters: [event.defaultPrevented]
        });
      }
    } catch (ex) {
      if (this.locationService.isPermissionDenied) {
        this.chatLoggingService.messageError({
          exceptionLogEvent: {
            logMessage: 'chat.component.getCurrentLocation() Permission Denied:',
            logParameters: [ex]
          },
          errorMessage: this.translate.instant('cl_chat.needAccessToLocation')
        });
      } else if (this.locationService.failedToGetLocation || this.locationService.invalidException) {
        this.chatLoggingService.messageError({
          exceptionLogEvent: {
            logMessage: 'chat.component.getCurrentLocation() Failed:',
            logParameters: [ex]
          },
          errorMessage: this.translate.instant('cl_chat.failedToGetLocation')
        });
      }

      if (throwError) {
        throw ex;
      }
    }
  }

  public async sendLocation(): Promise<any> {
    if (this.currentLocation) {
      const safeLocation = this.serializeGeolocationPosition(this.currentLocation);
      const body: string = JSON.stringify(safeLocation);

      this.chatLoggingService.info({
        logMessage: 'chat.component.sendLocation() send location'
      });

      const msg: any = {
        conversationId: this.conversationId,
        accountId: this.chatAuthService.accountId,
        messageId: this.getLocalMessageId(),
        contentType: ContentTypes.LOCATION,
        body: body,
        fallbackText: `${safeLocation.coords.latitude} / ${safeLocation.coords.longitude}`,
        createdOn: new Date(),
        createdBy: this.chatAuthService.userId,
        _links: {
          self: ''
        }
      };

      return this.sendMessage(msg, false, false);
    }
  }

  private serializeGeolocationPosition(position: GeolocationPosition) {
    if (!position || !position.coords) {
      return null;
    }

    const { latitude, longitude, accuracy, altitude, altitudeAccuracy, heading, speed } = position.coords;

    return {
      coords: {
        latitude,
        longitude,
        accuracy,
        altitude,
        altitudeAccuracy,
        heading,
        speed
      },
      timestamp: position.timestamp
    };
  }

  public isToday(date): boolean {
    var currentDate = new Date();
    var setDate = new Date(date);
    if (
      setDate.getDate() == currentDate.getDate() &&
      setDate.getMonth() == currentDate.getMonth() &&
      setDate.getFullYear() == currentDate.getFullYear()
    ) {
      return true;
    }
    return false;
  }

  public undoQuotedMessage(): void {
    this.quotedMessage = undefined;
    this.cdRef.markForCheck();
  }

  private replaceEmojiOnSend(body: string): string {
    this.emojiDictionary.forEach(d => {
      body = SharedHelpers.replaceAll(body, '\\s' + d.regex + '\\s', ' ' + MentionHelpers.getEmojiValue(d.value) + ' ');
      if (body.startsWith(d.key + ' ')) {
        body = MentionHelpers.getEmojiValue(d.value) + ' ' + body.substring(d.key.length + 1, body.length);
      }
      if (body.endsWith(' ' + d.key)) {
        body = body.substring(0, body.length - 1 - d.key.length) + ' ' + MentionHelpers.getEmojiValue(d.value);
      }
      if (body === d.key) {
        body = MentionHelpers.getEmojiValue(d.value);
      }
      //Need to do this a second time because they can share a space
      body = SharedHelpers.replaceAll(body, '\\s' + d.regex + '\\s', ' ' + MentionHelpers.getEmojiValue(d.value) + ' ');
    });
    return body;
  }

  private checkForMentions(key) {
    MentionHelpers.checkForMentions(
      key,
      this.textArea,
      this.textAreaProps,
      this.mentionsDropdown,
      this.chatCloudLinkService,
      this.chatDatabaseService,
      this.chatAdminService,
      this.chatAuthService,
      this.cdRef,
      this.participants,
      this.translate.instant('cl_chat.here'),
      this.commandsDropdown,
      this.mentionsAnchor,
      this.dropdownMaxHeight
    );
  }

  setDropdownIndex(index) {
    MentionHelpers.setDropdownIndex(
      index,
      this.textAreaProps,
      this.mentionsDropdown,
      this.dropdownMentions,
      this.cdRef,
      this.commandsDropdown,
      this.dropdownCommands
    );
  }

  selectDropdown() {
    if (
      this.commandsDropdown?.overlayVisible &&
      this.textAreaProps.commands?.length &&
      this.textAreaProps.commands[this.textAreaProps.selectedDropdownIndex]
    ) {
      this.enterCommand(this.textAreaProps.commands[this.textAreaProps.selectedDropdownIndex]);
    } else if (MentionHelpers.isMentionsDropdownOpen(this.mentionsDropdown, this.textAreaProps)) {
      this.enterMention(this.textAreaProps.mentions[this.textAreaProps.selectedDropdownIndex]);
    }
  }

  private checkForCommands(key) {
    if (SharedHelpers.isCharacterKeyPress(key)) {
      var foundSlashSymbol = false;
      var commandInput = '';
      var selection = MentionHelpers.getSelection(this.textArea);
      const text = MentionHelpers.getSelectionData(selection);
      if (!selection.anchorNode.previousSibling && text) {
        var commandCaretEnd = MentionHelpers.getRange(this.textArea, selection).startOffset;
        var endOfCaretIndex = text.substring(commandCaretEnd, text.length).indexOf(' ');
        if (endOfCaretIndex > -1) {
          commandCaretEnd += endOfCaretIndex;
        } else {
          commandCaretEnd += text.substring(commandCaretEnd, text.length).length;
        }
        var textToCaret = text.substring(0, commandCaretEnd);
        if (textToCaret.startsWith('/') && textToCaret.indexOf('@') === -1 && textToCaret.indexOf(' ') === -1) {
          foundSlashSymbol = true;
          commandInput = textToCaret.substring(1, textToCaret.length);
        }
      }
    }
    if (foundSlashSymbol) {
      this.getCommands(commandInput);
    } else if (SharedHelpers.isCharacterKeyPress(key)) {
      this.textAreaProps.commandPending = false;
      if (this.commandsDropdown) {
        this.commandsDropdown.hide();
      }
    }
  }

  private async getCommands(search: string = ''): Promise<void> {
    try {
      let commands: Command[] = [];
      let params: any = {
        options: {
          query: {
            $skip: 0,
            $top: 20,
            $search: search
          }
        }
      };
      this.textAreaProps.commandPending = true;
      const res = await this.chatCloudLinkService.chat.getCommands(params);
      const result = res.body;
      if (result._embedded) {
        commands = result._embedded.items;
      }

      for (const command of commands) {
        this.updateCommandParams(command);
      }

      const filtered = commands.filter(c => {
        if (this.config?.hiddenCommands && this.config.hiddenCommands.find(hc => hc === c.commandId)) {
          return false;
        }
        if (this.hiddenCommands.find(hc => hc === c.commandId)) {
          return false;
        }
        if (!this.currentConversation?.stream && (<any>c).streamOnly) {
          return false;
        }
        return true;
      });

      if (this.textAreaProps.commandPending) {
        this.textAreaProps.commands = [];
        this.textAreaProps.commands.push(...filtered);
        this.textAreaProps.mentionPending = false;
        if (this.mentionsDropdown) {
          this.mentionsDropdown.hide();
        }
        if (this.commandsDropdown && this.textAreaProps.commands.length) {
          MentionHelpers.setDropdownMaxHeight('commands', this.dropdownMaxHeight, this.chatLoggingService);
          MenuHelpers.showAbove(this.commandsDropdown, this.textArea.nativeElement, { align: 'left' });
        } else if (this.commandsDropdown) {
          this.commandsDropdown.hide();
        }
        this.textAreaProps.commandPending = false;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat.component.getCommands(): Error failed to load',
        logParameters: [ex]
      });
    }
    this.textAreaProps.selectedDropdownIndex = 0;
    this.cdRef.markForCheck();
  }

  private updateCommandParams(command) {
    // translate system commands
    if (command.accountId === '*') {
      const transKey: string = 'cl_chat.systemCommands.' + command.commandId + '.description';
      const desc = this.translate.instant(transKey);
      command.description = desc && desc !== transKey ? desc : command.description;
    }

    const isStreamCommand = this.streamOnlyCommands.some(filter => filter === command.commandId);
    command['streamOnly'] = isStreamCommand;

    command.parameters = command.parameters.sort((a, b) => {
      if (a.required && b.required) {
        return 0;
      } else if (a.required && !b.required) {
        return -1;
      } else if (!a.required && b.required) {
        return 1;
      }
    });

    for (const param of command.parameters) {
      let hidden: boolean = false;

      if (param.parameterId.toLowerCase() === 'conversationid') {
        param['value'] = this.conversationId;
        hidden = true;
      }

      if (command.referenceCommandParameters) {
        const refCmd = command.referenceCommandParameters.find(c => c.parameterId === param.parameterId);
        if (refCmd) {
          param['value'] = refCmd.value;
          hidden = true;
        }
      }

      param['hidden'] = hidden;
    }
  }

  public getCaretPosition(checkForMention = false) {
    MentionHelpers.getCaretPosition(
      this.textArea,
      this.textAreaProps,
      this.chatCloudLinkService,
      this.chatLoggingService,
      'chat.component',
      checkForMention
    );
  }

  public onEmojiClick(e: EmojiOutput) {
    if (e.isKeyboard) {
      this.preventSend = true;
    }
    if (
      this.overCharacterLength(this.convertMentionsToText(false).length + e.native.length) ||
      this.overQuotedMessageCharacterLimit(this.convertMentionsToText(false) + e.native)
    ) {
      this.showWarning(this.translate.instant('cl_chat.maximumTextInputCharacterLimitReached'));
    } else {
      MentionHelpers.onEmojiClick(
        e,
        this.textArea,
        this.textAreaProps,
        this.chatCloudLinkService,
        this.chatLoggingService,
        'chat.component',
        this.cdRef,
        this.emojiPipe,
        this.isMobile,
        this.config?.enableTwemoji
      );
      this.textAreaHasTwemoji = true;
    }
  }

  public goToMessage(params: { messageId: string; forceSearch: boolean; errorMessage?: string }) {
    if (this.chatCloudLinkService.recentDeletedMessages.find(d => d === params.messageId)) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat.component.loadMessageSnapshot(): Message not found',
          logParameters: [params.messageId]
        },
        errorMessage: params.errorMessage
      });
      return;
    }
    if (params.forceSearch && !this.chatCloudLinkService.isOffline()) {
      this.loadMessageSnapshot(params.messageId, params.errorMessage);
    } else {
      this.findMessage(params.messageId, 0, params.errorMessage);
    }
  }

  private setGoingToQuoted() {
    if (this.goingToQuotedTimeout) {
      clearTimeout(this.goingToQuotedTimeout);
    }
    this.goingToQuoted = true;
    this.goingToQuotedTimeout = setTimeout(() => {
      this.goingToQuoted = false;
      this.goingToQuotedTimeout = null;
    }, 2000);
  }

  public async findMessage(messageId: string, attempt: number = 0, errorMessage: string = null) {
    let msgElement = document.getElementById(messageId);
    if (msgElement) {
      this.scrollingService.scrollToMessage(messageId);
    }
    if (!msgElement && !this.chatCloudLinkService.isOffline()) {
      await this.fetchMessages(FetchType.Normal, {
        loadAttempt: this.conversationLoadAttempt
      });
      setTimeout(() => {
        this.setGoingToQuoted();
        msgElement = document.getElementById(messageId);
        if (msgElement) {
          this.scrollingService.scrollToMessage(messageId);
        } else if (attempt < 1) {
          this.findMessage(messageId, ++attempt, errorMessage);
        } else {
          this.loadMessageSnapshot(messageId, errorMessage);
        }
      }, 0);
    }
  }

  public sendText(message: string) {
    let now = new Date();
    let msg: MessageEx;
    msg = {
      conversationId: this.conversationId,
      accountId: this.chatAuthService.accountId,
      messageId: this.getLocalMessageId(),
      body: message,
      contentType: ContentTypes.TEXT,
      createdOn: now,
      createdBy: this.chatAuthService.userId,
      modifiedOn: now,
      modifiedBy: this.chatAuthService.userId,
      _links: {}
    };
    this.sendMessage(msg, false, false);
  }

  public async deletePendingSend(messageId: string) {
    const i: number = findIndex(this.messages, (listMessage: Message) => listMessage.messageId === messageId);
    if (i >= 0) {
      try {
        await this.chatDatabaseService.deleteMessage(this.chatAuthService.accountId, messageId);
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.component.deletePendingSend(): Error deleting message from db',
          logParameters: [messageId, ex]
        });
      }
      this.messages.splice(i, 1);
      this.cdRef.markForCheck();
    }
  }

  public async retrySend(params: { messageId: string; body: string; contentType: string; fallbackText?: string }) {
    const matchedMessage: MessageEx = find(
      this.messages,
      (listMessage: MessageEx) => listMessage.messageId === params.messageId
    );
    if (matchedMessage) {
      if (this.chatCloudLinkService.isOffline() && this.indexDbEnabled() && this.offlineSendingEnabled) {
        try {
          matchedMessage.clCompChat_state = 'pending';
          await this.chatDatabaseService.updateMessage(matchedMessage);
          var chatItem = this.chatItems.find(c => c.item().messageId === matchedMessage.messageId);
          if (chatItem) {
            chatItem.detectChanges();
          }
        } catch (ex) {
          this.chatLoggingService.error({
            logMessage: 'chat.component.retrySend(): Error updating message in db',
            logParameters: [matchedMessage.messageId, ex]
          });
        }
      } else {
        await this.deletePendingSend(params.messageId);
        let now = new Date();
        let msg: MessageEx;
        msg = {
          conversationId: this.conversationId,
          accountId: this.chatAuthService.accountId,
          messageId: params.messageId,
          body: params.body,
          fallbackText: params.fallbackText,
          contentType: params.contentType,
          createdOn: now,
          createdBy: this.chatAuthService.userId,
          modifiedOn: now,
          modifiedBy: this.chatAuthService.userId,
          _links: {}
        };
        this.sendMessage(msg, false, false);
      }
    }
  }

  setFullScreenUserInfo(params: { source: string; name?: string; id?: string; item?: MessageEx }) {
    this.itemName = params.name;
    this.itemCreateUserName = this.getDisplayName(params.item.createdBy);
    this.itemCreateUserId = params.item.createdBy;
    this.itemCreateUserAvatarSrc = this.chatAdminService.avatars[params.item.createdBy]?.hasOwnProperty('50')
      ? this.chatAdminService.avatars[params.item.createdBy][50]
      : '';
    this.itemCreateTime = params.item.createdOn;
  }

  public fullScreenImage(params: { source: string; name?: string; id?: string; item?: MessageEx }) {
    this.setFullScreenUserInfo(params);
    this.fullScreenUseWidth = false;
    this.resetFullScreenImage();
    this.isFullScreenImage = true;
    this.isFullScreen = true;
    this.onFullScreenMode.emit(this.isFullScreen);
    this.fullScreenSource = params.source;
    this.fullScreenId = params.id;
    this.fullScreenName = params.name || '';
    this.cdRef.markForCheck();
    setTimeout(() => {
      if (
        this.fullScreenImg?.nativeElement &&
        this.maincontent?.nativeElement &&
        this.fullScreenImg.nativeElement.offsetWidth > this.maincontent.nativeElement.offsetWidth
      ) {
        this.fullScreenUseWidth = true;
        this.cdRef.markForCheck();
        if (this.fullScreenImg.nativeElement.offsetHeight > this.maincontent.nativeElement.offsetHeight) {
          this.fullScreenUseWidth = false;
          this.cdRef.markForCheck();
        }
      }
    });
    setTimeout(() => {
      if (this.fullScreenId) {
        this.imageFullScreenDownload?.nativeElement.focus();
      } else {
        this.fullScreenClose?.nativeElement.focus();
      }
    });
    //Pinch to zoom on fullscreen img for mobile
    setTimeout(() => {
      if (this.isFullScreenImage && this.isMobile) {
        const zoomScale = 2; // To change the sensitivity of the zoom, you can change the "2" to something else.
        let pinchInScale: number;
        let pinchOutScale: number;
        let startingDistanceBetweenPoints: number; // Number of pixels between the two fingers. Used in addition with ev.scale to accurately know when to zoom in and out depending on the length of the pinch in/out.
        let currentZoom: number; // This does not represent the % of the zoom. It is a number use to call fullScreenImageZoomIn/Out in increments.
        let firstPinchOut: boolean; // Prevents from zooming the other direction when releasing fingers
        let firstPinchIn: boolean; // Prevents from zooming the other direction when releasing fingers

        this.hammer = new Hammer(this.fullScreenImageContainer.nativeElement, {
          drag: false
        });
        this.hammer.get('pinch').set({ enable: true });
        this.hammer.on('pinchstart', ev => {
          startingDistanceBetweenPoints =
            Math.sqrt(
              Math.pow(ev.pointers[0].x - ev.pointers[1].x, 2) + Math.pow(ev.pointers[0].y - ev.pointers[1].y, 2)
            ) / 100; // Keep the division by 100. The algorithm works best with it.
          this.disableCdkDrag = true;
          this.cdRef.markForCheck();
          document.dispatchEvent(new Event('touchend')); // Necessary to prevent cdk drag from being enabled when pinching.
          pinchInScale = 1000; // Just a random big number to reset the value.
          pinchOutScale = 1; // Just a random small number to reset the value.
          currentZoom = zoomScale * startingDistanceBetweenPoints;
          firstPinchOut = false;
          firstPinchIn = false;
        });
        this.hammer.on('pinchend', ev => {
          this.disableCdkDrag = false;
          this.cdRef.markForCheck();
        });
        this.hammer.on('pinchin', ev => {
          if (ev.scale * startingDistanceBetweenPoints < pinchInScale) {
            // When actually pinching in.
            // In a pinch in event, ev.scale represents the ratio of the new distance between the fingers compared to the original distance.
            // Therefore, if the fingers are originally 10 cm apart, and then 2 cm apart, ev.scale will be 0.2.
            firstPinchOut = false;
            pinchInScale = ev.scale * startingDistanceBetweenPoints;
            if (pinchInScale * zoomScale < currentZoom) {
              currentZoom--;
              this.fullScreenImageZoomOut();
            }
          } else {
            // In case we are still getting pinchin events even if, in reality, pinching out (Hacky workaround).
            if (!firstPinchOut) {
              firstPinchOut === true;
            } else {
              pinchInScale = ev.scale * startingDistanceBetweenPoints;
              if (pinchInScale * zoomScale > currentZoom) {
                currentZoom++;
                this.fullScreenImageZoomIn();
              }
            }
          }
        });
        this.hammer.on('pinchout', ev => {
          if (ev.scale * startingDistanceBetweenPoints > pinchOutScale) {
            // When actually pinching out.
            // In a pinch out event, ev.scale represents the ratio of the new distance between the fingers compared to the original distance.
            // Therefore, if the fingers are originally 2 cm apart, and then 10 cm apart, ev.scale will be 5.
            firstPinchIn = false;
            pinchOutScale = ev.scale * startingDistanceBetweenPoints;
            if (pinchOutScale * zoomScale > currentZoom) {
              currentZoom++;
              this.fullScreenImageZoomIn();
            }
          } else {
            // In case we are still getting pinchout events even if, in reality, pinching in (Hacky workaround).
            if (!firstPinchIn) {
              firstPinchIn === true;
            } else {
              pinchOutScale = ev.scale * startingDistanceBetweenPoints;
              if (pinchOutScale * zoomScale < currentZoom) {
                currentZoom--;
                this.fullScreenImageZoomOut();
              }
            }
          }
        });
      }
    }, 0);
  }

  public onFullScreenImageMouseWheel($event) {
    if ($event.deltaY < 0) {
      this.fullScreenImageZoomIn();
    }
    if ($event.deltaY > 0) {
      this.fullScreenImageZoomOut();
    }
  }

  private readonly fullScreenImagePanStep = 40;

  public onFullScreenImageKeydown(event: KeyboardEvent): void {
    const deltaByKey: { [key: string]: { dx: number; dy: number } } = {
      ArrowUp: { dx: 0, dy: -this.fullScreenImagePanStep },
      ArrowDown: { dx: 0, dy: this.fullScreenImagePanStep },
      ArrowLeft: { dx: -this.fullScreenImagePanStep, dy: 0 },
      ArrowRight: { dx: this.fullScreenImagePanStep, dy: 0 }
    };
    const delta = deltaByKey[event.key];
    if (!delta || !this.cdkDrag) {
      return;
    }
    event.preventDefault();
    this.panFullScreenImage(delta.dx, delta.dy);
  }

  private panFullScreenImage(dx: number, dy: number): void {
    const imgEl: HTMLElement = this.fullScreenImg?.nativeElement;
    const boundaryEl: HTMLElement = imgEl?.closest('.full-screen-image-draggable-container');
    if (!imgEl || !boundaryEl) {
      return;
    }
    const current = this.cdkDrag.getFreeDragPosition();
    const imgRect = imgEl.getBoundingClientRect();
    const boundaryRect = boundaryEl.getBoundingClientRect();

    // Two edge constraints - left edge inside the boundary, right edge inside the boundary - give
    // two bounds for the free-drag offset. When the image fits within the boundary on this axis
    // both constraints hold at once (a normal, non-degenerate slide range); when the image is
    // larger than the boundary they invert (sliding stops once either edge would open a gap on
    // the other side). Taking min/max of the two bounds handles both cases with no special-casing.
    const clampAxis = (
      proposed: number,
      natural: number,
      size: number,
      boundaryStart: number,
      boundaryEnd: number
    ): number => {
      const edgeAtStart = boundaryStart - natural;
      const edgeAtEnd = boundaryEnd - natural - size;
      const min = Math.min(edgeAtStart, edgeAtEnd);
      const max = Math.max(edgeAtStart, edgeAtEnd);
      return Math.min(Math.max(proposed, min), max);
    };

    const nextX = clampAxis(
      current.x + dx,
      imgRect.left - current.x,
      imgRect.width,
      boundaryRect.left,
      boundaryRect.right
    );
    const nextY = clampAxis(
      current.y + dy,
      imgRect.top - current.y,
      imgRect.height,
      boundaryRect.top,
      boundaryRect.bottom
    );

    this.cdkDrag.setFreeDragPosition({ x: nextX, y: nextY });
    this.cdRef.markForCheck();
  }

  public fullScreenImageZoomIn() {
    this.showTooltip = true;
    if (this.fullScreenPercentage < 300) {
      this.fullScreenPercentage += 20;
      this.fullScreenZoom += 2.5;
      this.cdRef.markForCheck();
    }
  }

  closeTooltip() {
    this.showTooltip = false;
  }

  isDisableZoomInButton() {
    if (this.fullScreenPercentage === 300) {
      return true;
    } else {
      return false;
    }
  }

  isDisableZoomOutButton() {
    if (this.fullScreenPercentage === 20) {
      return true;
    } else {
      return false;
    }
  }

  sliderChange($event: SliderChangeEvent) {
    this.showTooltip = true;
    this.fullScreenPercentage = $event.value as number;
    this.fullScreenZoom = (this.fullScreenPercentage / 20) * 2.5;
    this.cdRef.markForCheck();
  }

  public fullScreenImageZoomOut() {
    this.showTooltip = true;
    if (this.fullScreenPercentage > 20) {
      this.fullScreenPercentage -= 20;
      this.fullScreenZoom -= 2.5;
      this.cdRef.markForCheck();
    }
  }

  public resetFullScreenImage() {
    this.showTooltip = false;
    this.fullScreenPercentage = 100;
    this.fullScreenZoom = 12.5;
    if (this.cdkDrag) {
      this.cdkDrag.reset();
    }
    this.cdRef.markForCheck();
  }

  async showFileFullScreen(attachmentId: string, type: FileTypes) {
    let msgElement: ChatItemComponent = this.chatItems.find(c => c.attachmentId() === attachmentId);
    if (msgElement) {
      type === FileTypes.PDF ? msgElement.fullScreenPdf() : msgElement.fullScreenImage();
    } else if (!msgElement && !this.chatCloudLinkService.isOffline()) {
      try {
        MessageUtils.getMessageIdOfAttachmentMessage(
          attachmentId,
          this.conversationId,
          this.chatCloudLinkService,
          this.chatLoggingService
        ).then(messageId => {
          if (messageId) {
            if (this.chatCloudLinkService.recentDeletedMessages.find(d => d === messageId)) {
              throw 'Recently deleted';
            }
            this.loadMessageSnapshot(messageId).then(() => {
              msgElement = this.chatItems.find(c => c.attachmentId() === attachmentId);
              if (msgElement && type === FileTypes.PDF) {
                msgElement.fullScreenPdf();
              } else if (msgElement && msgElement.imageViewerUrl()) {
                msgElement.fullScreenImage();
              } else if (msgElement && !msgElement.imageViewerUrl()) {
                //gives a timeout for chat-item.component to initialize imageViewerUrl if not there yet
                if (this.imageFullScreenTimeout) {
                  clearTimeout(this.imageFullScreenTimeout);
                }
                this.imageFullScreenTimeout = setTimeout(() => {
                  this.imageFullScreenTimeout = null;
                  if (msgElement.imageViewerUrl) {
                    msgElement.fullScreenImage();
                  } else {
                    throw 'imageFullScreenTimeout expired';
                  }
                }, 2000);
              }
            });
          }
        });
      } catch (error) {
        this.chatLoggingService.messageError({
          exceptionLogEvent: {
            logMessage: 'chat.component.showFileFullScreen(): Message not found',
            logParameters: [attachmentId, error]
          },
          errorMessage: this.translate.instant('cl_chat.failedToPreview')
        });
      }
    }
  }

  public fullScreenPdf(params: { source: string; name?: string; id?: string; item?: MessageEx }) {
    this.setFullScreenUserInfo(params);
    this.isFullScreenPdf = true;
    this.isFullScreen = true;
    this.onFullScreenMode.emit(this.isFullScreen);
    this.fullScreenSource = params.source;
    this.fullScreenName = params.name || '';
    this.fullScreenId = params.id;
    this.cdRef.markForCheck();
    setTimeout(() => {
      if (this.fullScreenId) {
        this.pdfFullScreenDownload?.nativeElement.focus();
      } else {
        this.pdfFullScreenExit?.nativeElement.focus();
      }
    });
  }

  public exitFullScreen() {
    this.isFullScreen = false;
    this.onFullScreenMode.emit(this.isFullScreen);
    this.isFullScreenPdf = false;
    this.isFullScreenImage = false;
    this.fullScreenSource = undefined;
    if (this.hammer) {
      this.hammer.destroy();
    }
    this.cdRef.markForCheck();
  }

  public getText() {
    var returnMessage = this.message;
    if (this.textArea && this.textArea.nativeElement) {
      var clonedTextArea = this.textArea.nativeElement.cloneNode(true);
      //Transform mentions into text form to get
      var mentionPlaceholders = clonedTextArea.querySelectorAll('.mi-mention-placeholder');
      this.chatLoggingService.debug({
        logMessage: 'chat.component.getText() mentionPlaceholders length',
        logParameters: [mentionPlaceholders.length]
      });
      if (mentionPlaceholders.length) {
        for (var i = 0; i < mentionPlaceholders.length; i++) {
          var mentionId = mentionPlaceholders[i].getAttribute('itemprop');
          this.chatLoggingService.debug({
            logMessage: 'chat.component.getText() mentionId',
            logParameters: [mentionId]
          });
          var mentionNode = mentionPlaceholders[i].querySelector('.mention');
          if (mentionNode) {
            mentionNode.innerText = mentionId;
            this.chatLoggingService.debug({
              logMessage: 'chat.component.getText() returnText',
              logParameters: [mentionNode.innerText]
            });
          }
        }
        returnMessage = clonedTextArea.innerText;
        this.chatLoggingService.debug({
          logMessage: 'chat.component.getText() message',
          logParameters: [returnMessage]
        });
      }
    }
    this.onGetText.emit(returnMessage);
    return returnMessage;
  }

  public setText(message: string) {
    this.textArea.nativeElement.innerText += message;
    this.message = this.textArea.nativeElement.innerText;
    this.setCaret(false);
    this.checkMentions();
  }

  private checkMentions() {
    let mentions = this.message.match(SharedHelpers.mentionsRegexGlobal);
    if (mentions && mentions.length) {
      // @ts-ignore
      mentions = MentionHelpers.getFilteredMentions(mentions);
      var replaceMentions = uniqBy(mentions, v => v);
      var error = false;
      var oldBody = this.textArea.nativeElement.innerHTML;
      replaceMentions.forEach(m => {
        this.textArea.nativeElement.innerHTML = MentionHelpers.replaceWithMentionPlaceholder(
          this.textArea.nativeElement.innerHTML,
          m
        );
      });
      setTimeout(() => {
        let loadedMentionObservables = [];
        mentions.forEach(u => {
          try {
            if (!error) {
              var getElement = () => {
                return this.textArea;
              };
              let mentionReturn = MentionHelpers.replaceWithMentionElement(
                u,
                getElement,
                this.resolver,
                this.injector,
                this.appRef
              );
              if (mentionReturn.ref) {
                this.mentionRefs.push(mentionReturn.ref);
                loadedMentionObservables.push(mentionReturn.ref.instance.loaded$);
              }
            }
          } catch (ex) {
            this.chatLoggingService.error({
              logMessage: 'chat.component.checkMentions(): Error handling mentions',
              logParameters: [oldBody, ex]
            });
            error = true;
          }
        });
        if (error) {
          this.textArea.nativeElement.innerHTML = oldBody;
        }
        this.message = this.textArea.nativeElement.innerText;
        this.cdRef.markForCheck();
        this.setCaret(false);
        if (loadedMentionObservables.length) {
          MentionHelpers.waitForMentionsLoaded(
            loadedMentionObservables,
            this.ngUnsubscribe,
            this.cdRef,
            this.textArea,
            this.textAreaProps,
            this.chatLoggingService,
            this.chatCloudLinkService,
            'chat.component',
            () => this.setCaret(false),
            this.setMessage.bind(this),
            true
          );
        }
      }, 0);
      this.cdRef.markForCheck();
    }
  }

  public setCaret(atStart = false) {
    if (this.textArea) {
      var el = this.textArea.nativeElement;
      if (el) {
        el.focus();
        if (typeof window.getSelection != 'undefined' && typeof document.createRange != 'undefined') {
          var range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(atStart);
          var sel = MentionHelpers.getSelection(this.textArea);
          MentionHelpers.addRangeToSelection(range, sel);
        } else if (typeof (<any>document.body).createTextRange != 'undefined') {
          var textRange = (<any>document.body).createTextRange();
          textRange.moveToElementText(el);
          textRange.collapse(atStart);
          textRange.select();
        }
      }
    }
  }

  private async loadMessageSnapshot(messageId: string, errorMessage?: string) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.loadMessageSnapshot()',
      logParameters: [this.conversationId, messageId]
    });
    this.onConversationLoadStart.emit({
      conversationId: this.conversationId,
      loadType: ConversationLoadType[ConversationLoadType.Snapshot].toString()
    });
    this.resetSearch();
    this.firstLoad = false;
    this.isSnapshot = true;
    this.snapshotUp = JSON.parse(JSON.stringify(this.defaultNext));
    this.snapshotDown = JSON.parse(JSON.stringify(this.defaultNext));
    await this.fetchMessages(FetchType.Snapshot, {
      filter: 'messageId eq ' + "'" + messageId + "'",
      loadAttempt: this.conversationLoadAttempt
    });
    if (this.messages.length === 0) {
      this.goToLatest();
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat.component.loadMessageSnapshot(): Message not found',
          logParameters: [messageId]
        },
        errorMessage: errorMessage
      });
    } else if (this.snapshotUp.$filter && this.snapshotDown.$filter) {
      await this.fetchMessages(FetchType.SnapshotUp, {
        loadAttempt: this.conversationLoadAttempt
      });
      await this.fetchMessages(FetchType.SnapshotDown, {
        loadAttempt: this.conversationLoadAttempt
      });
      this.setGoingToQuoted();
      this.scrollingService.scrollToMessage(messageId);
      if (!this.snapshotDown?.$skipToken) {
        this.isSnapshot = false;
      }
      this.contentLoading.set(false);
      this.loading.set(false);
      this.onConversationLoadEnd.emit({ conversationId: this.conversationId });
      this.cdRef.markForCheck();
    }
  }

  public async goToLatest() {
    if (this.isSnapshot || this.isSearch) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.goToLatest()'
      });
      this.search = '';
      this.resetSearch();
      this.onGoToLatest.emit();
      try {
        this.syncing.set(false);
        await this.loadConversationFromDb(this.conversationId, this.conversationLoadAttempt);
        if (this.messages.length && !this.chatCloudLinkService.isOffline()) {
          this.syncing.set(!this.chatDatabaseService.synced[this.conversationId]);
          this.contentLoading.set(false);
          this.cdRef.markForCheck();
        }
      } catch (ex) {
        this.syncing.set(false);
        this.chatLoggingService.error({
          logMessage: 'chat.component.goToLatest(): Error loading conversation from db',
          logParameters: [ex]
        });
      }
      this.fetchMessages(FetchType.Normal, {
        loadAttempt: this.conversationLoadAttempt
      });
    }
  }

  public setReadOnly(reason: string = 'unknown') {
    this.chatLoggingService.info({
      logMessage: 'chat.component.setReadOnly() setting read only',
      logParameters: [reason]
    });
    this.readOnly = true;
    this.cdRef.markForCheck();
  }

  public messageContextMenu(event) {
    this.onMessageContextMenu.emit(event);
  }
  public getDisplayName(userId) {
    if (this.chatAdminService.displayNames[userId]) {
      return this.chatAdminService.displayNames[userId];
    }
    return userId;
  }

  public onEmojiDropdownClick(event: MouseEvent | KeyboardEvent) {
    if (this.emojisDropdown?.overlayVisible) {
      this.emojisDropdown.hide();
    } else {
      this.emojisDropdown.show(event);
    }
  }

  public detectChanges() {
    setTimeout(() => {
      this.cdRef.markForCheck();
    }, 0);
  }

  public suggestionCountChange(count) {
    this.suggestionCount = count;
    this.cdRef.markForCheck();
  }

  public suggestionClicked(suggestion) {
    this.send(suggestion);
  }

  // Chips only ever show on the conversation's last message ($last in the messages @for), once it
  // has finished streaming: an older reply's suggestions no longer fit the conversation, and
  // sending the user's turn appends a newer message, so the chips disappear on their own. Not
  // gated on aiConfig.quickReplies - that flag only controls parsing model replies, while the
  // greeting's starter chips show whenever the host configures them. The handoff chip alone is
  // enough to show the row, but only under the assistant's own message, never the user's.
  public showAiQuickReplies(item: MessageEx, isLast: boolean): boolean {
    if (!this.aiMode || !isLast || item.clCompChat_aiStreaming) {
      return false;
    }
    const isAssistant = item.createdBy === (this.aiConfig?.assistantUserId ?? 'ai-assistant');
    return !!item.clCompChat_quickReplies?.length || (isAssistant && !!this.aiHandoffLabel);
  }

  get aiHandoffLabel(): string | undefined {
    return this.aiConfig?.handoff?.label?.trim() || undefined;
  }

  // Sent as an ordinary user turn, so it goes through the same aiMode send path as typed text -
  // passing the label straight to send() leaves any draft in the composer untouched. Focus moves
  // back to the composer because the clicked chip is removed from the DOM once the turn is sent.
  public onAiQuickReply(label: string) {
    this.send(label);
    this.focusTextArea();
  }

  // Never sent to MAIS - the host decides what a handoff means (see onAiHandoffRequested).
  public onAiHandoff() {
    this.onAiHandoffRequested.emit({ conversationId: this.conversationId, sessionId: this.aiConfig?.sessionId });
  }

  // Starts a fresh MAIS session in place: a new sessionId (persisted, so a reload stays in the new
  // session), an empty conversation and the greeting again, then onAiThreadReady with the new id.
  // A reply still arriving for the old session is dropped via aiSessionGeneration rather than
  // aborted (streamChat() has no abort hook), so it can't land in the new chat. Returns false and
  // does nothing while aiMode isn't usable yet, or while the first history load is still in
  // flight - that load would otherwise overwrite the fresh conversation when it lands.
  public startNewAiSession(): boolean {
    if (
      !this.aiMode ||
      !this.aiConfig?.workspaceSlug ||
      !this.aiConfig?.authToken ||
      !this.conversationId ||
      this.aiBootstrapInFlight
    ) {
      return false;
    }
    this.aiSessionGeneration++;
    this.aiConfig.sessionId = uuid();
    this.saveAiSessionState();
    this.aiAccumulatingMessages.clear();
    this.clearRemoteTyping(this.aiConfig.assistantUserId ?? 'ai-assistant');
    this.messages = [];
    this.applyAiGreeting();
    // A brand-new session has no history by definition, so this also stands in for a first
    // bootstrap that failed - there's nothing left for maybeInitializeAiConversation() to load.
    this.aiBootstrappedConversationId = this.conversationId;
    this.loading.set(false);
    this.cdRef.markForCheck();
    this.onAiThreadReady.emit(this.aiConfig.sessionId);
    return true;
  }

  public getSuggestionsCount() {
    return this.suggestionCount;
  }

  public uploadFileClick(target: AttachTarget = 'auto') {
    this.optionsOpen = false;
    this.attachTarget = target;
    this.cdRef.markForCheck();
    setTimeout(() => {
      this.attachmentInput.nativeElement.click();
    }, 0);
  }

  // The paperclip offers "Upload to OneDrive" or "Upload to chat" once OneDrive can be used; otherwise it opens
  // the file picker straight away, as before
  public oneDriveAttachAvailable(): boolean {
    return !!this.config?.enableOneDriveLargeFiles && this.chatOneDriveService.isConnected();
  }

  public onAttachClick(event: Event) {
    if (!this.oneDriveAttachAvailable()) {
      this.uploadFileClick();
      return;
    }
    this.attachMenu?.toggle(event);
  }

  public onAttachMenuOpenChange(open: boolean) {
    this.attachMenuOpen = open;
    this.cdRef.markForCheck();
  }

  public onPaste(e: ClipboardEvent): void {
    e.preventDefault();

    //Can't upload if subscription error
    if (
      (!this.config || !this.config.disablePasteFile) &&
      !this.subscriptionError() &&
      !this.textAreaDisabled &&
      !e.clipboardData.getData(ContentTypes.TEXT)
    ) {
      //Handle pasting a file
      if (e?.clipboardData?.files?.length) {
        this.uploadFileToClient(e.clipboardData.files[0]);
        return;
      }
      //In case files array is empty
      if (e?.clipboardData?.items?.length) {
        let file = null;
        for (let i = 0; i < e.clipboardData.items.length; i++) {
          if (e.clipboardData.items[i].kind === 'file') {
            file = e.clipboardData.items[i].getAsFile();
            break;
          }
        }
        if (file) {
          this.uploadFileToClient(file);
          return;
        }
      }
    }

    var text;
    if (e.clipboardData) {
      text = e.clipboardData.getData(ContentTypes.TEXT);
    } else if ((<any>window).clipboardData) {
      text = (<any>window).clipboardData.getData('Text');
    }
    //TODO Need to figure a way to figure out the actual mention length if it's selected. Edge case, so won't deal with it for now.
    //https://github.com/mitel-networks/cloudlink-components-chat/issues/1047
    let selectionLength = window?.getSelection()?.toString()?.length || 0;
    if (
      this.overCharacterLength(text.length + this.convertMentionsToText(false).length - selectionLength) ||
      this.overQuotedMessageCharacterLimit(text + this.convertMentionsToText(false))
    ) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.onPaste(): Max Input Characters exceeded'
      });
      this.showWarning(this.translate.instant('cl_chat.pasteExceedsTheMaximumTextInputCharacterLimit'));
      if (this.quotedMessage) {
        text = text.substring(
          0,
          ChatMessageLimits.MaxQuotedMessageReply - this.convertMentionsToText(false).length + selectionLength
        );
      } else {
        text = text.substring(
          0,
          this.textAreaProps.maxInputCharacters - this.convertMentionsToText(false).length + selectionLength
        );
      }
    }
    if (document.queryCommandSupported('insertHTML')) {
      document.execCommand('insertHTML', false, escape(text));
    } else {
      document.execCommand('paste', false, text);
    }
    this.updatePaddingRight();
    this.cdRef.markForCheck();
  }

  public clearCopiedAttachment(attachment) {
    if (attachment.imageUrl) {
      window.URL.revokeObjectURL(attachment.imageUrl);
    }
    var attachmentIndex = this.pendingAttachments.indexOf(attachment);
    if (attachmentIndex > -1) {
      this.pendingAttachments.splice(attachmentIndex, 1);
    }
    this.cdRef.markForCheck();
  }

  public clearCopiedAttachments(detectChanges = true) {
    this.pendingAttachments.forEach(a => {
      if (a.imageUrl) {
        window.URL.revokeObjectURL(a.imageUrl);
      }
    });
    this.pendingAttachments = [];
    if (detectChanges) {
      this.cdRef.markForCheck();
    }
  }

  public checkAllowedFileType(type: string): boolean {
    var fileTypeAllowed = false;
    //Check allowed file types
    if (!this.config || !this.config.allowedFileTypes) {
      fileTypeAllowed = true;
    } else {
      try {
        var allowedMatch = false;
        this.config.allowedFileTypes.forEach(c => {
          var regex = new RegExp(c);
          if (type.match(regex)) {
            allowedMatch = true;
          }
        });
        if (!allowedMatch) {
          this.chatLoggingService.messageError({
            exceptionLogEvent: {
              logMessage: 'chat.component.checkAllowedFileType(): Allowed list invalid file type',
              logParameters: [type]
            },
            errorMessage:
              this.translate.instant('cl_chat.failedToUploadAttachment') +
              ': ' +
              this.translate.instant('cl_chat.invalidFileType')
          });
        }
        fileTypeAllowed = allowedMatch;
      } catch (ex) {
        this.chatLoggingService.messageError({
          exceptionLogEvent: {
            logMessage: 'chat.component.checkAllowedFileType(): Error',
            logParameters: [ex]
          },
          errorMessage:
            this.translate.instant('cl_chat.failedToUploadAttachment') +
            ': ' +
            this.translate.instant('cl_chat.invalidFileType')
        });
        return false;
      }
    }
    //Check blocked list
    if (!this.config || !this.config.blockedFileTypes) {
      return fileTypeAllowed;
    } else {
      try {
        var blockedMatch = false;
        this.config.blockedFileTypes.forEach(c => {
          var regex = new RegExp(c);
          if (type.match(regex)) {
            blockedMatch = true;
          }
        });
        if (blockedMatch) {
          this.chatLoggingService.messageError({
            exceptionLogEvent: {
              logMessage: 'chat.component.checkAllowedFileType(): Blocked list invalid file type',
              logParameters: [type]
            },
            errorMessage:
              this.translate.instant('cl_chat.failedToUploadAttachment') +
              ': ' +
              this.translate.instant('cl_chat.invalidFileType')
          });
          fileTypeAllowed = false;
        }
      } catch (ex) {
        this.chatLoggingService.messageError({
          exceptionLogEvent: {
            logMessage: 'chat.component.checkAllowedFileType(): Error',
            logParameters: [ex]
          },
          errorMessage:
            this.translate.instant('cl_chat.failedToUploadAttachment') +
            ': ' +
            this.translate.instant('cl_chat.invalidFileType')
        });
        return false;
      }
    }
    return fileTypeAllowed;
  }

  public onDrop(e: Event): void {
    e.preventDefault();
  }

  public hideCloseError(): boolean {
    if (!this.error()) {
      return false;
    }
    var noCloseErrors = [
      this.translate.instant('cl_chat.userIsNotAnAcceptedParticipant'),
      this.translate.instant('cl_chat.failedToLoadMessages') +
        ' ' +
        this.translate.instant('cl_chat.problemCommunicatingWithChat'),
      this.translate.instant('cl_chat.failedToLoadConversation') +
        ' ' +
        this.translate.instant('cl_chat.problemCommunicatingWithChat')
    ];
    return noCloseErrors.indexOf(this.error()) > -1;
  }

  public async resubscribeChatSubscriptions(refreshOverride = null) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.resubscribeChatSubscriptions()'
    });
    let refresh = true;
    if (refreshOverride !== null) {
      refresh = refreshOverride;
    }
    await this.chatCloudLinkService.subscribeToSubs(refresh);
    this.reloadConversation();
  }

  private getLocalMessageId(): string {
    const localMessageId = 'local' + uuid();
    return localMessageId;
  }

  private mapToMessageEx(messages: Message[], isDb: boolean): MessageEx[] {
    var locationCount = 0;
    let newMessages = messages.map(m => {
      let mex: MessageEx = m as MessageEx;
      this.setAttachmentType(mex);
      this.updateMessageReadIndicator(mex);
      this.setIsSMSMessage(mex);
      if (m?.contentType === ContentTypes.LOCATION) {
        locationCount++;
      }
      if (locationCount > 5) {
        mex.clCompChat_hideLocation = true;
      }
      mex.clCompChat_isDb = isDb;
      mex.clCompChat_needsResync = false;
      //To change content type to attachment system message if need be
      MessageUtils.isAttachmentSystemMessage(mex);
      return mex;
    });
    return newMessages;
  }

  public trackByFn(index, item: MessageEx) {
    if (item?.tag) {
      try {
        let tag = JSON.parse(item.tag);
        if (Object.keys(tag).length === 1 && tag.sendId) {
          // For split messages with same sendId, combine with messageId to ensure uniqueness
          return `${item.tag}-${item.messageId}`;
        }
      } catch (ex) {}
    }
    return item.messageId;
  }

  private indexDbEnabled(): boolean {
    return this.config?.enableIndexDb && 'indexedDB' in window;
  }

  private async setIndexDb() {
    if (this.indexDbEnabled() && !this.chatDatabaseService.indexDbError) {
      if (this.config?.indexDbConversationLimit) {
        this.chatDatabaseService.conversationLimit = this.config.indexDbConversationLimit;
      }
      if (this.config?.indexDbAddNewConversations) {
        this.chatConversationService.listenForNewConversationForDatabase();
      }
      this.chatDatabaseService.indexDbEnabled = true;
      await this.chatDatabaseService.openDb();
    }
  }

  public onTouchMove($event): void {
    if ($event && $event.changedTouches) {
      //To close the keyboard if finger moves from chat list to text area on mobile (e.g. scroll upwards)
      var location = $event.changedTouches[0];
      var element = document.elementFromPoint(location.clientX, location.clientY);
      if (element?.className && typeof element.className === 'string' && element.className.indexOf('chat-item') > -1) {
        this.lastTouchedElementWasChatItem = true;
      }
      if (
        element &&
        !this.selectMessagesMode &&
        element === this.textArea.nativeElement &&
        this.lastTouchedElementWasChatItem &&
        this.chatInputFocused
      ) {
        this.textArea.nativeElement.blur();
        this.lastTouchedElementWasChatItem = false;
      }
    }
  }

  public onTouchEnd($event): void {
    this.lastTouchedElementWasChatItem = false;
  }

  private updateMessageReadIndicator(message: MessageEx): void {
    if (message?.conversationId === this.conversationId) {
      var readArray = message.clCompChat_readList ? message.clCompChat_readList : [];
      this.participants.forEach(p => {
        if (
          p.conversationId === message.conversationId &&
          new Date(p.lastReadMessageDate) >= new Date(message.createdOn) &&
          message.createdBy !== p.userId &&
          p.userId !== this.chatAuthService.userId &&
          (!message.clCompChat_readList || message.clCompChat_readList.indexOf(p.userId) === -1)
        ) {
          readArray.push(p.userId);
        }
      });
      message.clCompChat_readList = readArray.sort();
      var lastMessageToCheckForRead = this.messages.find(m => m.clCompChat_lastMessageToCheckForRead);
      this.changeDetectReadIndicator(lastMessageToCheckForRead);
    }
  }

  private updateParticipantReadIndicator(participant: Participant): void {
    if (participant?.userId !== this.chatAuthService.userId && participant?.conversationId === this.conversationId) {
      const participantLastReadDate = participant.lastReadMessageDate
        ? new Date(participant.lastReadMessageDate)
        : undefined;

      const maxKnownReadMessageTime = this.messages
        .filter(
          m =>
            m.conversationId === participant.conversationId &&
            m.createdBy !== participant.userId &&
            m.clCompChat_readList?.includes(participant.userId)
        )
        .reduce((maxTime, m) => {
          const messageTime = new Date(m.createdOn).getTime();
          return messageTime > maxTime ? messageTime : maxTime;
        }, 0);
      const effectiveLastReadTime = Math.max(participantLastReadDate?.getTime() || 0, maxKnownReadMessageTime);

      var readMessages = this.messages.filter(
        m =>
          m.conversationId === participant.conversationId &&
          effectiveLastReadTime >= new Date(m.createdOn).getTime() &&
          m.createdBy !== participant.userId
        // removed: duplicate guard in forEach makes this redundant
        // (!m.clCompChat_readList || m.clCompChat_readList.indexOf(participant.userId) === -1)
      );
      let participantOwnMessages = this.messages.filter(
        m => m.conversationId === participant.conversationId && m.createdBy === participant.userId
      );
      if (readMessages.length === 0 && this.messages.length != participantOwnMessages.length) {
        this.chatLoggingService.info({
          logMessage: `chat.component.updateParticipantReadIndicator(): participant ${participant.userId} 'marked unread' conversation ${this.conversationId}.`
        });
      }
      readMessages.forEach(r => {
        if (!r.clCompChat_readList?.includes(participant.userId)) {
          var readArray = r.clCompChat_readList ? r.clCompChat_readList : [];
          readArray.push(participant.userId);
          r.clCompChat_readList = readArray.sort();
        }
      });
      var lastMessageToCheckForRead = this.messages.find(m => m.clCompChat_lastMessageToCheckForRead);
      this.changeDetectReadIndicator(lastMessageToCheckForRead);
    }
  }

  private updateLastReadMessageToCheck(): void {
    let existingLastMessageToCheckForRead = this.messages.find(m => m.clCompChat_lastMessageToCheckForRead);
    let existingLastMessageToCheckForReadChatItem;
    if (existingLastMessageToCheckForRead) {
      existingLastMessageToCheckForRead.clCompChat_lastMessageToCheckForRead = false;
      existingLastMessageToCheckForReadChatItem = this.chatItems.find(
        c => c.item().messageId === existingLastMessageToCheckForRead.messageId
      );
    }
    let hasSendingMessage = this.messages.find(m => m.clCompChat_state === 'sending');
    if (!hasSendingMessage) {
      var lastMessageToCheckForRead = findLast(
        this.messages,
        m =>
          m?.contentType !== ContentTypes.WHISPER &&
          m.clCompChat_state !== 'failed' &&
          m.clCompChat_state !== 'sending' &&
          m.clCompChat_state !== 'pending'
      );
      if (lastMessageToCheckForRead) {
        lastMessageToCheckForRead.clCompChat_lastMessageToCheckForRead = true;
        if (existingLastMessageToCheckForReadChatItem) {
          existingLastMessageToCheckForReadChatItem.detectChanges();
        }
        this.changeDetectReadIndicator(lastMessageToCheckForRead);
      }
    }
  }

  private changeDetectReadIndicator(message: MessageEx): void {
    if (message) {
      var lastMessageToCheckForReadChatItem = this.chatItems.find(c => c.item().messageId === message.messageId);
      if (lastMessageToCheckForReadChatItem) {
        lastMessageToCheckForReadChatItem.detectChanges();
        if (
          message.clCompChat_readList?.length &&
          this.scrollingService.isNearBottom() &&
          !this.isSearch &&
          !this.isSnapshot
        ) {
          setTimeout(() => {
            this.scrollingService.scrollToBottom();
          }, 0);
        }
      }
    }
  }

  private scrollPage(down: boolean = true) {
    var pageHeight = this.maincontent.nativeElement.offsetHeight;
    this.scrollingService.scrollPage(pageHeight, down);
  }

  private participantTotalEvent() {
    this.onParticipantsChanged.emit({
      conversationId: this.conversationId,
      total: this.participantTotal
    });
  }

  private participantEvent() {
    if (!this.isLargeParticipantLoad) {
      this.chatLoggingService.info({
        logMessage: 'chat.component.participantEvents()',
        logParameters: [this.conversationId, this.participants.length]
      });
      this.chatConversationService.participantsUpdated$.next({
        conversationId: this.conversationId,
        participants: this.participants
      });
      this.onParticipantsChanged.emit({
        conversationId: this.conversationId,
        participants: this.participants,
        total: this.participants.length
      });
    }
    let me = this.participants.find(p => p.userId === this.chatAuthService.userId);
    if (me && me.lastReadMessageDate) {
      this.onUnreadUpdated.emit({
        conversationId: this.conversationId,
        lastReadMessageDate: this.lastReadMessageDate as any
      });
    }
  }

  private setOfflineSending() {
    this.offlineSendingEnabled =
      this.config && !this.config.disableTextArea && this.config.enableOfflineSending && this.indexDbEnabled();
  }

  public isOffline() {
    return this.chatCloudLinkService.isOffline();
  }

  private saveTextInput(conversationId) {
    if (this.config?.saveInput && conversationId && this.inputSaveRestored) {
      let inputText = this.getText();
      let savedInputs = sessionStorage.getItem(ChatStorageKeys.CHAT_INPUT);
      let savedInputsJson = {};
      if (savedInputs) {
        savedInputsJson = JSON.parse(savedInputs);
      }
      if (inputText.trim() || this.quotedMessage) {
        savedInputsJson[conversationId] = {
          input: inputText,
          quote: this.quotedMessage
        };
        this.setChatInputStorage(savedInputsJson);
      } else if (savedInputsJson[conversationId]) {
        delete savedInputsJson[conversationId];
        this.setChatInputStorage(savedInputsJson);
      }
    }
    this.inputSaveRestored = false;
  }

  private setChatInputStorage(savedInputsJson) {
    sessionStorage.setItem(ChatStorageKeys.CHAT_INPUT, JSON.stringify(savedInputsJson));
    this.onInputSaved.emit(savedInputsJson);
  }

  private restoreTextInput() {
    if (this.config?.saveInput && this.conversationId) {
      let savedInputs = sessionStorage.getItem(ChatStorageKeys.CHAT_INPUT);
      if (savedInputs) {
        let savedInputsJson = JSON.parse(savedInputs);
        if (savedInputsJson[this.conversationId]) {
          if (savedInputsJson[this.conversationId].input) {
            this.setText(savedInputsJson[this.conversationId].input);
          }
          if (savedInputsJson[this.conversationId].quote) {
            this.quotedMessageChange(savedInputsJson[this.conversationId].quote);
          }
          delete savedInputsJson[this.conversationId];
          this.setChatInputStorage(savedInputsJson);
        }
      }
    }
    this.inputSaveRestored = true;
  }

  public handleDbConversationList(conversations: Conversation[]) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.handleDbConversationList()',
      logParameters: [conversations.length]
    });
    this.chatConversationService.handleConversationListDb(conversations);
  }

  public handleDbUserConversationList(userConversations: UserConversation[]) {
    this.chatLoggingService.info({
      logMessage: 'chat.component.handleDbUserConversationList()',
      logParameters: [userConversations.length]
    });
    this.chatConversationService.handleUserConversationListDb(userConversations);
  }

  public handleDbConversation(conversation: Conversation) {
    //Wait for auth service to be ready for the account id
    let authSub = new Subscription();
    authSub.add(
      this.chatAuthService.ready$.subscribe(async () => {
        authSub.unsubscribe();
        this.chatConversationService.addConversationToDatabase(conversation);
      })
    );
  }

  public handleMessageNotificationInput(message: Message) {
    //only process if in current conversation
    if (message.conversationId === this.conversationId) {
      try {
        //Don't want to process a message in the middle of loading/fetching/syncing
        if (this.fetchingMessages > 0 || this.loading() || this.contentLoading() || this.syncing()) {
          setTimeout(() => {
            this.handleMessageNotificationInput(message);
          }, 500);
        } else {
          //check message is not already in chat/recent and is newer than all messages
          let matchedMessage = this.messages.find(
            m => m.messageId === message.messageId && m.conversationId === message.conversationId
          );
          let matchedRecentMessage = this.chatCloudLinkService.recentMessages.find(
            m => m.messageId === message.messageId && m.conversationId === message.conversationId
          );
          let newerThanAllRecent: boolean = this.chatCloudLinkService.recentMessages.every(
            m => new Date(m.createdOn) < new Date(message.createdOn)
          );
          let newerThanMessages: boolean = this.messages.every(
            m => new Date(m.createdOn) < new Date(message.createdOn)
          );
          if (
            (!matchedMessage || this.messages.length === 0) &&
            !matchedRecentMessage &&
            newerThanAllRecent &&
            newerThanMessages
          ) {
            //inject message into UI
            this.chatCloudLinkService.sendMessagesChanged(message as MessageEx);
            this.chatLoggingService.info({
              logMessage: 'chat.component.handleMessageNotificationInput(): Firing messagesChanged$',
              logParameters: [message.conversationId, message.messageId]
            });
            //schedule reload conversation to make sure all messages are still relevant
            //do not reload if previous reload was less then 15 sec ago
            if (this.reloadConversationTimeout) {
              clearTimeout(this.reloadConversationTimeout);
              this.chatLoggingService.info({
                logMessage:
                  '[chat.component.handleMessageNotificationInput]: Reload conversation timeout exists, waiting 15 seconds before reloading again'
              });
              this.reloadConversationTimeout = setTimeout(() => {
                this.chatLoggingService.info({
                  logMessage:
                    '[chat.component.handleMessageNotificationInput]: Reload conversation timeout expired, reloading conversation'
                });
                this.reloadConversation();
                this.reloadConversationTimeout = setTimeout(() => {
                  this.chatLoggingService.info({
                    logMessage: '[chat.component.handleMessageNotificationInput]: Reload conversation timeout expired'
                  });
                  this.reloadConversationTimeout = null;
                }, this.chatCloudLinkService.elasticDelay);
              }, this.chatCloudLinkService.elasticDelay);
            } else {
              this.chatLoggingService.info({
                logMessage: '[chat.component.handleMessageNotificationInput]: Reloading conversation'
              });
              this.reloadConversationTimeout = setTimeout(() => {
                this.chatLoggingService.info({
                  logMessage: '[chat.component.handleMessageNotificationInput]: Reloading conversation timeout expired'
                });
                this.reloadConversationTimeout = null;
              }, this.chatCloudLinkService.elasticDelay);
              this.reloadConversation();
            }
          }
        }
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.component.handleMessageNotificationInput(): Error',
          logParameters: [ex]
        });
      }
    }
  }

  public nameClicked(userId: string) {
    this.onNameClick.emit(userId);
  }

  public filterOutMatchingAttachmentMessages(messages: MessageEx[]) {
    let attachmentSystemMessages = messages.filter(m => MessageUtils.isAttachmentSystemMessage(m));
    let matchedComponentSystemMessages = [];
    attachmentSystemMessages.forEach(a => {
      let body = MessageUtils.getAttachmentSystemMessageBody(a);
      let match = messages.find(m => {
        if (MessageUtils.isAttachment(m) && m.body === body) {
          return true;
        }
      });
      if (match && matchedComponentSystemMessages.indexOf(match) === -1) {
        matchedComponentSystemMessages.push(match);
      }
    });
    if (matchedComponentSystemMessages.length) {
      messages = messages.filter(m => matchedComponentSystemMessages.indexOf(m) === -1);
    }
    return messages;
  }

  public noMobileTextOrAttachments(): boolean {
    return (
      !(this.message && this.message.trim() != '' && this.isMobile) &&
      !(this.pendingAttachments.length && this.isMobile)
    );
  }

  public setIsSMSMessage(message: MessageEx) {
    if (this.config?.smsMode) {
      //Can just use the participants off the conversation
      let participants = this.currentConversation?._embedded?.participants?._embedded?.items;
      if (participants) {
        let participantMatch = participants.find(p => p.userId === message.createdBy);
        if (participantMatch?.participantType === 'SMS') {
          message.clCompChat_smsMessage = true;
        } else {
          message.clCompChat_smsMessage = false;
        }
      } else {
        //Hacky check for now. If we know we're in SMS mode we can just check that the other user isn't me
        if (message.createdBy !== this.chatAuthService.userId) {
          message.clCompChat_smsMessage = true;
        }
      }
    }
  }

  public async findAttachment(attachmentId: string) {
    let msgElement: ChatItemComponent = this.chatItems.find(c => c.attachmentId() === attachmentId);
    if (msgElement) {
      this.scrollingService.scrollToMessage(msgElement.item().messageId);
    } else if (!msgElement && !this.chatCloudLinkService.isOffline()) {
      await MessageUtils.getMessageIdOfAttachmentMessage(
        attachmentId,
        this.conversationId,
        this.chatCloudLinkService,
        this.chatLoggingService
      ).then(messageId => {
        if (messageId) {
          this.goToMessage({ messageId: messageId, forceSearch: true });
        }
      });
    }
  }

  public activateSelectMessagesMode(event: boolean) {
    this.selectMessagesMode = event;
    this.onSelectMessageMode.emit(this.selectMessagesMode);
    if (!event) {
      this.messageDeleteList = [];
      this.editModeOpen = false;
      this.currentEditMessageId = null;
      this.clearInput();
      this.clearCopiedAttachments();
      this.removeRecording();
      this.undoQuotedMessage();
    }
  }

  onSelectRemoveMessage(toggle: boolean, item: MessageEx) {
    if (toggle) {
      this.messageDeleteList.push(item);
    } else {
      this.messageDeleteList = this.messageDeleteList.filter(deleteditem => deleteditem !== item);
    }
    this.cdRef.markForCheck();
  }

  deleteMessages() {
    let title, bodyText;

    if (this.messageDeleteList.length >= 2) {
      title = this.translate.instant('cl_chat.deleteDialogSelectedMessagesButton', {
        number: this.messageDeleteList.length
      });
      bodyText = this.translate.instant('cl_chat.deleteDialogSelectedMessages', {
        number: this.messageDeleteList.length
      });
    } else {
      title = this.translate.instant('cl_chat.deleteDialogSelectedMessageButton', {
        number: this.messageDeleteList.length
      });
      bodyText = this.translate.instant('cl_chat.deleteDialogMessage');
    }

    const previouslyFocusedElement = document.activeElement as HTMLElement;

    const dialogRef = this.dialogService.open(ChatConfirmationDialogComponent, {
      data: {
        title: title,
        bodyText: bodyText,
        okButtonText: this.translate.instant('cl_chat.deleteDialogMessageButton'),
        cancelButtonText: this.translate.instant('cl_chat.deleteDialogCancelButton')
      },
      showHeader: false,
      modal: true,
      dismissableMask: true,
      focusOnShow: false,
      pt: { root: { role: 'alertdialog', 'aria-labelledby': 'chat-confirmation-dialog-title' } },
      styleClass: 'chat-confirmation-modal',
      contentStyle: { padding: '0', overflow: 'visible' }
    });

    dialogRef.onClose.subscribe(async result => {
      if (result) {
        await this.deleteSelectedMessages();
        this.textArea?.nativeElement?.focus();
      } else {
        previouslyFocusedElement?.focus();
      }
    });
  }

  async deleteSelectedMessages() {
    this.loading.set(true);
    if (this.messageDeleteList) {
      for (let i = 0; i < this.messageDeleteList.length; i++) {
        this.chatLoggingService.info({
          logMessage: 'chat.component.deleteSelectedMessages()',
          logParameters: [this.messageDeleteList[i].conversationId, this.messageDeleteList[i].messageId]
        });
        if (this.messageDeleteList[i].contentType === ContentTypes.ATTACHMENT && this.messageDeleteList[i].body) {
          await MessageUtils.deleteAttachment(
            this.messageDeleteList[i],
            this.chatLoggingService,
            this.chatConversationService,
            this.translate
          );
        } else if (
          this.messageDeleteList[i].contentType === ContentTypes.ATTACHMENTSYSTEMMESSAGE &&
          this.messageDeleteList[i].body
        ) {
          await MessageUtils.deleteAttachment(
            this.messageDeleteList[i],
            this.chatLoggingService,
            this.chatConversationService,
            this.translate
          );
          await MessageUtils.checkAndDeleteAttachmentMessage(
            this.messageDeleteList[i].body,
            this.messageDeleteList[i].conversationId,
            this.chatCloudLinkService,
            this.chatAuthService,
            this.chatLoggingService,
            this.chatConversationService,
            this.translate
          );
          //Can't delete the system message itself
          this.chatCloudLinkService.sendMessagesDeleted(this.messageDeleteList[i].messageId, true);
          return;
        }
        try {
          await defer(() =>
            this.chatConversationService.deleteMessage({
              conversationId: this.messageDeleteList[i].conversationId,
              messageId: this.messageDeleteList[i].messageId
            })
          )
            .pipe(
              retryWhen(
                SharedHelpers.genericRetryStrategy(
                  'chat.component.deleteMessage(): delete message ' +
                    this.messageDeleteList[i].conversationId +
                    ' ' +
                    this.messageDeleteList[i].messageId,
                  this.chatLoggingService,
                  3,
                  2000,
                  true
                )
              )
            )
            .toPromise();
        } catch (ex) {
          this.chatLoggingService.messageError({
            exceptionLogEvent: {
              logMessage: 'chat.component.deleteSelectedMessages(): Error deleting message',
              logParameters: [ex]
            },
            errorMessage: this.translate.instant('cl_chat.errorDeletingMessage')
          });
        }
      }
      this.activateSelectMessagesMode(false);
    }
    this.loading.set(false);
  }

  public onActionCardBehavior($event: ActionCardBehaviorEvent) {
    ActionCardHelpers.actionCardBehavior($event.behavior, $event.success, this, $event.chatItem);
  }

  public onOpenLink($event: OpenLinkEvent) {
    ActionCardHelpers.openLink(
      $event.$event,
      $event.url,
      $event.title,
      $event.behavior,
      this.chatLoggingService,
      $event.componentName,
      this,
      $event.chatItem
    );
  }

  public onPostWebhookPayload($event: PostWebhookPayloadEvent) {
    ActionCardHelpers.postWebhookPayload(
      $event.$event,
      $event.url,
      $event.payload,
      $event.title,
      $event.behavior,
      $event.retry401,
      this.chatAuthService,
      this.chatLoggingService,
      this.httpClient,
      this.translate,
      $event.componentName,
      this,
      $event.chatItem
    );
  }

  public onSendWebhookPayload($event: SendWebhookPayloadEvent) {
    ActionCardHelpers.sendWebhookPayload(
      $event.method,
      $event.url,
      $event.payload,
      $event.title,
      $event.behavior,
      $event.retry401,
      this.chatAuthService,
      this.chatLoggingService,
      this.httpClient,
      this.translate,
      $event.componentName,
      this,
      $event.chatItem
    );
  }

  public onOpenModal($event): void {
    switch ($event.modal) {
      case 'MessageInfoComponent':
        // Close existing message info modal if open
        if (this.currentMessageInfoModalRef) {
          this.currentMessageInfoModalRef.close();
        }

        this.currentMessageInfoModalRef = this.dialogService.open(MessageInfoComponent, {
          data: {
            item: $event.chatItem.item(),
            maxImageSize: this.maxImageSize,
            dateTimeLocale: this.dateTimeLocale,
            title: this.translate.instant('cl_chat.messageInfo'),
            isReadByAll: $event.chatItem.isReadByAll(),
            deliveredList: $event.chatItem.getDeliveredList(),
            readList: $event.chatItem.getReadList(),
            isMine: $event.chatItem.isMine,
            config: this.config
          },
          showHeader: false,
          modal: true,
          dismissableMask: true,
          pt: { root: { role: 'dialog', 'aria-labelledby': 'message-info-title' } },
          styleClass: 'message-info-modal',
          contentStyle: { padding: '0', overflow: 'visible' }
        });

        this.isMessageInfoOpen = true;
        this.currentMessageInfoId = $event.chatItem.item().messageId;

        this.currentMessageInfoModalRef.onClose.subscribe(() => {
          this.isMessageInfoOpen = false;
          this.currentMessageInfoId = null;
          this.currentMessageInfoModalRef = null;
        });
        break;
      case 'ChatConfirmationDialogComponent': {
        const previouslyFocusedElement = document.activeElement as HTMLElement;

        const confirmDialogRef = this.dialogService.open(ChatConfirmationDialogComponent, {
          data: {
            item: $event.chatItem.item(),
            maxImageSize: this.maxImageSize,
            dateTimeLocale: this.dateTimeLocale,
            title: this.translate.instant('cl_chat.deleteDialogMessageButton'),
            bodyText: this.translate.instant('cl_chat.deleteDialogMessage'),
            okButtonText: this.translate.instant('cl_chat.deleteDialogMessageButton'),
            cancelButtonText: this.translate.instant('cl_chat.deleteDialogCancelButton')
          },
          showHeader: false,
          modal: true,
          dismissableMask: true,
          focusOnShow: false,
          pt: { root: { role: 'alertdialog', 'aria-labelledby': 'chat-confirmation-dialog-title' } },
          styleClass: 'chat-confirmation-modal',
          contentStyle: { padding: '0', overflow: 'visible' }
        });

        confirmDialogRef.onClose.subscribe(async result => {
          if (result) {
            await $event.chatItem.deleteMessage();
            this.textArea?.nativeElement?.focus();
          } else {
            previouslyFocusedElement?.focus();
          }
        });
        break;
      }
    }
  }
}
